/**
 * Minimal en-only string table. Keyed for type safety; extend as the
 * app grows. A full next-intl/lingui setup is overkill for v0.1 scope.
 */
export const STRINGS = {
  'app.name': 'LeanOpx',
  'app.tagline': 'Operating intelligence for CEOs',
  'nav.graph': 'Network',
  'nav.dossiers': 'Dossiers',
  'nav.briefs': 'Briefs',
  'nav.query': 'Ask the Cup',
  'nav.capital': 'Capital flows',
  'nav.sources': 'Data sources',
  'ask.placeholder': 'Ask anything — or type / for commands',
  'ask.thinking': 'Thinking…',
  'ask.eyebrow': 'Ask',
  'ask.title': 'Ask the agent',
  'ask.sub':
    'Plain English in, cited synthesis out. Every claim anchors to a source the agent can show you.',
  'ask.input.placeholder': 'Ask anything about your company — runway, deals, people, signals.',
  'ask.input.hint': '⌘↵ to send',
  'ask.empty.heading': 'Start a conversation',
  'ask.empty.body':
    'Ask a question and the agent will stitch a cited answer from your connected tools.',
  'ask.empty.examples.heading': 'Try',
  'ask.empty.example.1': 'Why is the conversion-rate Rock slipping?',
  'ask.empty.example.2': 'What changed with Acme this week?',
  'ask.empty.example.3': 'Who in the company is at risk of leaving?',
  'ask.thread.list.title': 'Recent threads',
  'ask.thread.list.empty': 'No conversations yet.',
  'ask.thread.list.error': 'Couldn’t load your conversations.',
  'ask.thread.new': 'New conversation',
  'ask.message.error': 'Synthesis failed. Try again or rephrase.',
  'ask.send': 'Ask',
  'ask.streaming': 'Streaming…',
  'l10.prep.title': 'L10 prep brief',
  'l10.prep.sub': 'A 5-section pre-meeting outline the agent generates from your business context.',
  'l10.prep.generate': 'Generate brief',
  'l10.prep.generating': 'Drafting your brief…',
  'l10.prep.empty': 'No brief yet. Generate one to see what the agent surfaces.',
  'l10.prep.error': 'Couldn’t generate the brief. Try again.',
  'l10.prep.section.headlines': 'Headlines',
  'l10.prep.section.scorecardSlips': 'Scorecard slips',
  'l10.prep.section.issuesToDiscuss': 'Issues to discuss',
  'l10.prep.section.rocksUpdate': 'Rocks update',
  'l10.prep.section.peopleNotes': 'People notes',
  'l10.prep.section.empty': 'Quiet — nothing to flag.',
  'l10.prep.footer': 'Generated {date} · model {model}',
  'dossier.overview': 'Overview',
  'dossier.network': 'Network',
  'dossier.sources': 'Sources',
  'dossier.notes': 'Notes',
  'dossier.synthesis': 'Synthesis',
  'dossier.positions': 'Positions',
  'dossier.relationships': 'Relationships',
  'dossier.signals': 'Signals',
  'dossier.timeline': 'Timeline',
  'dossier.relationship-strength': 'Strength',
  'dossier.no-signals': 'No signals recorded yet.',
  'dossier.no-positions': 'No documented positions.',
  'auth.signin': 'Sign in',
  'auth.signout': 'Sign out',
  'auth.signup': 'Sign up',
  'auth.forgot': 'Forgot password?',
  'auth.reset': 'Reset password',
  'auth.verify': 'Verify email',
  'auth.google': 'Continue with Google',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirm-password': 'Confirm password',
  'auth.have-account': 'Already have an account?',
  'auth.no-account': "Don't have an account?",
  'auth.check-email': 'Check your email for a verification link.',
  'auth.resend': 'Resend verification email',
  'auth.back-to-signin': 'Back to sign in',
  'auth.verify-email.verifying': 'Verifying your email…',
  'auth.verify-email.success': 'Your email has been verified.',
  'auth.verify-email.error': 'We could not verify your email. The link may have expired.',
  'auth.verify-email.continue': 'Continue to the archive →',

  // Sign-in screen — v6 dev-handoff refresh (#114). The screen
  // composes Google / Microsoft / SAML SSO buttons, magic-link, and
  // email/password into a single split-pane (form + trust panel).
  'signin.title': 'Welcome back.',
  'signin.sub': 'Sign in to your LeanOpx workspace.',
  // Three SSO buttons matching the design's `Google · domain` / `Magic
  // link` / `SSO / SAML` triplet (replaces the prior
  // Google/Microsoft/SAML triplet — Microsoft and Okta weren't wired,
  // and the magic-link button is the live affordance per the design).
  'signin.continue.google': 'Continue with Google',
  'signin.continue.googleDomain': 'Google · {domain}',
  'signin.continue.magicLink': 'Magic link to your inbox',
  'signin.continue.ssoGeneric': 'SSO / SAML',
  'signin.passwordForgotInline': 'Forgot?',
  // Legacy keys preserved for any non-/sign-in caller; not rendered on
  // /sign-in after the design pass.
  'signin.continue.microsoft': 'Continue with Microsoft 365',
  'signin.continue.saml': 'SAML SSO (Okta · OneLogin · custom)',
  'signin.or': 'Or',
  'signin.work-email': 'Work email',
  'signin.work-email.placeholder': 'you@company.com',
  'signin.send-link.unavailable': 'Sign-in link · not yet available',
  'signin.link-unavailable': "Sign-in link isn't live yet — use your password instead.",
  'signin.link-coming-soon': "We're still wiring email-link sign-in. Use your password for now.",
  'signin.use-password': 'Use a password instead',
  'signin.use-magic-link': 'Use a sign-in link instead',
  'signin.signing-in': 'Signing in…',
  'signin.sso-unavailable':
    "We're still onboarding this SSO connection — drop us a line at hello@leanopx.com to enable it.",
  'signin.session-expired':
    'Your session expired or got into a bad state. Signing in again will fix it.',
  'signin.tos.terms': 'Terms',
  'signin.tos.privacy': 'Privacy policy',
  'signin.trust.eyebrow': 'What sits behind the sign-in',

  // Sign-in trust-posture sidebar — v7 designer-handoff refresh. The
  // right pane is pure executive reassurance: four belief-statement
  // bullets (title + body) anchored by an accent sparkle, plus a
  // privacy-policy footer. The SSO provider list moved entirely to the
  // form on the left; the trust pane no longer duplicates it. Keep the
  // claims here in sync with security.leanopx.ai when it ships.
  'signin.trust.posture.readonly.title': 'Read-only by default',
  'signin.trust.posture.readonly.body':
    'All connectors land in read-only mode. Write actions require per-draft approval.',
  'signin.trust.posture.compliance.title': 'SOC 2 Type II · ISO 27001',
  'signin.trust.posture.compliance.body':
    'Audit reports under MNDA. Sub-processors enumerated; data residency US (EU on request).',
  'signin.trust.posture.tenant.title': 'Tenant-isolated graph',
  'signin.trust.posture.tenant.body':
    'Your data sits in a dedicated, encrypted namespace. No cross-tenant features and no training on customer content.',
  'signin.trust.posture.disconnect.title': 'One-click disconnect',
  'signin.trust.posture.disconnect.body':
    'Disconnect any source instantly. Derived data purged within the 30-day retention window.',
  'signin.trust.footer.policy': 'Privacy policy →',

  // Split-pane auth shell — designer-handoff refresh. The brand pane
  // (start column at ≥880px) carries the brand mark, marquee tagline,
  // optional product-preview card, and footer compliance chips. The
  // form pane (end column) is per-screen content owned by the route.
  // All copy reads from these keys — pages never hardcode the
  // marquee/tagline/chip strings.
  'auth.splitPane.pilotChip': 'Pilot',
  'auth.splitPane.tagline': 'The {em} that shapes itself to your business.',
  'auth.splitPane.taglineEm': 'intelligence platform',
  'auth.splitPane.sub':
    "Reads your stack, drafts Monday's briefing, surfaces what shipped and what slipped.",
  'auth.splitPane.preview.eyebrow': '● LeanOpx recommends',
  'auth.splitPane.preview.confidence': 'conf 92%',
  'auth.splitPane.preview.headline':
    'Push on the Acme renewal at the L10, ignore the velocity dip.',
  'auth.splitPane.preview.sub':
    'Daria Cole moved off the account · QBR is May 22 · Lena needs the pre-brief by Wednesday.',
  'auth.splitPane.preview.cite.salesforce': 'salesforce · 4,421',
  'auth.splitPane.preview.cite.slack': 'slack · 318',
  'auth.splitPane.preview.cite.linkedin': 'linkedin · 88',
  'auth.splitPane.chipsSoc2': '🔒 SOC 2 Type II',
  'auth.splitPane.chipsCustomers': '✓ 312 customers',
  'auth.splitPane.chipsPilot': '$ Pilot · private preview',
  'auth.splitPane.brandAria': 'LeanOpx brand panel',

  // Per-page copy that supplements the existing auth.* / signin.* keys
  // for the split-pane refit.
  'auth.signup.title': 'Create your workspace.',
  'auth.signup.sub': 'Spin up a workspace for your team. We connect to your stack in minutes.',
  'auth.signup.submit': 'Create account →',
  'auth.signup.firstName': 'First name',
  'auth.signup.lastName': 'Last name',
  'auth.signup.emailHint': 'Use your company domain so we can route to the right workspace.',
  'auth.signup.errors.tooShort': 'Password must be at least 8 characters.',
  'auth.signup.errors.mismatch': 'Passwords do not match.',
  'auth.forgot.title': 'Reset your password.',
  'auth.forgot.sub':
    "Enter your work email and we'll send a link to set a new password. The link expires in 30 minutes.",
  'auth.forgot.submit': 'Send reset link →',
  'auth.forgot.sent.title': 'Check your inbox.',
  'auth.forgot.sent.body':
    'If an account exists for {email}, we sent a password reset link. Check your inbox.',
  'auth.forgot.help': 'Need help? Email support@leanopx.com',
  'auth.forgot.backCrumb': '← Back to sign in',
  'auth.forgot.ssoHint':
    "If your workspace uses SSO, you'll be redirected to your identity provider instead.",
  'auth.reset.title': 'Choose a new password.',
  'auth.reset.sub': 'Pick a password you don’t use elsewhere.',
  'auth.reset.submit': 'Update password →',
  'auth.reset.done': 'Password updated. Redirecting to sign in…',
  'auth.verifySent.title': 'Check your inbox.',
  'auth.verifySent.body':
    'We sent a verification link to {email}. Click it to activate your LeanOpx account.',
  'auth.verifySent.bodyPrefix': 'We sent a verification link to',
  'auth.verifySent.bodySuffix': 'Click it to activate your LeanOpx account.',
  'auth.verifySent.bodyNoEmail':
    'We sent a verification link to your inbox. Click it to activate your LeanOpx account.',
  'auth.verifySent.helpEyebrow': "Didn't get it?",
  'auth.verifySent.help.1': 'Check spam, or "Promotions" if you use Gmail',
  'auth.verifySent.help.2': 'Make sure the email above is correct',
  'auth.verifySent.help.3': 'Allowlist noreply@leanopx.com in your IT settings',
  'auth.verifySent.resent': 'Verification email resent.',
  'auth.verifySent.later': "I'll do this later",
  'auth.verifySent.crumb': '← Try a different email',
  'auth.verifyToken.title': 'Verify email',
  'auth.verifyToken.continue': 'Continue to your briefing →',
  'auth.verifyToken.eyebrow': 'Email verification',
  'auth.mfa.eyebrow': 'Security · step 2 of 2',
  'auth.mfa.enroll.copy': 'Copy',
  'auth.mfa.enroll.copied': 'Copied',
  'auth.mfa.enroll.qrAlt': 'Authenticator QR code',
  'auth.mfa.enroll.info':
    'Your workspace requires MFA for admins. You can switch methods later in Settings → Security.',
  'auth.mfa.enroll.helpLink': 'Need a hand? Read the setup guide →',
  'auth.mfa.challenge.lost': 'Lost your device? Contact your admin.',
  'auth.invite.eyebrow': "You've been invited",
  'auth.invite.decline': 'Decline',
  'auth.invite.title': 'Join the workspace.',
  // {orgName} is interpolated via tFormat — the design header reads
  // "Join the LeanOpx QA workspace." with the org name in line.
  'auth.invite.titleNamed': 'Join the {orgName} workspace.',
  // Inviter sentence used in the body row above the inviter card.
  // Designed for {inviter} + {role} interpolation via tFormat.
  'auth.invite.bodySentence':
    "{inviter} added you as a {role}. You'll get the Monday briefing tomorrow morning.",
  'auth.invite.row.role': 'Role',
  'auth.invite.row.sso': 'SSO inheritance',
  'auth.invite.row.sources': 'Connected sources',
  'auth.invite.row.role.adminDesc': 'Read, draft, approve — plus member management',
  'auth.invite.row.role.memberDesc': 'Read, draft, approve (with admin OK on critical actions)',
  'auth.invite.row.ssoNone': 'Email + password',
  'auth.invite.inviterFallback': 'A teammate',
  'auth.invite.row.sourcesPending': 'Connect after sign-in',
  'auth.invite.accept': 'Accept & continue →',
  'auth.invite.footerNotYou': 'Not you? Sign in to a different account',
  'auth.invite.role.admin': 'Admin',
  'auth.invite.role.member': 'Member',
  'auth.invite.expires': 'Expires',
  'auth.invite.signinFirst': 'Sign in to accept this invite.',
  'auth.invite.footer': 'Already have an account? Switch accounts before accepting.',
  // Sign-in extras for the split-pane refit.
  'signin.remember': 'Remember this device for 30 days',
  'signin.footerNoAccount': "Don't have an account? Request access →",
  'auth.signup.footerHaveAccount': 'Already have an account? Sign in →',
  'auth.forgot.footerSupport': 'Need help? Email support@leanopx.com',
  'auth.verifySent.iconAria': 'Email envelope',
  'auth.reset.footerBack': '← Back to sign in',

  // Sidebar — fix-first additions from /review on PR #186 (Platform.html
  // design pass). Wave-1 shell agent shipped these as inline literals;
  // routing through STRINGS so future locales + screen-reader users get
  // coherent copy.
  'sidebar.label.briefing': 'Briefing',
  'sidebar.label.scorecard': 'Scorecard',
  'sidebar.label.rocks': 'Rocks',
  'sidebar.label.issues': 'Issues',
  'sidebar.label.playbook': 'Playbook',
  'sidebar.label.signals': 'Signals',
  'sidebar.label.alerts': 'Alerts',
  'sidebar.label.todos': 'Todos',
  'sidebar.label.dossiers': 'Dossiers',
  'sidebar.label.insights': 'Insights',
  'sidebar.label.outsideView': 'Outside view',
  'sidebar.label.agentLog': 'Agent log',
  'sidebar.label.people': 'People',
  'sidebar.label.sources': 'Sources',
  'sidebar.label.audit': 'Audit log',
  'sidebar.label.settings': 'Settings',
  'sidebar.nav.aria': 'Primary navigation',
  'sidebar.agent.openActivity.aria': 'Open agent activity',
  'sidebar.pin.unpin.aria': 'Unpin sidebar (collapse on idle)',
  'sidebar.pin.pin.aria': 'Pin sidebar open',
  'sidebar.pin.unpin.title': 'Unpin (collapse on idle)',
  'sidebar.pin.pin.title': 'Pin sidebar open',
  'sidebar.switchOrg.error': 'Couldn’t switch organization',

  // 404 / 403 — unified NotFoundScreen per v6 design.
  'notfound.404.title': "We couldn't find that.",
  'notfound.404.body':
    "The dossier, person, or filing you were looking for isn't in your graph — it may have been removed, renamed, or you may have followed an old link.",
  'notfound.404.primary': 'Go to briefing',
  'notfound.404.secondary': 'Search instead',
  'notfound.403.title': "You don't have access to this.",
  'notfound.403.body':
    "Your workspace role doesn't include access to this view. Ask an admin to grant the right role, or open the parts of the workspace you do have access to.",
  // The primary CTA label matches its destination (`/today`). An earlier
  // "Request access" copy implied an in-product flow we haven't shipped
  // — clicking it would silently land the user on the briefing without
  // any request being made.
  'notfound.403.primary': 'Go to briefing',
  // Secondary opens a `mailto:` to hello@leanopx.com so the user can
  // actually email someone to request access until the in-product
  // request-access flow ships (#114).
  'notfound.403.secondary': 'Email for access',
  'notfound.rail.tried': 'What we tried',
  'notfound.rail.requested': 'requested',
  'notfound.rail.lookedup': 'looked up',
  'notfound.rail.lastseen': 'last seen',
  'notfound.rail.traceid': 'trace id',
  'notfound.rail.suggestions': 'Suggestions',

  // Empty-workspace — first-run /empty surface (#114).
  'empty.eyebrow': 'Welcome to LeanOpx · workspace created',
  'empty.title': 'Three connectors. One briefing. About an afternoon.',
  'empty.sub':
    "LeanOpx doesn't ship with dashboards — it builds them by reading what's already in your stack. Connect anything below to start.",
  'empty.step.label': 'Step {n}',
  'empty.step.01.title': 'Connect a few sources',
  'empty.step.01.body':
    'Workspace, Slack, Salesforce — read-only by default. Each one writes to your graph with provenance.',
  'empty.step.01.cta': '+ Connect a source',
  'empty.step.02.title': 'Invite the inner circle',
  'empty.step.02.body':
    'Whoever runs the operating loop with you. The agent will start writing 1:1 prep + L10 agendas around their cadence.',
  'empty.step.02.cta': 'Invite teammates',
  'empty.step.03.title': 'Pick the rituals you run on',
  'empty.step.03.body':
    'EOS / Scaling Up / homegrown — LeanOpx maps them to your data. You can change this any time.',
  'empty.step.03.cta': 'Set up rituals',
  'empty.preview.eyebrow': 'What you will see Monday',
  'empty.preview.tag': 'Your first briefing · preview',
  'empty.preview.headline': 'Quiet morning — your net new ARR is steady.',
  'empty.preview.northstar.k': 'North star',
  'empty.preview.northstar.t': 'Net new ARR steady at $214k',
  'empty.preview.industry.k': 'Industry',
  'empty.preview.industry.t': 'SaaS scorecard on point',
  'empty.preview.priority.k': 'Priority',
  'empty.preview.priority.t': 'Churn watch',
  'empty.preview.people.k': 'People',
  'empty.preview.people.t': 'Acme champion moved',
  'empty.preview.cta': '+ Connect to see this for real',
  'empty.rail.workspace': 'Workspace',
  'empty.rail.org': 'org',
  'empty.rail.created': 'created',
  'empty.rail.people': 'people',
  'empty.rail.sources': 'sources',
  'empty.rail.just-now': 'just now',
  'empty.rail.path.eyebrow': 'Quickest path to value',
  'empty.rail.path.body':
    'Connect Google Workspace — the agent will draft your first briefing within 30 minutes.',
  'empty.rail.path.cta': '+ Connect Workspace',

  // Billing — /admin/billing (#12). Live Stripe wiring (Checkout + Portal).
  // Plan tier labels + features mirror apps/marketing/src/components/pricing.tsx.
  'billing.eyebrow': 'Billing & subscription',
  'billing.title': 'Plan & billing',
  'billing.subtitle':
    'Per-seat pricing across Operator and Team. Enterprise is sales-led — talk to us if you run multiple workspaces or need SSO / SCIM / custom DPA.',
  'billing.plan.current': 'Current plan',
  'billing.plan.cta.manage': 'Manage plan',
  'billing.plan.cta.switch': 'Switch to {plan}',
  'billing.plan.cta.contact': 'Talk to us',
  'billing.plan.cta.opening': 'Opening Stripe…',
  'billing.plan.seats': 'Seats',
  'billing.plan.renews': 'Renews {date}',
  'billing.plan.trial-ends': 'Trial ends {date}',
  'billing.plan.no-subscription': 'No paid plan yet',
  'billing.status.trialing': 'In trial',
  'billing.status.active': 'Active',
  'billing.status.past_due': 'Past due',
  'billing.status.canceled': 'Canceled',
  'billing.status.incomplete': 'Setup incomplete',
  'billing.status.incomplete_expired': 'Setup expired',
  'billing.trial.banner.title': 'Trial ends in {days} days',
  'billing.trial.banner.body':
    'Pick a plan to keep your agent watching. Your sources stay connected — only the writes pause.',
  'billing.empty.title': 'No subscription on file',
  'billing.empty.body':
    'This workspace doesn’t have a Stripe subscription yet. Pick a plan below to start a 14-day trial.',
  'billing.error.load': 'Couldn’t load your subscription.',
  'billing.error.checkout': 'Couldn’t start checkout. Try again.',
  'billing.error.portal': 'Couldn’t open the billing portal. Try again.',
  'billing.error.portal.no-customer':
    'No Stripe customer on file yet — pick a plan below to start your subscription.',
  'billing.compare.eyebrow': 'Compare plans',
  'billing.compare.cadence': '/ seat / month',
  'billing.compare.featured-badge': 'Best value',
  'billing.compare.who.operator': 'Solo founder',
  'billing.compare.who.team': 'Most teams',
  'billing.compare.who.enterprise': 'Multi-workspace + PE platforms',
  'billing.plan.operator.name': 'Operator',
  'billing.plan.operator.price': '$99.99',
  'billing.plan.operator.blurb': 'For founders running solo with full agent power.',
  'billing.plan.operator.f1': '1 seat, full agent',
  'billing.plan.operator.f2': 'Daily briefing · agent rehearsal',
  'billing.plan.operator.f3': 'Up to 8 connectors',
  'billing.plan.operator.f4': '7-day source history',
  'billing.plan.operator.f5': 'Email support',
  'billing.plan.team.name': 'Team',
  'billing.plan.team.price': '$149.99',
  'billing.plan.team.blurb': 'For ops teams that run the full operating loop.',
  'billing.plan.team.f1': 'Unlimited connectors',
  'billing.plan.team.f2': 'Briefing · Issues · Playbook · Scorecard',
  'billing.plan.team.f3': 'Custom agent voice + tone',
  'billing.plan.team.f4': '12-month source history',
  'billing.plan.team.f5': '30 collaborator seats',
  'billing.plan.team.f6': 'Slack + email support',
  'billing.plan.enterprise.name': 'Enterprise',
  'billing.plan.enterprise.price': 'Custom',
  'billing.plan.enterprise.blurb': 'For multi-business operators and PE platforms.',
  'billing.plan.enterprise.f1': 'Multi-workspace federation',
  'billing.plan.enterprise.f2': 'SSO + SCIM + custom DPA',
  'billing.plan.enterprise.f3': 'Self-hosted in your AWS',
  'billing.plan.enterprise.f4': 'Custom retention',
  'billing.plan.enterprise.f5': 'Dedicated success engineer',
  'billing.plan.enterprise.f6': 'SOC 2 + DPA',
  // Legacy keys kept (some still reference `billing.compare.who.builder`
  // off other surfaces); the page below no longer uses these.
  'billing.compare.who.builder': 'Solo founder, pre-team',
  'billing.current': 'Current',
  'billing.manage': 'Manage plan',
  'billing.renews': 'Renews May 31, 2026 · annual contract through Feb 2027',
  'billing.stat.seats': 'Seats',
  'billing.stat.connectors': 'Connectors',
  'billing.stat.agent-runs': 'Agent runs · 30d',
  'billing.stat.storage': 'Storage',
  'billing.compare.current': 'Current plan',
  'billing.compare.switch-to': 'Switch to',
  'billing.compare.contact-sales': 'Talk to sales',
  'billing.invoices.eyebrow': 'Invoices · last 12 months',
  'billing.invoices.col.date': 'Date',
  'billing.invoices.col.invoice': 'Invoice',
  'billing.invoices.col.description': 'Description',
  'billing.invoices.col.amount': 'Amount',
  'billing.invoices.col.status': 'Status',
  'billing.invoices.status.paid': 'Paid',
  'billing.invoices.pdf': 'PDF',
  'billing.invoices.empty': 'Invoices appear here once your first payment posts.',
  'billing.rail.account': 'Account',
  'billing.rail.workspace': 'workspace',
  'billing.rail.billing-email': 'billing email',
  'billing.rail.stripe-id': 'stripe id',
  'billing.rail.payment': 'Payment method',
  'billing.rail.update-card': 'Update card',
  'billing.rail.help': 'Help',
  'billing.rail.help.body': 'Questions on invoicing or enterprise terms?',
  'billing.rail.help.cta': 'Email billing@leanopx.com',

  // Team — unified invites + members surface at /settings/org/team.
  'team.eyebrow': "Team · who's in the workspace",
  'team.title.template': '{members} people, {pending} invited.',
  'team.invite-cta': '+ Invite teammates',
  'team.invite-by-email': 'Invite by email',
  'team.invite-placeholder': 'person1@company.com, person2@company.com',
  'team.invite-send': 'Send',
  'team.invite-hint':
    "Invitees get a magic-link email valid for 7 days. They'll inherit your Workspace SSO if configured.",
  'team.tab.members': 'Members',
  'team.tab.invites': 'Invites',
  'team.col.member': 'Member',
  'team.col.role': 'Role',
  'team.col.status': 'Status',
  'team.col.joined': 'Joined',
  'team.col.email': 'Email',
  'team.col.expires': 'Expires',
  'team.rail.seats': 'Seats',
  'team.rail.seats.used': 'used',
  'team.rail.seats.pending': 'pending invites',
  // Seat limit + plan + overage figures source from the future
  // `GET /api/orgs/:o/billing/subscription` (#114). Until then the rail
  // shows a "no plan" sentinel instead of a hardcoded denominator that
  // could mislead the orgAdmin.
  'team.rail.seats.limit': 'plan limit',
  'team.rail.seats.no-plan': '—',
  'team.rail.set-up-billing': 'Set up billing',
  'team.rail.roles': 'Roles',
  'team.rail.role.admin.body': 'Full access · billing · team',
  'team.rail.role.member.body': 'Read + draft · no billing',
  'team.rail.role.viewer.body': 'Read-only briefings & dossiers',
  'team.rail.role.guest.body': 'Single dossier only',
  'team.row.actions': 'Member actions',
  'team.row.cannot-remove-self': 'You can’t remove yourself.',
  'team.invites.empty': 'No invites yet.',
  'team.invite.revoke': 'Revoke',

  // Profile — /profile (v7 redesign, epic #123 Phase 2). The page is
  // refit around "told" vs "learned" facts: hover-pencil inline edit
  // for the rows the user typed; cited source + confidence + scan
  // history for the rows the agent learned from LinkedIn / calendar /
  // email metadata. See `docs/designs/v7/project/screens-profiles.jsx`.
  'profile.eyebrow': 'Your profile',
  'profile.title': 'Everything we know about you.',
  'profile.sub':
    'Two layers: what you told us during onboarding (hover any value to edit), and what we learned about you from LinkedIn, calendar, and email metadata.',
  'profile.reRun.label': 'Re-run setup',
  'profile.reRun.cta': 'Re-run setup',
  'profile.told.eyebrow': 'What you told us',
  'profile.told.sub': 'Hover any row to edit. Changes save when you press Enter or click away.',
  'profile.told.row.name.label': 'Name',
  'profile.told.row.name.placeholder': 'Your name',
  'profile.told.row.title.label': 'Role / Title',
  'profile.told.row.title.placeholder': 'e.g. COO',
  'profile.told.row.email.label': 'Work email',
  'profile.told.row.email.placeholder': 'you@company.com',
  'profile.told.row.timezone.label': 'Timezone',
  'profile.told.row.timezone.placeholder': 'America/Los_Angeles',
  'profile.told.row.phone.label': 'Phone',
  'profile.told.row.phone.placeholder': 'Optional',
  'profile.told.row.location.label': 'Location',
  'profile.told.row.location.placeholder': 'City, region',
  'profile.told.savingLabel': 'Saving…',
  'profile.told.savingFailedLabel': "Couldn't save — try again.",
  // "Where to find you online" — separate told card. The LinkedIn row
  // is the primary disambiguator for the Gemini-grounded person
  // enrichment that drives the "What I've learned" section, so any
  // value typed here is preferred over the per-org
  // `BusinessContext.userLinkedinUrl` fallback.
  'profile.online.eyebrow': 'Where to find you online',
  'profile.online.sub':
    'Adds disambiguators for the agent so it can find you across the web. LinkedIn is what powers your learned profile below.',
  'profile.online.row.linkedinUrl.label': 'LinkedIn',
  'profile.online.row.linkedinUrl.placeholder': 'https://linkedin.com/in/your-handle',
  'profile.online.row.xHandle.label': 'X / Twitter',
  'profile.online.row.xHandle.placeholder': '@your-handle',
  'profile.online.row.personalSiteUrl.label': 'Personal site',
  'profile.online.row.personalSiteUrl.placeholder': 'https://you.example.com',
  'profile.learned.eyebrow': "What I've learned",
  'profile.learned.sub':
    'From LinkedIn, calendar, and email metadata. Every claim cites its source.',
  'profile.learned.empty.title': "We haven't learned anything about you yet.",
  'profile.learned.empty.body': 'Add your LinkedIn URL in onboarding to start.',
  'profile.learned.row.background.label': 'Career background',
  'profile.learned.row.pastRoles.label': 'Past roles',
  'profile.learned.row.focusAreas.label': 'Focus areas',
  'profile.learned.row.currentCompany.label': 'Current company',
  'profile.learned.row.tenure.label': 'Tenure at current role',
  'profile.learned.row.correct.cta': 'This is wrong',
  'profile.learned.scanned.label': 'Last scanned',
  'profile.learned.whereUsed.eyebrow': 'Used in',
  // One entry per FactSurface enum value (`packages/shared/src/fact-provenance.ts`).
  // Renders as ghost pills under each learned fact.
  'profile.learned.whereUsed.briefing': 'Briefing',
  'profile.learned.whereUsed.profile': 'Profile',
  'profile.learned.whereUsed.profile-company': 'Company profile',
  'profile.learned.whereUsed.dossier-overview': 'Dossier overview',
  'profile.learned.whereUsed.dossier-graph': 'Dossier graph',
  'profile.learned.whereUsed.ask': 'Ask',
  'profile.learned.whereUsed.rocks': 'Rocks',
  'profile.learned.whereUsed.scorecard': 'Scorecard',
  'profile.learned.whereUsed.insights': 'Insights',
  // Correction SlideOver (fires when the user clicks "This is wrong" on
  // a learned fact). Phase 2 records the audit event; backend re-
  // synthesis on the correction is a follow-up.
  'profile.correct.slideOver.eyebrow': 'Correct a fact',
  'profile.correct.slideOver.title': "What's the correct version?",
  'profile.correct.slideOver.sub': 'The agent will use this to refine future scans.',
  'profile.correct.slideOver.placeholder': 'e.g. "I left Stripe in 2020, not 2021."',
  'profile.correct.slideOver.submitCta': 'Submit correction',
  'profile.correct.slideOver.cancelCta': 'Cancel',
  'profile.correct.slideOver.savingLabel': 'Submitting…',
  'profile.correct.slideOver.savedToast': 'Correction received.',
  'profile.correct.slideOver.errorToast': "Couldn't submit. Try again.",
  'profile.correct.slideOver.closeLabel': 'Close',

  // Company profile — /profile/company (v7 redesign, epic #123 Phase 3).
  // Same told/learned model as the personal profile: hover-pencil told
  // rows for the cadence + market + funding + engineering snapshots the
  // operator can state directly, plus a learned-facts section driven by
  // the existing onboarding-time `enrichCompany` web synthesis. Admin-
  // only edits; non-admin members see the same content read-only.
  'companyProfile.title': 'Everything we know about your company.',
  'companyProfile.sub':
    'Two layers: what your admins told us during onboarding (hover any row to edit), and what we learned from public web research.',
  'companyProfile.reRunCta': 'Re-run company setup',
  'companyProfile.adminOnly.notice': 'Edits require admin role.',
  // Operating rituals — when the team meets + how cadence is anchored.
  'companyProfile.section.rituals.eyebrow': 'Operating rituals',
  'companyProfile.section.rituals.sub':
    'When your team meets and on what cadence. Drives the briefing and L10 prep.',
  'companyProfile.told.row.briefingDayOfWeek.label': 'Briefing day',
  'companyProfile.told.row.briefingDayOfWeek.placeholder': '1 (Mon) – 7 (Sun)',
  'companyProfile.told.row.briefingTime.label': 'Briefing time',
  'companyProfile.told.row.briefingTime.placeholder': '07:30',
  'companyProfile.told.row.l10DayOfWeek.label': 'L10 day',
  'companyProfile.told.row.l10DayOfWeek.placeholder': '1 (Mon) – 7 (Sun)',
  'companyProfile.told.row.l10Time.label': 'L10 time',
  'companyProfile.told.row.l10Time.placeholder': '09:30',
  'companyProfile.told.row.usesEosL10.label': 'EOS L10 format',
  'companyProfile.told.row.usesEosL10.placeholder': 'yes / no',
  'companyProfile.told.row.rocksKickoffWeek.label': 'Rocks kickoff week',
  'companyProfile.told.row.rocksKickoffWeek.placeholder': 'Week 1–13 of quarter',
  // Market position.
  'companyProfile.section.market.eyebrow': 'Market position',
  'companyProfile.section.market.sub':
    'Who you sell to and the space you play in. Shapes signal filtering and ICP reasoning.',
  'companyProfile.told.row.industryTags.label': 'Industry tags',
  'companyProfile.told.row.industryTags.placeholder': 'b2b-saas, devtools, infra',
  'companyProfile.told.row.customerProfile.label': 'Customer profile',
  'companyProfile.told.row.customerProfile.placeholder': 'Who buys today',
  'companyProfile.told.row.idealCustomerProfile.label': 'Ideal customer (ICP)',
  'companyProfile.told.row.idealCustomerProfile.placeholder': 'Who you want to win next',
  // Funding history.
  'companyProfile.section.funding.eyebrow': 'Funding history',
  'companyProfile.section.funding.sub':
    'Latest round, lead investor, and runway. The agent corroborates against public sources.',
  'companyProfile.told.row.latestRoundLabel.label': 'Latest round',
  'companyProfile.told.row.latestRoundLabel.placeholder': 'Series B',
  'companyProfile.told.row.latestRoundUsd.label': 'Round size (USD)',
  'companyProfile.told.row.latestRoundUsd.placeholder': '48000000',
  'companyProfile.told.row.leadInvestor.label': 'Lead investor',
  'companyProfile.told.row.leadInvestor.placeholder': 'Bessemer Venture Partners',
  'companyProfile.told.row.runwayMonths.label': 'Runway (months)',
  'companyProfile.told.row.runwayMonths.placeholder': '24',
  'companyProfile.told.row.latestRoundClosedAt.label': 'Round closed',
  'companyProfile.told.row.latestRoundClosedAt.placeholder': 'YYYY-MM-DD',
  // Engineering org shape.
  'companyProfile.section.engineering.eyebrow': 'Engineering org',
  'companyProfile.section.engineering.sub':
    'Team size, languages, repo count. Refined by the GitHub connector once wired.',
  'companyProfile.told.row.teamSize.label': 'Eng team size',
  'companyProfile.told.row.teamSize.placeholder': '24',
  'companyProfile.told.row.primaryLanguages.label': 'Primary languages',
  'companyProfile.told.row.primaryLanguages.placeholder': 'TypeScript, Python, Rust',
  'companyProfile.told.row.repoCount.label': 'Repo count',
  'companyProfile.told.row.repoCount.placeholder': '12',
  'companyProfile.told.row.monorepo.label': 'Monorepo',
  'companyProfile.told.row.monorepo.placeholder': 'yes / no',
  // Learned section (web research).
  'companyProfile.section.learned.eyebrow': "What we've learned",
  'companyProfile.section.learned.sub':
    'From public web research. Every claim cites its source and carries a confidence band.',
  'companyProfile.empty.learned.title': "We haven't learned anything yet.",
  'companyProfile.empty.learned.body':
    'Add your company URL in onboarding so the agent can start researching.',
  // Saving microcopy — passed through the InlineEdit primitive.
  'companyProfile.told.savingLabel': 'Saving…',
  'companyProfile.told.savingFailedLabel': "Couldn't save — try again.",
  // Toast surfaced when the optimistic PUT rolls back. The InlineEdit
  // primitive shows `savingFailedLabel` inline; the toast adds the
  // out-of-row affordance so the user notices even if they've already
  // moved on to a different row.
  'companyProfile.error.saveFailed':
    "Couldn't save the company profile — we rolled back your change.",

  // Sidebar label for the admin-only company profile link.
  'sidebar.companyProfile': 'Company profile',

  'common.confidence': 'Confidence',
  'common.source': 'Source',
  'common.last-generated': 'Last generated',
  'common.loading': 'Loading…',
  // Three-letter month abbreviations. Used as <select> options for
  // fiscal-year-start-month pickers + anywhere else the UI needs a
  // compact month label. Per-month keys (rather than one comma-joined
  // string) give translators per-token flexibility.
  'common.month.jan': 'Jan',
  'common.month.feb': 'Feb',
  'common.month.mar': 'Mar',
  'common.month.apr': 'Apr',
  'common.month.may': 'May',
  'common.month.jun': 'Jun',
  'common.month.jul': 'Jul',
  'common.month.aug': 'Aug',
  'common.month.sep': 'Sep',
  'common.month.oct': 'Oct',
  'common.month.nov': 'Nov',
  'common.month.dec': 'Dec',
  'palette.ai-suggestions': 'AI suggestions',
  'palette.ai-thinking': 'Finding refinements…',
  'palette.placeholder': 'Search, ask, or type / for commands',
  'palette.try': 'Try',
  'palette.entities': 'Entities',
  'palette.add-to-watchlist': 'Add to watchlist',
  'watchlist.toggle.add': 'Watch',
  'watchlist.toggle.watching': 'Watching',
  'watchlist.toggle.aria-add': 'Add to watchlist',
  'watchlist.toggle.aria-remove': 'Remove from watchlist',
  'palette.commands': 'Commands',
  'palette.ask': 'Ask',
  'palette.nav.graph': 'Go to network',
  'palette.nav.dossiers': 'Go to dossiers',
  'palette.cmd.theme': 'Toggle theme',
  'palette.cmd.language': 'Toggle language',
  'menu.density': 'Density',
  'menu.language': 'Language',
  'menu.theme': 'Theme',
  // Surfaces that ship as UI shells today (Profile, Billing) carry a
  // small "preview" hint in the account menu so users aren't surprised
  // by no-op CTAs. Removed once the backend wiring lands (#114).
  'menu.preview': 'Preview',
  'menu.preview.title': 'This surface is a design preview — some actions don’t persist yet.',
  'theme.daylight': 'Daylight',
  'theme.midnight': 'Midnight',
  'theme.system': 'System',
  'menu.account': 'Account',
  'thread.new': 'New thread',
  'thread.pin': 'Pin',
  'thread.unpin': 'Unpin',
  'thread.fork': 'Fork',
  'thread.rename': 'Rename',
  'thread.delete': 'Delete',
  'thread.empty': 'No threads yet. Start by asking something.',
  'chat.title': 'Conversation',
  'chat.title.dossier': 'Discuss this dossier',
  'chat.title.graph': 'Discuss this graph',
  'chat.empty.dossier':
    'Ask anything about this dossier — relationships, sourcing, gaps, next steps.',
  'chat.empty.graph': 'Ask anything about this graph view — paths, hubs, hidden communities.',
  'chat.empty.free':
    'Ask anything across the whole corpus — entities, relationships, evidence gaps.',
  'chat.placeholder': 'Ask a follow-up… (⌘↵ to send)',
  'chat.send': 'Send',
  'chat.stop': 'Stop',
  'chat.assistant': 'The Cup',
  'chat.threads.title': 'Past conversations',
  'chat.action.applied': 'Done',
  'chat.action.failed': 'Failed',
  'chat.reasoning.label': 'Reasoning · {n}',
  // Agentic-retrieval trace labels (issue #39). Surfaced in the
  // <Reasoning> disclosure as `detail` strings — server emits the
  // step code ("plan" / "iteration" / "tool" / "critique") and the
  // client formats the human-readable detail.
  'chat.reasoning.plan.direct': 'Direct retrieval',
  'chat.reasoning.plan.decomposed': 'Decomposed into {n} sub-quer{plural}',
  'chat.reasoning.iteration': 'Iteration {n} of {max}',
  'chat.reasoning.subquery': 'Sub-query: {query}',
  'chat.reasoning.tool_result': '{tool}: +{f} facts / +{s} sources',
  'chat.reasoning.critique.sufficient': 'Evidence sufficient (confidence {conf})',
  'chat.reasoning.critique.gaps': 'Gaps remain (confidence {conf}): {gaps}',
  'chat.scroll-to-latest': 'Scroll to latest message',
  // Friendly error renderings — replace raw JSON / stack-trace dumps with
  // a single sentence that tells the analyst what to do next. The
  // formatter (chat-pane.tsx → formatChatError) maps Vertex / Firebase
  // error envelopes onto these keys.
  'chat.error.quota': 'The AI service is at capacity right now. Try again in a minute.',
  'chat.error.auth': 'Your session expired. Refresh the page and sign in again.',
  'chat.error.permission': "You don't have permission to access this organization's data.",
  'chat.error.server': 'Something went wrong on our end. Please retry in a moment.',
  'chat.error.network': 'Network error — check your connection and retry.',
  // Out-of-scope: the orchestrator's planner classified the question
  // as outside the corpus. Server emits `OUT_OF_SCOPE: <reason>` on the
  // SSE error event; formatChatError matches the prefix and routes
  // here. The reason from the planner doesn't surface in the UI today
  // (it's logged on the server) — we lean on a stable, scannable
  // sentence the analyst will read once and recognize on every future
  // off-topic ask.
  'chat.error.scope':
    "I can only help with questions about your watchlist or this org's intelligence corpus. Try asking about a tracked entity, organization, or document.",
  'chat.error.generic':
    'Something went wrong while processing your question. If this keeps happening, copy the conversation id and ping the team.',
  'chat.error.dismiss': 'Dismiss',
  'chat.error.retry': 'Try again',
  // Per-verb chip messages. {entity} / {a} / {b} are interpolated by
  // tFormat from the action payload; the dispatcher returns the raw
  // verb + entity, never a pre-formatted English string.
  'chat.action.pinEntity.applied': 'Pinned {entity} to your watchlist.',
  'chat.action.unpinEntity.applied': 'Removed {entity} from your watchlist.',
  'chat.action.focusSubgraph.applied': 'Focused graph on {entity}.',
  'chat.action.openDossier.applied': 'Opening {entity} dossier.',
  'chat.action.compareEntities.applied': 'Comparing {a} with {b}.',
  'task.tray.title': 'Active tasks',
  'task.tray.empty': 'No active tasks',
  'task.open': 'Open deliverable',
  'task.demo': 'Run demo task',
  'task.retry': 'Retry',
  'synthesis.edit': 'Edit',
  'synthesis.edited-by': 'Edited by {name}',
  'synthesis.save': 'Save',
  'synthesis.cancel': 'Cancel',
  'synthesis.edit-reason.placeholder': 'Optional: why are you correcting this?',
  'brief.save': 'Save',
  'brief.share': 'Share link',
  'brief.link-copied': 'Link copied',
  'brief.recent': 'Recent briefs',
  'brief.empty-recent': 'No saved briefs yet.',
  'brief.read-only': 'Read only',
  'brief.edit': 'Edit',
  // Briefing hero card eyebrow labels — one per BriefingHighlightKind.
  // Changing the enum without touching these produces a TS error
  // (Record<BriefingHighlightKind, …> on the consumer side).
  'today.hero.kind.north-star': 'North star',
  'today.hero.kind.priority': 'Priority',
  'today.hero.kind.industry': 'Industry',
  'today.hero.kind.signal': 'Signal',
  'today.hero.kind.playbook': 'Playbook',
  'today.hero.kind.note': 'Note',
  'today.hero.open': 'Open',
  'today.section.for-your-attention': 'For your attention',
  'today.section.outside-view': 'Outside view',
  'today.section.recent-notes': 'Recent notes',
  'today.section.jump-to': 'Jump to',
  'today.link.all-signals': 'All signals',
  'today.link.all-notes': 'All notes',
  'today.empty.signals.prefix': 'No recent signals yet — subscribe to topics on ',
  'today.empty.signals.cta': 'Outside view',
  'today.empty.signals.suffix': ' to start the feed.',
  'today.empty.notes.prefix': 'No notes yet. Open ',
  'today.empty.notes.cta': 'Notes',
  'today.empty.notes.suffix': ' to capture your first one.',
  'today.empty.heroes.member':
    "The agent's hero cards fill in once an org admin completes onboarding (industry, stage, top priorities). Ask an admin to run the onboarding wizard — your read of the rest of the portal works in the meantime.",
  'today.empty.heroes.admin.prefix': 'Run the ',
  'today.empty.heroes.admin.cta': 'onboarding wizard',
  'today.empty.heroes.admin.suffix':
    " to capture your business context — once it's there, the agent surfaces what to look at first each day.",
  'today.notes.untitled': 'Untitled note',
  'today.notes.updated': 'Updated {when}',
  'today.qa.ask': 'Ask the agent',
  'today.qa.note': 'Take a note',
  'today.qa.outside-view': 'Outside view',
  'today.qa.scorecard': 'Scorecard',
  // Provenance line on the For-your-attention section header.
  // Surfaces that the section is AI-synthesized so the CEO knows where
  // the framings came from. {model} is a humanized model name
  // ('Gemini', 'rules-based'); {when} is a relative-date string.
  'today.hero.provenance.synced': 'AI-personalized · {model} · synced {when}',
  'today.hero.provenance.rules': 'Personalized · {when} (rules-based)',
  // Share-panel (briefShare.*) — analyst-side surface for minting + revoking
  // tokenized public links. Public-brief strings live under publicBrief.*.
  'briefShare.panelTitle': 'Share with a principal',
  'briefShare.panelDescription':
    'Generate a read-only link that works without signing in. Revoke any time.',
  'briefShare.labelLabel': 'Link label (optional)',
  'briefShare.labelPlaceholder': 'e.g. Board read, Apr 2026',
  'briefShare.generateCta': 'Generate share link',
  'briefShare.copyLink': 'Copy link',
  'briefShare.copied': 'Copied to clipboard',
  'briefShare.shareLink': 'Share link',
  'briefShare.activeLinksTitle': 'Active share links',
  'briefShare.noActiveLinks':
    'No active share links. Generate one to share this brief with a principal.',
  'briefShare.untitledLink': 'Untitled link',
  'briefShare.revoke': 'Revoke',
  'briefShare.revokeConfirm': 'Revoke this link? The principal will lose access immediately.',
  'briefShare.generatedAt': 'Created {date}',
  'briefShare.generateError': "Couldn't create share link. Try again.",
  'mfa.enroll.title': 'Two-factor authentication',
  'mfa.enroll.subtitle':
    'Required for analyst-role access. Scan the code with your authenticator app.',
  'mfa.enroll.secret-label': 'Or enter this secret manually',
  'mfa.enroll.code-label': '6-digit code',
  'mfa.enroll.submit': 'Complete enrollment',
  'mfa.challenge.title': 'Enter your 6-digit code',
  'mfa.challenge.subtitle': 'Open your authenticator app and enter the 6-digit code for LeanOpx.',
  'mfa.challenge.submit': 'Continue',
  'mfa.challenge.noFactor': 'No enrolled second factor found.',
  'nav.er': 'Entity resolution',
  'er.title': 'Entity resolution queue',
  'er.pending': 'Pending',
  'er.promoted': 'Promoted',
  'er.rejected': 'Rejected',
  'er.promote': 'Promote',
  'er.reject': 'Reject',
  'er.unsure': 'Unsure',
  'er.no-candidates': 'No candidates in this state.',
  'er.candidate-for': 'Candidate for',
  'er.no-link': 'No confident link',
  'er.detail.title': 'Candidate details',
  'er.detail.observed': 'Observed name',
  'er.detail.confidence': 'Confidence',
  'er.detail.derivation': 'Derivation',
  'er.detail.source': 'Source',
  // Streamliner-extended ER queue (issue #36). The page now shows
  // four kinds of review items, not just alias candidates.
  'er.subtitle':
    'Probabilistic, non-merging. Promotion preserves the original lineage. Streamliner output appears alongside ingest-time candidates.',
  'er.kind.label': 'Filter by kind',
  'er.kind.all': 'All',
  'er.kind.alias': 'Alias',
  'er.kind.lowConfidenceFact': 'Low-conf fact',
  'er.kind.missingEdge': 'Missing edge',
  'er.kind.redundantSchema': 'Redundant schema',
  'er.evidence.fact': 'Edge under review',
  'er.evidence.coMention': 'Frequently co-mentioned',
  'er.evidence.coMention.detail': 'Co-mentioned in {n} documents but no direct edge in the graph.',
  'er.evidence.schema': 'Likely-redundant predicates',
  'er.evidence.schema.detail': 'Occurrences A: {a}, B: {b}. Reason: {reason}.',
  // Compare (side-by-side dossier).
  'compare.title': 'Compare entities',
  'compare.empty': 'Pass two entities via the URL: /compare?a=<name>&b=<name>.',
  'compare.same-entity': 'Cannot compare an entity with itself.',
  'compare.section.facts': 'Facts',
  'compare.section.facts.empty': 'No facts on this entity.',
  'compare.section.sources': 'Sources',
  'compare.section.sources.empty': 'No sources on this entity.',
  'compare.shared.title': 'Shared',
  'compare.shared.neighbors': 'Common neighbors',
  'compare.shared.neighbors.empty': 'No 1-hop neighbors connect both entities.',
  'compare.shared.sources': 'Common sources',
  'compare.shared.sources.empty': 'No source documents mention both.',
  'compare.action.with': 'Compare with…',
  'compare.picker.title': 'Compare with another entity',
  'compare.picker.description': 'Pick a neighbor or type any entity name.',
  'compare.picker.placeholder': 'Search neighbors or type a name…',
  'compare.picker.empty': 'No matching neighbor.',
  'compare.picker.compare-with': 'Compare with',
  // Todos.
  'todos.title': 'Todos',
  'todos.subtitle': 'Personal task list scoped to this org.',
  'todos.composer.placeholder': 'Add a todo — Enter to save…',
  'todos.composer.add': 'Add',
  'todos.empty.no-todos': 'No todos yet. Add the first one above.',
  'todos.empty.no-match': 'No todos match this filter.',
  'todos.error.title': "Couldn't load todos",
  'todos.error.body':
    'The request failed. Showing the last good list if cached, otherwise nothing yet.',
  'todos.filter.all': 'All',
  'todos.filter.todo': 'Open',
  'todos.filter.in_progress': 'In progress',
  'todos.filter.done': 'Done',
  'todos.status.todo': 'open',
  'todos.status.in_progress': 'in progress',
  'todos.status.done': 'done',
  'todos.priority': 'Priority',
  'todos.priority.low': 'Low',
  'todos.priority.normal': 'Normal',
  'todos.priority.high': 'High',
  'todos.action.advance.todo': 'Re-open',
  'todos.action.advance.in_progress': 'Mark in-progress',
  'todos.action.advance.done': 'Mark done',
  'todos.action.delete': 'Delete todo',
  'todos.page.sub':
    'Your personal task list. Promote `- [ ]` checkboxes from notes — flipping a todo to done updates the source note.',
  'todos.action.mark-done': 'Mark done',
  'todos.action.mark-not-done': 'Mark not done',
  'todos.composer.inline-placeholder': 'What needs doing?',
  'todos.loading': 'Loading…',
  'todos.empty.open': 'Nothing on your list. Add one above or promote a `- [ ]` from a note.',
  'todos.empty.done': 'No completed todos yet.',
  'todos.tab.open': 'Open',
  'todos.tab.done': 'Done',
  'todos.row.due-prefix': 'due',
  'todos.row.from-note': 'from note',
  'todos.row.open-source-note': 'Open source note',
  'todos.toast.load-failed': 'Failed to load todos',
  'todos.toast.create-failed': 'Failed to create todo',
  'todos.toast.update-failed': 'Failed to update todo',
  'todos.toast.delete-failed': 'Failed to delete todo',

  // /walkthrough — first-run guided tour (designer reference mock).
  'walkthrough.eyebrow': 'Welcome aboard',
  'walkthrough.title': 'Five things to know in 60 seconds.',
  'walkthrough.sub':
    'A guided tour of LeanOpx — headline, insights, calendar, drafts, ask. Step through the spotlights below; you can replay any time from /admin/settings.',
  'walkthrough.start': 'Start tour',
  'walkthrough.callout.agent-tour': 'Agent tour',
  'walkthrough.callout.skip': 'Skip',
  'walkthrough.callout.next': 'Next →',
  'walkthrough.list.heading': 'The 5-stop tour',
  'walkthrough.footer.path': 'Headline → Insights → Calendar → Drafts → Ask',
  'walkthrough.footer.label': 'WALKTHROUGH',
  // /walkthrough — static BriefingMock that the spotlight overlays.
  // Purely illustrative; the real Today briefing renders at /today.
  'walkthrough.briefing.eyebrow': 'Command Center · Monday briefing',
  'walkthrough.briefing.greeting': 'Good evening, Nima.',
  'walkthrough.briefing.activeOrg': 'Active org · LeanOpx QA',
  'walkthrough.briefing.headline': 'Quiet morning — your',
  'walkthrough.briefing.headlineMetric': 'net new ARR',
  'walkthrough.briefing.headlineTail': 'is steady.',
  'walkthrough.briefing.cardLabel': '● Card {i}',

  // /accountability/chart — org chart (designer reference scaffold).
  'orgChart.eyebrow': 'Org chart · reporting',
  'orgChart.title': 'How LeanOpx is wired.',
  'orgChart.sub':
    'Visual reporting structure. Pulls from your HRIS + the membership graph; the agent uses it to decide who owns what when escalating.',
  'orgChart.action.export': 'Export',
  'orgChart.action.addPerson': '+ Add person',
  'orgChart.svg.aria': 'Org chart',
  'orgChart.teams.heading': 'Teams · what each one owns',
  'orgChart.teams.lead': 'Lead',
  'orgChart.teams.owns': 'Owns →',
  'orgChart.teams.noRocks': 'No active rocks',
  // Headcount line: "Lead · {name} · {count} person|people". Split on
  // singular/plural so a localizer can use the right plural form (some
  // locales have 0/1/few/many forms — keeping two keys lets us expand
  // to a CLDR-style helper without changing call sites).
  'orgChart.teams.countPerson': 'person',
  'orgChart.teams.countPeople': 'people',
  'orgChart.depts.heading': 'Departments · spend & headcount',
  'orgChart.depts.col.department': 'Department',
  'orgChart.depts.col.headcount': 'Headcount',
  'orgChart.depts.col.openRoles': 'Open roles',
  'orgChart.depts.col.q2Spend': 'Q2 spend',
  'orgChart.depts.col.trend': '13-week trend',
  'orgChart.rail.shape': 'Org shape',
  'orgChart.rail.shape.people': 'people',
  'orgChart.rail.shape.teams': 'teams',
  'orgChart.rail.shape.openRoles': 'open roles',
  'orgChart.rail.shape.span': 'span (avg)',
  'orgChart.rail.viewAs': 'View as',
  'orgChart.rail.viewAs.tree': 'Tree (reporting)',
  'orgChart.rail.viewAs.teams': 'Teams',
  'orgChart.rail.viewAs.depts': 'Departments',
  'orgChart.rail.viewAs.locations': 'Locations',
  'orgChart.rail.agentNoticed': 'Agent · noticed',
  'orgChart.rail.agentNoticed.body':
    "Engineering's span of control is highest (6). Two open senior roles concentrate risk in one team.",
  'orgChart.filter.all': 'All',
  'orgChart.filter.engineering': 'Engineering · 7',
  'orgChart.filter.gtm': 'GTM · 3',
  'orgChart.filter.ops': 'Ops · 1',
  'orgChart.filter.design': 'Design · 1',
  'todos.linked.investigation': 'investigation',
  'nav.todos': 'Todos',
  'nav.map': 'Map',
  // Map page.
  'map.title': 'Map',
  'map.empty':
    'No entities have coordinates yet. Add `entity_locations` to your corpus to surface them here.',
  'map.error.title': 'Map data unavailable',
  'map.error.body': 'The map entities request failed. Check your connection and try again.',
  'map.year-range': 'Active in',
  'map.year-range.clear': 'clear',
  'map.showing': 'showing',
  'graph.insight.bridge.title': 'The structural bridge',
  'graph.insight.bridge.body-1':
    'Inside this 2-hop subgraph, Trita Parsi sits at the highest betweenness centrality — more shortest paths run through him than any other node.',
  'graph.insight.bridge.body-2':
    'He links two clusters that otherwise barely touch: the Quincy Institute think-tank orbit, and the NIAC / NIAC Action advocacy apparatus.',
  'graph.insight.bridge.body-3':
    'Remove him and the network fragments — a signature of a structural broker rather than a peripheral participant.',
  'graph.insight.bridge.body-removed':
    'With Parsi removed, disconnected components remain — the clusters come apart.',
  'graph.preset.label': 'Preset',
  'graph.preset.back': 'Back to full view',
  'graph.node.detail.title': 'Node details',
  'graph.node.detail.entity': 'Entity',
  'graph.node.detail.type': 'Type',
  'graph.node.action.open': 'Open dossier',
  'graph.node.action.focus': 'Focus on graph',
  'graph.zoom.toolbar': 'Zoom controls',
  'graph.zoom.in': 'Zoom in',
  'graph.zoom.out': 'Zoom out',
  'graph.zoom.fit': 'Fit to view',
  'graph.toolbar.export': 'Export PNG',
  'graph.toolbar.lasso': 'Box-select',
  'graph.toolbar.lasso.hint': 'Hold shift and drag to box-select.',
  'graph.toolbar.group': 'Group by type',
  'graph.context.open': 'Open dossier',
  'graph.context.focus': 'Focus subgraph',
  'graph.context.pin': 'Pin position',
  'graph.context.unpin': 'Unpin',
  'graph.context.hide': 'Hide node',
  'graph.context.find-paths': 'Find paths to…',
  'graph.context.path-from': 'Set as path "from"',
  'graph.context.path-to': 'Set as path "to"',
  'graph.context.watchlist': 'Add to watchlist',
  'graph.context.compare': 'Compare with…',
  'graph.bulk.selected': '{n} selected',
  'graph.bulk.clear': 'Clear',
  'graph.bulk.add-watchlist': 'Add all to watchlist',
  'graph.bulk.compare': 'Compare these two',
  'graph.bulk.hide': 'Hide selected',
  'graph.bulk.pin': 'Pin selected',
  'graph.pane.detail': 'Detail',
  'graph.pane.filters': 'Filters',
  'graph.pane.overlay': 'Overlay',
  'graph.pane.views': 'Views',
  'graph.filters.labels': 'Entity types',
  'graph.filters.relationships': 'Relationship types',
  'graph.filters.hidden': 'Hidden nodes',
  'graph.filters.unhide-all': 'Unhide all',
  'graph.filters.show-all': 'Show all',
  'graph.filters.hide-all': 'Hide all',
  'graph.filters.empty.types': 'No entity types in this view.',
  'graph.filters.empty.rels': 'No relationship types in this view.',
  'graph.overlay.title': 'Color overlay',
  'graph.overlay.subtitle': 'Color and size nodes by a structural metric.',
  'graph.overlay.none': 'Off (by type)',
  'graph.overlay.degree': 'Degree',
  'graph.overlay.degree.desc': 'Direct neighbor count — finds local hubs.',
  'graph.overlay.betweenness': 'Betweenness',
  'graph.overlay.betweenness.desc': 'Bridge importance — entities that connect clusters.',
  'graph.overlay.community': 'Communities',
  'graph.overlay.community.desc':
    'Connected-component grouping — discrete clusters get distinct colors.',
  'graph.overlay.community.count': '{n} groups',
  'graph.views.title': 'Saved views',
  'graph.views.subtitle': 'Snapshots of mode, focus, filters, and pinned nodes.',
  'graph.views.save': 'Save current view',
  'graph.views.name.placeholder': 'Name this view…',
  'graph.views.empty': 'No saved views yet.',
  'graph.views.load': 'Load',
  'graph.views.delete': 'Delete',
  'graph.views.created': 'Saved {date}',
  'graph.export.filename': 'graph',
  'graph.search.label': 'Search graph',
  'graph.search.placeholder': 'Find an entity by name…',
  'graph.search.no-matches': 'no matches',
  'graph.search.close': 'Close search',
  'graph.search.open': 'Find on graph',
  'graph.search.open-hint': 'Find on graph (press /)',
  'graph.search.list.label': 'Matched entities',
  'graph.search.list.open': 'Show match list',
  'graph.search.list.close': 'Hide match list',
  'graph.toolbar.show-all': 'Show all',
  'graph.toolbar.show-top-hubs': 'Top hubs',
  'graph.toolbar.show-all.hint': 'Show every entity, including the long tail',
  'graph.toolbar.show-top-hubs.hint': 'Hide low-degree nodes; show only the top hubs',
  'graph.a11y.connections': 'Graph connections',
  'graph.filters.confidence': 'Confidence',
  'graph.filters.confidence.all': 'All',
  'graph.filters.confidence.mid': 'Medium+',
  'graph.filters.confidence.high': 'High',
  'graph.filters.density': 'Density',
  'graph.filters.density.hubs-only': 'Hubs only',
  'graph.filters.community': 'Communities',
  'graph.filters.community.show-all': 'Show all',
  'graph.filters.community.entities': 'entities',
  'graph.filters.sources': 'Source documents',
  'graph.filters.year': 'Year',
  'graph.filters.year.from': 'From',
  'graph.filters.year.to': 'To',
  'graph.filters.year.any': 'Any',
  'graph.filters.year.reset': 'Reset',
  'graph.context.menu': 'Node actions',
  'graph.pinned.label': 'Pinned',
  'graph.mode.overview': 'Overview',
  'graph.mode.focus': 'Focus',
  'graph.mode.path': 'Path',
  'graph.input.entity-name': 'Entity name',
  'graph.input.path-from': 'From',
  'graph.input.path-to': 'To',
  'common.reset': 'Reset',
  'splitPane.open': 'Open pane',
  'splitPane.close': 'Collapse pane',
  'splitPane.resize': 'Resize pane',
  'pane.status.title': 'Status',
  'pane.stats.title': 'Stats',
  'pane.status.classification': 'classification',
  'pane.status.classification.value': 'RESTRICTED',
  'pane.status.clearance': 'clearance',
  'pane.status.session': 'session',
  'pane.status.session.live': 'LIVE',
  'pane.status.signed-in': 'signed in as',
  'pane.status.org': 'active organization',
  'pane.stats.empty': 'Graph stats unavailable.',
  'graph.insight.remove-parsi': 'Remove Parsi from the graph',
  'graph.insight.restore-parsi': 'Restore Parsi',
  'graph.insight.components': '{n} connected components',
  'graph.insight.betweenness': 'Betweenness top 5',
  'preset.title': 'Try the demo arc',
  'preset.hero.1': 'Trace every connection between the IRGC and Robert Malley.',
  'preset.insight.1':
    'Who are the main individuals connected to both NIAC and the Quincy Institute?',
  'preset.dossier.1': 'Brief me on Trita Parsi.',
  'preset.path.1': 'Show the full chain from Open Society Foundations to the US State Department.',
  'preset.stumper.1': "What is Alavi Foundation's 2024 annual budget?",
  'preset.stumper.2':
    "Which Iranian defectors have shared evidence about Siamak Namazi's detention?",
  'nav.more': 'More',
  // Capital-flows page
  'capital.hero': 'Follow the capital, read the intent.',
  'capital.header-note': '{total}M traced · {count} grant records · 2013–2021',
  'capital.grants.ranked': 'Grant flows, ranked by magnitude',
  'capital.bar-note': 'bar width = relative amount',
  'capital.pattern.alavi.title': 'Flagged — Alavi Foundation',
  'capital.pattern.alavi.body':
    'Subject of 2008 DOJ civil-forfeiture action alleging the foundation operated as a front for Bank Melli Iran, a sanctioned entity. $500M in assets forfeited 2017.',
  'capital.pattern.ploughshares.title': 'Pattern — Ploughshares hub',
  'capital.pattern.ploughshares.body':
    'Ploughshares Fund functions as the structural hub of US arms-control advocacy, distributing the majority of grants in this slice across 40+ recipients.',
  // Data-sources page
  'sources.hero': 'The cup draws from many wells.',
  'sources.header-note': '10 federated sources · 7 live, 2 on-demand, 1 static',
  'sources.col.source': 'Source',
  'sources.col.category': 'Category',
  'sources.col.records': 'Records',
  'sources.col.freshness': 'Freshness',
  'sources.col.use': 'Primary use',
  'sources.col.status': 'Status',
  'sources.status.healthy.tooltip':
    'Source is responding within SLA. Last ingest succeeded; no rate-limit or access errors.',
  'sources.status.warn.tooltip':
    'Degraded — ingest still works but with limits or partial coverage. See note for details.',
  'sources.status.critical.tooltip':
    'Source is failing — recent ingests errored or are blocked. See note for details.',
  'sources.ontology-title': 'Ontology notes',
  'sources.ontology.body-pre':
    'LeanOpx does not merge records. It maintains source-of-origin for every claim and exposes lineage in every answer. Entity resolution is probabilistic — see the ',
  'sources.ontology.body-link': 'resolution queue',
  'sources.ontology.body-post': ' for candidates awaiting analyst promotion.',
  // Ask the Cup query page
  'query.hero': 'Ask, and the cup answers.',
  'query.header-note':
    'natural-language query · routed across 7 data sources · answered via graph + LLM',
  'query.console-title': 'Query console',
  'query.placeholder': 'Which orgs connect Trita Parsi to Iranian state capital?',
  'query.execute': 'Execute',
  'query.try': 'Try',
  'query.sources-label': 'Sources',
  'query.suggest.1': 'Which orgs connect Trita Parsi to Iranian state capital?',
  'query.suggest.2': 'Map the full downstream grant network of Alavi Foundation since 2010.',
  'query.suggest.3': 'Show me diaspora board members who never appear in FARA filings.',
  // Ask the Cup
  'ask.hero-prompt': 'What are you looking for?',
  'ask.mode.ask': 'Ask',
  'ask.mode.template': 'Template',
  'ask.mode.cypher': 'Cypher',
  'ask.mode.entity': 'Entity',
  'ask.submit': 'Ask',
  // Signals feed + watchlist
  'signals.empty':
    'No signals yet. Add entities to your watchlist — the nightly scan will surface changes here.',
  'signals.dismiss': 'Dismiss',
  'watchlist.title': 'Watchlist',
  'watchlist.empty': 'No entities watched yet.',
  'watchlist.signal-singular': 'signal',
  'watchlist.signal-plural': 'signals',
  'watchlist.add': '+ Add',
  'watchlist.remove': 'Remove',
  'watchlist.edit': 'Edit',
  'watchlist.edit.description': 'Categorize, pin, mute, or annotate this entity.',
  'watchlist.uncategorized': 'Uncategorized',
  'watchlist.group.pinned': 'Pinned',
  'watchlist.field.category': 'Category',
  'watchlist.field.category.placeholder': 'e.g. lobby, officials, research',
  'watchlist.field.note': 'Note',
  'watchlist.field.note.placeholder': 'Short context — what to look for.',
  'watchlist.field.pinned': 'Pin to top',
  'watchlist.field.mute': 'Mute signals',
  'watchlist.mute.off': 'Off',
  'watchlist.mute.1h': 'For 1 hour',
  'watchlist.mute.1d': 'For 1 day',
  'watchlist.mute.1w': 'For 1 week',
  'watchlist.mute.forever': 'Until I unmute',
  'common.save': 'Save',
  'watchlist.since-prefix': 'Since your last visit',
  'watchlist.since-first-visit': 'Welcome back — first morning brief.',
  // Capture-to-investigation dialog (Phase 2 bridge).
  // Opened from a source row (dossier, ask-result) to log the source as
  // a link in an investigation's evidence chain.
  'capture.triggerLabel': 'Save to investigation',
  'capture.dialogTitle': 'Save finding to investigation',
  'capture.dialogDescription': 'Log this source as a link in an investigation evidence chain.',
  'capture.fromDossier': 'from {name} dossier',
  'capture.pickInvestigation': 'Pick investigation',
  'capture.newInvestigation': 'New investigation…',
  'capture.newInvestigationTitleLabel': 'Investigation title',
  'capture.newInvestigationHypothesisLabel': 'Hypothesis (optional)',
  'capture.claimLabel': 'Claim',
  'capture.claimPlaceholder': 'What does this source support?',
  'capture.confidenceLabel': 'Confidence',
  'capture.confidenceLow': 'low',
  'capture.confidenceMedium': 'medium',
  'capture.confidenceHigh': 'high',
  'capture.saveCta': 'Save to investigation',
  'capture.saving': 'Saving…',
  'capture.cancelCta': 'Cancel',
  'capture.savedToastPrefix': 'Saved to “',
  'capture.viewCta': 'View investigation',
  'capture.addAnother': 'Add another',
  'capture.closeCta': 'Close',
  'capture.missingClaim': 'Enter a claim before saving.',
  'capture.missingPick': 'Pick an investigation first.',
  'capture.loadError': 'Failed to load investigations.',
  'capture.emptyInvestigations': 'No investigations yet — create a new one.',
  // Investigation → brief composer handoff CTA.
  'investigationBrief.composeCta': 'Compose brief from investigation',
  'investigationBrief.composeAria': 'Compose brief from this investigation',
  // Palette slash-commands + verb dispatches (Phase 2 additions).
  'palette.cmd.newInvestigation': 'Create investigation',
  'palette.cmd.viewBriefs': 'View my briefs',
  'palette.verb.investigate': 'Investigate {target}',
  // Public (principal-facing) shared brief — unauthenticated /b/$token page.
  'publicBrief.brandLabel': 'Shared brief',
  'publicBrief.preparedBy': 'Prepared by {name}',
  'publicBrief.preparedAt': 'Prepared {date}',
  'publicBrief.sharedAt': 'Prepared for shared access · {date}',
  'publicBrief.limitedEvidence': 'Limited evidence — review before citing.',
  'publicBrief.notAvailableTitle': 'This shared brief is no longer available.',
  'publicBrief.notAvailableBody':
    'The analyst may have revoked access. Contact them for a fresh link.',
  'publicBrief.networkError': 'Unable to load this brief. Try again in a moment.',
  'publicBrief.loading': 'Loading brief…',
  'publicBrief.footerTagline': 'Built for principals who cannot afford a fabricated citation.',
  'publicBrief.executiveSummary': 'Executive summary',
  'publicBrief.analysis': 'Analysis',
  'publicBrief.sources': 'Sources',
  'publicBrief.sourcesCount': '{n} sources',
  // Admin settings hub — /admin/settings. Admin-only nav + page copy.
  'menu.admin-settings': 'Settings',
  'admin.header': 'Admin settings',
  'admin.subtitle':
    'Platform-wide configuration. Changes take effect immediately for all analysts.',
  'admin.signed-in-as': 'Signed in as',
  'admin.section.graph': 'Graph & grouping',
  'admin.section.import': 'Data import',
  'admin.section.system': 'System health',
  'admin.section.graph.desc': 'Control how the network overview groups entities into hub-islands.',
  'admin.section.import.desc': 'Ingest corpora into the knowledge graph.',
  'admin.section.system.desc': 'Neo4j connection health and live probes.',
  'admin.section.jobs': 'Background jobs',
  'admin.section.jobs.desc':
    'Cron schedules, last-run summary, and manual triggers for the streamliner, signal refresh, and audit redaction jobs.',
  'admin.jobs.scheduler-dormant':
    'Cloud Scheduler API is disabled on this project — cron entries are wired but not firing. Use "Run now" for manual triggers.',
  'admin.jobs.run-now': 'Run now',
  'admin.jobs.running': 'Running…',
  'admin.jobs.last-run': 'Last run',
  'admin.jobs.last-outcome': 'Outcome',
  'admin.jobs.last-detail': 'Detail',
  'admin.jobs.never-run': 'Never run',
  'admin.jobs.outcome.ok': 'OK',
  'admin.jobs.outcome.warn': 'Warnings',
  'admin.jobs.outcome.failed': 'Failed',
  'admin.jobs.outcome.unknown': 'Unknown',
  'admin.jobs.edit-note': 'Schedule edits require redeploy: bash scripts/deploy.sh',
  'admin.jobs.signalRefresh.title': 'Watchlist signal refresh',
  'admin.jobs.signalRefresh.desc':
    'Diffs every org’s watchlist against the graph and emits Signal entries for changes since the last run.',
  'admin.jobs.auditRedact.title': 'Audit PII redaction',
  'admin.jobs.auditRedact.desc':
    'Strips IP-hash, country, and user-agent fields off audit entries past their redactAfter horizon.',
  'admin.jobs.streamliner.title': 'Streamliner agent',
  'admin.jobs.streamliner.desc':
    'Graph hygiene pass: alias / low-confidence-fact / missing-edge / redundant-schema candidates per org.',
  'admin.graph.hub-count': 'Hubs on overview',
  'admin.graph.hub-count.help':
    'Top N most-connected entities to show as island anchors on the overview.',
  'admin.graph.depth': 'Neighborhood depth',
  'admin.graph.depth.help':
    'How many hops from each hub to include. Depth 2 is denser but can bridge into other hubs.',
  'admin.graph.depth.1': '1 hop (direct)',
  'admin.graph.depth.2': '2 hops (denser)',
  'admin.graph.selection': 'Hub selection',
  'admin.graph.selection.help':
    'Only degree centrality is active today. Other strategies are planned.',
  'admin.graph.selection.degree': 'Degree centrality',
  'admin.graph.selection.betweenness': 'Betweenness (coming soon)',
  'admin.graph.selection.curated': 'Curated list (coming soon)',
  'admin.graph.save': 'Save configuration',
  'admin.graph.saving': 'Saving…',
  'admin.graph.saved': 'Saved',
  'admin.graph.save-error': "Couldn't save configuration. Try again.",
  'admin.graph.last-updated': 'Last updated by {email} at {date}',
  'admin.graph.load-error': "Couldn't load current configuration.",
  'admin.import.format-title': 'Corpus format',
  'admin.import.upload-title': 'Upload corpus',
  'admin.import.upload-help':
    'Pick a .json file to preview. Backend ingest endpoint is not wired yet — for now run `pnpm ingest` locally against the file you selected.',
  'admin.import.selected': 'Selected: {name} ({size})',
  'admin.import.pick-file': 'Pick .json file',
  'admin.import.import-disabled': 'Import (coming soon)',
  'admin.import.sources-cta': 'Browse planned data sources',
  'admin.import.corpus-stats': 'Current corpus',
  'admin.import.stats.entities': 'Entities',
  'admin.import.stats.relationships': 'Relationships',
  'admin.import.stats.documents': 'Documents',
  'admin.import.stats.top-label': 'Top label',
  // Dedup: this file was imported before
  'admin.import.prior-runs.badge': 'SEEN',
  'admin.import.prior-runs.title': 'This file has been imported before',
  'admin.import.prior-runs.explain':
    'Re-running is safe — entities are upserted by name, not duplicated.',
  // Structured run-start errors
  'admin.import.error.already-running.title': 'Another ingest is already running',
  'admin.import.error.already-running.subtitle':
    'Started by {who} at {when}. Wait for it to finish, then try again.',
  'admin.import.error.payload-too-large.title': 'File too large',
  'admin.import.error.payload-too-large.subtitle':
    'The server rejected this payload. Max {maxMb} MB.',
  // Diff preview (validated state)
  'admin.import.diff.title': 'What this run will change',
  'admin.import.diff.loading': 'Comparing against the current graph…',
  'admin.import.diff.unavailable':
    'Couldn’t reach the graph to compute a diff — re-run will still upsert correctly.',
  'admin.import.diff.new-entities': 'New entities',
  'admin.import.diff.existing-entities': 'Existing (will upsert)',
  'admin.import.diff.new-edges': 'New edges',
  'admin.import.diff.existing-edges': 'Existing edges',
  'admin.import.diff.sample-new': 'Sample new entities',
  'admin.import.diff.sample-existing': 'Sample existing entities',
  // Cancel a running ingest
  'admin.import.run.cancel': 'Cancel run',
  'admin.import.run.confirm-cancel': 'Confirm cancel?',
  'admin.import.run.canceling': 'Canceling…',
  'admin.import.run.canceled': 'Ingest canceled',
  // Rollback (recent-runs row action)
  'admin.import.rollback.start': 'Roll back',
  'admin.import.rollback.confirm': 'Confirm rollback?',
  'admin.import.rollback.pending': 'Rolling back…',
  'admin.import.rollback.help': 'Deletes every node and edge stamped with this run’s id.',
  'admin.import.rollback.success': 'Removed {nodes} nodes and {edges} edges.',
  'admin.import.rollback.still-running': 'Cancel the run before rolling it back.',
  'admin.import.rollback.badge': 'ROLLED BACK',
  'admin.import.rollback.stamped':
    'Rolled back {when} by {who} — removed {nodes} nodes and {edges} edges.',
  // Drop zone + parsing
  'admin.import.dropzone.title': 'Drop a corpus JSON here',
  'admin.import.dropzone.hint':
    'or click to choose a file • max {maxMb} MB • up to {maxDocs} documents',
  'admin.import.dropzone.too-large': 'File exceeds {maxMb} MB limit',
  'admin.import.dropzone.bad-type': 'Only .json files are accepted',
  'admin.import.parsing': 'Parsing…',
  'admin.import.parse-error': "Couldn't parse this file",
  'admin.import.parse-error.json': 'Invalid JSON',
  'admin.import.parse-error.schema': 'Schema mismatch — expected the corpus shape shown below',
  'admin.import.parse-error.field': 'Field {path}: {message}',
  // Validation preview
  'admin.import.preview.title': 'Ready to ingest',
  'admin.import.preview.file': 'File',
  'admin.import.preview.corpus': 'Corpus',
  'admin.import.preview.source': 'Source document',
  'admin.import.preview.documents': 'Documents',
  'admin.import.preview.entities': 'Expected entities',
  'admin.import.preview.edges': 'Expected edges',
  'admin.import.preview.relationships': 'Expected relationships',
  'admin.import.preview.sample-entities': 'Sample entities',
  'admin.import.preview.sample-edges': 'Sample edges',
  'admin.import.preview.warnings': 'Warnings',
  'admin.import.preview.show-all-warnings': 'Show all {count} warnings',
  // Mode toggle + start
  'admin.import.mode.title': 'Mode',
  'admin.import.mode.merge': 'Merge — additive',
  'admin.import.mode.merge.help': 'Adds new entities and edges; existing data is preserved.',
  'admin.import.mode.replace': 'Replace — wipe + reload',
  'admin.import.mode.replace.help': 'Deletes the entire graph before ingesting. Cannot be undone.',
  'admin.import.start': 'Start ingest',
  'admin.import.confirm-replace': 'Confirm replace?',
  'admin.import.cancel': 'Cancel',
  // Run progress
  'admin.import.run.phase.constraints': 'Constraints',
  'admin.import.run.phase.clear': 'Clearing graph',
  'admin.import.run.phase.documents': 'Documents',
  'admin.import.run.phase.demo_seeds': 'Demo seeds',
  'admin.import.run.phase.finalize': 'Finalizing',
  'admin.import.run.docs-processed': 'Documents processed',
  'admin.import.run.nodes-written': 'Nodes written',
  'admin.import.run.edges-written': 'Edges written',
  'admin.import.run.errors': 'Errors',
  'admin.import.run.log-title': 'Live log',
  'admin.import.run.complete': 'Ingest completed',
  'admin.import.run.partial': 'Ingest finished with errors',
  'admin.import.run.failed': 'Ingest failed',
  'admin.import.run.duration': 'in {duration}',
  'admin.import.run.done': 'Done',
  'admin.import.run.import-another': 'Import another',
  // Recent runs table
  'admin.import.runs.title': 'Recent ingest runs',
  'admin.import.runs.empty': 'No ingest runs yet',
  'admin.import.runs.refresh': 'Refresh',
  'admin.import.runs.col.started': 'Started',
  'admin.import.runs.col.by': 'By',
  'admin.import.runs.col.mode': 'Mode',
  'admin.import.runs.col.status': 'Status',
  'admin.import.runs.col.docs': 'Documents',
  'admin.import.runs.col.nodes': 'Nodes',
  'admin.import.runs.col.edges': 'Edges',
  'admin.import.runs.col.errors': 'Errors',
  'admin.import.runs.col.duration': 'Duration',
  'admin.import.runs.expanded.error-log': 'Error log',
  'admin.import.runs.expanded.no-errors': 'No errors recorded',
  'admin.import.status.queued': 'Queued',
  'admin.import.status.running': 'Running',
  'admin.import.status.completed': 'Completed',
  'admin.import.status.partial': 'Partial',
  'admin.import.status.failed': 'Failed',
  'admin.import.status.canceled': 'Canceled',
  'admin.system.refresh': 'Refresh',
  'admin.system.refreshing': 'Checking…',
  'admin.system.status': 'Status',
  'admin.system.healthy': 'Healthy',
  'admin.system.failed': 'Failed',
  'admin.system.server': 'Server',
  'admin.system.uri': 'URI',
  'admin.system.latency': 'Latency',
  'admin.system.checked-at': 'Checked at',
  'admin.system.counts': 'Counts',
  'admin.system.top-labels': 'Top labels',
  'admin.system.top-rels': 'Top relationships',
  'admin.system.load-error': "Couldn't load system health.",
  // Organization / multi-tenant strings.
  'org.create': 'Create organization',
  'org.edit': 'Edit organization',
  'org.members': 'Members',
  'org.invites': 'Invites',
  'org.settings': 'Organization settings',
  'org.ingest.nav': 'Import data',
  'org.ingest.eyebrow': 'organization data',
  'org.ingest.title': 'Import data',
  'org.ingest.description':
    'Layer your own corpus on top of system data. Documents, edges, and resolution candidates are tagged with your organization id and visible only to your members alongside the shared system corpus. Active organization:',
  'org.ingest.pick-file': 'Corpus JSON file',
  'org.ingest.run': 'Run import',
  'org.ingest.running': 'Importing…',
  'org.ingest.detach': 'Detach (run continues server-side)',
  'org.ingest.detached.hint': 'Stopped listening. The server may still be running this import.',
  'org.ingest.progress': 'Progress',
  'org.ingest.docs': 'Documents',
  'org.ingest.nodes': 'Nodes',
  'org.ingest.edges': 'Edges',
  'org.ingest.errors': 'Errors',
  'org.ingest.complete': 'Import complete (status: {status}).',
  'org.ingest.failed': 'Import failed.',
  'org.ingest.stream-lost.title': 'Live stream disconnected',
  'org.ingest.stream-lost.body':
    'The server is still ingesting this run. Progress below keeps updating from the server-side run record; the final status will appear here when the run finishes.',
  'org.ingest.log.title': 'Live log',
  'org.ingest.detail.tab': 'Run detail',
  'org.ingest.detail.not-found':
    'Run not found (it may have been deleted, or belongs to a different organization).',
  'org.ingest.detail.status': 'Status',
  'org.ingest.detail.format': 'Format',
  'org.ingest.detail.mode': 'Mode',
  'org.ingest.detail.size': 'File size',
  'org.ingest.detail.started': 'Started',
  'org.ingest.detail.duration': 'Duration',
  'org.ingest.detail.actor': 'Started by',
  'org.ingest.detail.error-log': 'Error log',
  'org.ingest.detail.no-errors': 'No errors recorded for this run.',
  'org.ingest.detail.retry.title': 'Re-import the same data',
  'org.ingest.detail.retry.body':
    'Submits the same payload the server stored for this run, creating a new entry in the import history. Idempotent — already-ingested entities upsert in place via MERGE-by-name + deterministic doc IDs.',
  'org.ingest.detail.retry.cta': 'Re-import',
  'org.ingest.detail.retry.in-flight': 'Submitting…',
  'org.ingest.detail.retry.payload-gone.title': 'Original payload unavailable',
  'org.ingest.detail.retry.payload-gone.body':
    'This run pre-dates server-side payload storage, or the stored bytes have aged out of retention. Re-upload the file via the picker on the import page to start a fresh run with the same data.',
  'org.ingest.detail.retry.payload-gone.cta': '← Back to picker',
  'org.ingest.format-label': 'Source format',
  'org.ingest.format.canonical.label': 'Canonical corpus (JSON)',
  'org.ingest.format.canonical.hint':
    'Documents pre-extracted into the LeanOpx corpus shape (see example below).',
  'org.ingest.format.fec.label': 'FEC contributions (JSON)',
  'org.ingest.format.fec.hint':
    'Raw FEC contributions JSON ({ contributions: [...] }). Each row → donor → committee + employer edge.',
  'org.ingest.format.fec-identities.label': 'FEC identities (JSON, donor-indexed)',
  'org.ingest.format.fec-identities.hint':
    'FEC donor index: { "DONOR NAME": [{ contributions: [...] }, ...], ... }. Each nested contribution becomes one Document.',
  'org.ingest.format.irs990-officers.label': 'IRS Form 990 officers (CSV)',
  'org.ingest.format.irs990-officers.hint':
    'CSV with org_ein, org_name, name, title, compensation, hours, tax_year. Each row → officer OFFICER_OF organization.',
  'org.ingest.format.irs990-grants.label': 'IRS Form 990 grants (CSV)',
  'org.ingest.format.irs990-grants.hint':
    'CSV with grantor_name, recipient, amount, purpose, tax_year. Each row → grantor GRANTED_TO recipient.',
  'org.ingest.format.title': 'Format & limits',
  'org.ingest.format.canonical.shape':
    'Upload a JSON file matching the corpus shape below — entities and edges already extracted by your pipeline (no NER on our side). Extra props on edges (year, confidence, custom fields) pass through to the graph as relationship properties.',
  'org.ingest.format.fec.shape':
    'Raw FEC contributions JSON. Each item in `contributions[]` becomes one Document. Donor → committee edges are minted automatically; if `donor_employer` is present, an extra donor → employer edge is added. Address, occupation, amount, and date pass through as edge properties.',
  'org.ingest.format.fec-identities.shape':
    'FEC donor index — top-level keys are donor names; each value is an array of identity records (different employer/occupation/location for the same name), and each identity carries its own nested `contributions[]`. The adapter walks down to the per-contribution records and emits one Document per contribution. The same donor → committee + EMPLOYED_BY edges are minted as the flat `fec-contributions` format.',
  'org.ingest.format.irs990-officers.shape':
    'IRS Form 990 "officers + key employees" CSV. Each row becomes one Document and one Person OFFICER_OF Organization edge. Title, compensation, hours, and tax year pass through as edge properties.',
  'org.ingest.format.irs990-grants.shape':
    'IRS Form 990 "grants paid" CSV. Each row becomes one Document and one Organization GRANTED_TO Organization edge. Amount, purpose, and tax year pass through as edge properties.',
  'org.ingest.format.size': 'Max file size: {maxMb} MB. Max documents per run: {maxDocs}.',
  'org.ingest.format.shape-note':
    "Re-imports are idempotent — a row with the same content updates the existing document instead of duplicating. (System-corpus documents are read-only to org imports; only your own org's data can be overwritten.)",
  'org.ingest.format.cli':
    'For corpora larger than the upload limit, use the CLI from a workstation with Neo4j credentials:',
  'org.ingest.format.auto-detected': 'auto-detected',
  'org.ingest.format.auto-detected.tooltip':
    'Source format was auto-selected from the picked file. Override via the dropdown if it guessed wrong.',
  'org.ingest.submitted.title': 'Import submitted — you can navigate away',
  'org.ingest.submitted.toast-body':
    '{fileName} is processing server-side. Pick another file or open Run detail to watch progress.',
  'org.ingest.submitted.cta': 'Open run detail →',
  'org.ingest.format.example-annotated':
    "Example below is annotated for clarity (`// comments` and `...` placeholders) — copy your real data into a valid file, don't paste this snippet verbatim.",
  'org.ingest.history.title': 'Recent imports',
  'org.ingest.history.empty': 'No imports yet for this organization.',
  'org.ingest.history.docs': 'docs',
  'org.ingest.history.nodes': 'nodes',
  'org.ingest.history.edges': 'edges',
  'org.ingest.history.errors': 'errors',
  'org.ingest.history.status.running': 'running',
  'org.ingest.history.status.completed': 'completed',
  'org.ingest.history.status.completed_with_errors': 'completed w/ errors',
  'org.ingest.history.status.failed': 'failed',
  'org.ingest.history.status.canceled': 'canceled',
  'org.ingest.history.status.stale': 'stalled (timed out)',
  'org.ingest.error.too-large':
    'File exceeds the {max} MB upload limit. Use the CLI for larger corpora (see Format & limits above).',
  'org.switch': 'Switch organization',
  'org.noMemberships': 'You are not a member of any organization yet.',
  'org.inviteAccept': 'Accept invite',
  'org.inviteExpired': 'This invite has expired or been revoked.',
  'org.lastAdminWarning': 'You are the last admin. Promote another member before stepping down.',
  'org.emailPinned': 'This invite is pinned to a specific email address.',
  'org.generateInvite': 'Generate invite',
  'org.copyLink': 'Copy link',
  'org.revoke': 'Revoke',
  'org.grantSuperAdmin': 'Grant super admin',
  'org.revokeSuperAdmin': 'Revoke super admin',
  'org.name': 'Organization name',
  'org.slug': 'Slug',
  'org.slug.help': 'Lowercase letters, numbers, and hyphens. Used in URLs.',
  'org.save': 'Save',
  'org.saving': 'Saving…',
  'org.saved': 'Saved',
  'org.role.admin': 'Admin',
  'org.role.member': 'Member',
  'org.promote': 'Promote to admin',
  'org.demote': 'Demote to member',
  'org.removeMember': 'Remove',
  'org.removeConfirm': 'Remove this member from the organization?',
  'org.invites.empty': 'No pending invites.',
  'org.invites.email': 'Email (optional)',
  'org.invites.ttlDays': 'Expires in (days)',
  'org.invites.qr': 'Show QR code',
  'org.members.empty': 'No members yet.',
  'org.created': 'Created',
  'org.expires': 'Expires',
  'org.linkCopied': 'Link copied',
  // Platform superadmin.
  'superadmin.section': 'Platform admin',
  'superadmin.orgs': 'Organizations',
  'superadmin.users': 'Users',
  'superadmin.newOrg': 'New organization',
  'superadmin.orgs.empty': 'No organizations yet.',
  'superadmin.users.empty': 'No users yet.',
  'superadmin.firstAdminUid': 'First admin UID (optional — defaults to you)',
  'superadmin.createCta': 'Create organization',
  'superadmin.role.superAdmin': 'superAdmin',
  'superadmin.revokeSelf': 'revoke self',
  'org.col.user': 'User',
  'org.col.uid': 'UID',
  'org.col.email': 'Email',
  'org.col.role': 'Role',
  'org.col.member': 'Member',
  'org.col.platformRole': 'Platform role',
  'org.settings.emailDomains': 'Email domain allowlist',
  'org.settings.emailDomains.help': 'Comma-separated. Empty = any domain.',
  'org.settings.timezone': 'Timezone',
  'org.settings.timezone.currentSaved': '{tz} (current saved value)',
  'org.invite.status.active': 'Active',
  'org.invite.status.consumed': 'Accepted',
  'org.invite.status.revoked': 'Revoked',
  'org.invite.status.expired': 'Expired',
  'org.invite.status.pending': 'Pending',
  'org.member.status.active': 'Active',
  'org.invite.welcome': 'You have been invited to {name}',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  // Audit explorer (superAdmin).
  'superadmin.audit': 'Audit log',
  'superadmin.audit.title': 'Audit log',
  'superadmin.audit.subtitle':
    'Cross-tenant forensic record. Every mutation, every share, every membership change. Privacy-sensitive context (IP hash, country, device) is stored separately and auto-redacted after 90 days.',
  'superadmin.audit.privacy_note': 'Action + actor only — no IP/device shown',
  'superadmin.audit.filter.org': 'Organization',
  'superadmin.audit.filter.org.all': 'All organizations',
  'superadmin.audit.filter.org.platform': 'Platform-level only',
  'superadmin.audit.filter.category': 'Category',
  'superadmin.audit.filter.search': 'Search',
  'superadmin.audit.filter.search.placeholder': 'Filter by action, target, or email…',
  'superadmin.audit.empty': 'No audit entries match the current filters.',
  'superadmin.audit.error':
    'Could not load audit entries. Confirm you have superAdmin access and try again.',
  'superadmin.audit.col.at': 'When',
  'superadmin.audit.col.actor': 'Actor',
  'superadmin.audit.col.org': 'Org',
  'superadmin.audit.col.action': 'Action',
  'superadmin.audit.col.target': 'Target',
  'superadmin.audit.col.severity': 'Severity',
  'superadmin.audit.org.public': 'public',
  'superadmin.audit.row_count': 'Showing {count} entries',
  'superadmin.audit.detail.title': 'Event details',
  'superadmin.audit.detail.country': 'Country',
  'superadmin.audit.detail.device': 'Device',
  'superadmin.audit.detail.payload': 'Payload',
  // Onboarding.
  'onboarding.selectOrg.prompt': 'Pick an organization to continue.',
  'onboarding.selectOrg.continue': 'Continue',
  'onboarding.noOrg.title': 'No organization yet',
  'onboarding.noOrg.message':
    'Ask an admin to invite you. Once you accept, this page will hand off to your workspace.',
  // Self-serve org create + 14-day trial (feat/self-serve-org-create-trial).
  // Surfaced at /onboarding/create-org (the new default landing when a
  // signed-in user has zero memberships) and via the sidebar org-switcher's
  // "+ Create another organization" entry.
  'onboarding.createOrg.title': 'Start your free trial',
  'onboarding.createOrg.subtitle': 'Spin up a new workspace for your team. 14-day trial, no card.',
  'onboarding.createOrg.name.label': 'Organization name',
  'onboarding.createOrg.name.placeholder': 'Acme Inc',
  'onboarding.createOrg.slug.label': 'URL slug',
  'onboarding.createOrg.slug.hint': 'Used in URLs and webhook secrets. Lowercase, hyphens only.',
  'onboarding.createOrg.slug.checking': 'Checking…',
  'onboarding.createOrg.slug.available': '{slug} is available',
  'onboarding.createOrg.slug.taken': 'This slug is taken — try another.',
  'onboarding.createOrg.slug.invalid': 'Slug must be lowercase letters, numbers, and hyphens only.',
  'onboarding.createOrg.slug.reserved': 'This slug is reserved — try another.',
  'onboarding.createOrg.cta': 'Start 14-day trial',
  'onboarding.createOrg.cta.creating': 'Creating workspace…',
  'onboarding.createOrg.error.limit':
    "You've reached the 10-org limit. Contact support to add more.",
  'onboarding.createOrg.error.unknown':
    "Couldn't create the workspace. Try again, or reach out to support.",
  'onboarding.createOrg.error.slug-taken-on-submit':
    'Another workspace claimed that slug while you were filling the form. Try a different one.',
  'onboarding.createOrg.error.slug-reserved-on-submit':
    'That slug is reserved — pick a different one.',
  'onboarding.createOrg.invitedFallback':
    "Already a member of an org? Wait for your invite — it'll arrive at {email}.",
  'onboarding.createOrg.invitedFallback.noEmail':
    "Already a member of an org? Wait for your invite — it'll arrive at the email you signed up with.",
  'onboarding.createOrg.switcher.create': '+ Create another organization',
  // Playbook surface.
  'playbook.eyebrow': 'Surface · playbook',
  'playbook.title': 'Playbook',
  'playbook.sub':
    'How the company operates — and how each leadership area runs. Living docs the AI refines as the company evolves.',
  'playbook.company.title': 'Company playbook',
  'playbook.departments.title': 'Department playbooks',
  'playbook.departments.empty':
    'No department playbooks yet. Newly-created orgs get eight AI-suggested defaults seeded automatically (one company + seven departments).',
  'playbook.action.edit': 'Edit',
  'playbook.action.refine': 'Refine with AI',
  'playbook.action.delete': 'Delete',
  'playbook.action.new': 'New department playbook',
  'playbook.badge.ai': 'AI',
  'playbook.badge.ai-tooltip': 'AI-suggested template; edit any section to make it yours.',
  'playbook.detail.not-found':
    'No playbook for that department yet — admins can create one from the playbook landing page.',
  'playbook.empty.title': "You don't have a company playbook yet.",
  'playbook.empty.body':
    'Newly-created orgs receive eight AI-suggested defaults (one company playbook + seven department playbooks). Ask an admin to seed them, or create one from scratch.',
  'playbook.last-edited': 'Updated {date}',
  'playbook.last-edited-by-ai': 'AI · {date}',
  'playbook.section.body.empty': '(no body yet)',
  'playbook.edit.title': 'Edit playbook',
  'playbook.edit.summary-label': 'Summary',
  'playbook.edit.title-label': 'Title',
  'playbook.edit.sections-label': 'Sections',
  'playbook.edit.section.title-label': 'Section title',
  'playbook.edit.section.body-label': 'Section body (markdown)',
  'playbook.edit.section.type-label': 'Section type',
  'playbook.edit.add-section': 'Add section',
  'playbook.edit.remove-section': 'Remove section',
  'playbook.edit.save': 'Save changes',
  'playbook.edit.saving': 'Saving…',
  'playbook.edit.cancel': 'Cancel',
  'playbook.ai.title': 'Refine with AI',
  'playbook.ai.prompt-label': 'How should the AI revise this playbook?',
  'playbook.ai.prompt-placeholder': 'e.g. Tighten the engineering cycle-time section.',
  'playbook.ai.section-label': 'Scope',
  'playbook.ai.section.whole': 'Whole playbook',
  'playbook.ai.submit': 'Refine',
  'playbook.ai.submitting': 'Refining…',
  'playbook.ai.cancel': 'Cancel',
  'playbook.ai.note':
    'Phase 5 will land real synthesis. Today this is a deterministic stub so the wire is testable end-to-end.',
  'playbook.dept.engineering': 'Engineering',
  'playbook.dept.sales': 'Sales',
  'playbook.dept.operations': 'Operations',
  'playbook.dept.marketing': 'Marketing',
  'playbook.dept.customer-success': 'Customer Success',
  'playbook.dept.finance': 'Finance',
  'playbook.dept.product': 'Product',
  'playbook.error.load': "Couldn't load playbooks. Refresh to try again.",
  'playbook.error.save': "Couldn't save the playbook. Try again.",
  'playbook.error.delete': "Couldn't delete the playbook. Try again.",
  'playbook.error.ai':
    'The AI-edit endpoint failed. Try again — Phase 5 will replace this stub with real synthesis.',
  'playbook.confirm.delete': 'Delete this playbook? This cannot be undone.',
  // Create-playbook modal (issue #152).
  'playbook.create.title': 'New department playbook',
  'playbook.create.sub': 'A blank doc the agent + team can fill in. Pick a department to scope it.',
  'playbook.create.title-label': 'Title',
  'playbook.create.title-placeholder': 'e.g. Engineering — how we ship',
  'playbook.create.department-label': 'Department',
  'playbook.create.department-placeholder': 'Choose a department',
  'playbook.create.summary-label': 'Summary',
  'playbook.create.summary-placeholder':
    'A one-line description of what this playbook covers. Optional.',
  'playbook.create.submit': 'Create playbook',
  'playbook.create.submitting': 'Creating…',
  'playbook.create.cancel': 'Cancel',
  // Confirmation toast after a successful create (distinct from the
  // button label `playbook.create.submit`).
  'playbook.create.success': 'Playbook created.',
  // Surfaced inside the modal when the client-side Zod parse fails
  // (typically: department not picked or title empty). Specific Zod
  // issue messages are still surfaced when present; this is the
  // fallback when the issue list is empty.
  'playbook.create.invalid': "Couldn't validate the form. Pick a department and fill in the title.",
  // Surfaced inside the modal when the server returns a non-Error
  // rejection (rare; typical errors come back as Error instances and
  // their `.message` is surfaced directly).
  'playbook.create.unknown-error': 'Unknown error',
  'playbook.create.taken-tag': 'taken',
  'playbook.create.all-taken':
    'All {n} departments already have a playbook. Edit one from the landing page or delete one first.',
  'playbook.error.create': "Couldn't create the playbook. Try again.",
  'playbook.delete.confirm-title': 'Delete this playbook?',
  'playbook.delete.confirming': 'Deleting…',
  'playbook.delete.confirm-cancel': 'Cancel',
  'playbook.delete.confirm-submit': 'Delete',
  'playbook.delete.success': 'Playbook deleted.',
  // Onboarding wizard — captures the BusinessContext after org creation.
  'onboarding.wizard.eyebrow': 'Onboarding',
  'onboarding.wizard.next': 'Next',
  'onboarding.wizard.back': 'Back',
  'onboarding.wizard.skip': 'Skip for now',
  'onboarding.wizard.progress': 'Step {current} of {total}',
  'onboarding.wizard.welcome.title': 'Welcome to LeanOpx',
  'onboarding.wizard.welcome.body':
    "We'll learn your business in about 5 minutes. Industry, stage, top priorities, the metrics that matter — so the agent can tailor the briefing, the playbooks, and the signals it watches for you.",
  'onboarding.wizard.welcome.cta': 'Get started',
  'onboarding.wizard.welcome.card1.title': 'Capture',
  'onboarding.wizard.welcome.card1.body':
    'Industry, stage, region, top priorities. The agent uses this as its starting hypothesis.',
  'onboarding.wizard.welcome.card2.title': 'Tailor',
  'onboarding.wizard.welcome.card2.body':
    'The signal feed, the playbooks, and the nav reorganize around what your business actually is.',
  'onboarding.wizard.welcome.card3.title': 'Operate',
  'onboarding.wizard.welcome.card3.body':
    'Daily briefings, weekly L10 prep, quarterly Rocks — drafted in your shape, not generic.',
  'onboarding.wizard.business.eyebrow': 'About your business',
  'onboarding.wizard.business.title': 'What kind of company are we operating?',
  'onboarding.wizard.business.industry': 'Industry',
  'onboarding.wizard.business.industry.help':
    'The closest match — drives default scorecard rows, signal topics, and playbook tailoring.',
  'onboarding.wizard.business.industryTags': 'Industry tags',
  'onboarding.wizard.business.industryTags.help':
    'Comma-separated, e.g. "b2b-saas/devtools, infra". Refines the industry slug.',
  'onboarding.wizard.business.stage': 'Funding / maturity stage',
  'onboarding.wizard.business.size': 'Headcount',
  'onboarding.wizard.business.region': 'Where are you operating?',
  'onboarding.wizard.business.region.help':
    'Pick all that apply. Drives region-specific signal topics.',
  'onboarding.wizard.northstar.eyebrow': 'North star',
  'onboarding.wizard.northstar.title': 'What single metric tells you whether you are winning?',
  'onboarding.wizard.northstar.metric': 'North star metric',
  'onboarding.wizard.northstar.metric.help':
    "Free-text — e.g. 'Net new ARR', 'Weekly active orgs', 'Placements per month'.",
  'onboarding.wizard.northstar.priorities': 'Top priorities right now',
  'onboarding.wizard.northstar.priorities.help':
    'Three to five short statements. Personalization rules scan these for keywords like "fundraise" or "churn" to tailor the nav.',
  'onboarding.wizard.northstar.priorities.placeholder': 'e.g. Land 10 design partners',
  'onboarding.wizard.northstar.priorities.add': 'Add another priority',
  'onboarding.wizard.goals.eyebrow': 'Goals',
  'onboarding.wizard.goals.title': 'Plant flags. Where are we trying to land?',
  'onboarding.wizard.goals.body':
    'One quarterly, one annual, one three-year. Free-text — attach a metric and target if you think in numbers.',
  'onboarding.wizard.goals.quarterly': 'This quarter',
  'onboarding.wizard.goals.annual': 'This year',
  'onboarding.wizard.goals.three-year': 'Three years out',
  'onboarding.wizard.goals.placeholder': 'e.g. Hit $3M ARR',
  'onboarding.wizard.goals.metric.placeholder': 'Metric (optional) — e.g. ARR',
  'onboarding.wizard.goals.target.placeholder': 'Target (optional) — e.g. $3M',
  'onboarding.wizard.signals.eyebrow': 'Signals',
  'onboarding.wizard.signals.title': 'Tell the agent what to watch outside.',
  'onboarding.wizard.signals.body':
    "Pick three to seven topics. Defaults adapt to your industry; deselect anything that's not for you, add anything missing.",
  'onboarding.wizard.signals.subscribed': '{n} of {min}–{max} topics selected',
  'onboarding.wizard.confirm.eyebrow': 'Almost done',
  'onboarding.wizard.confirm.title': 'Looks good?',
  'onboarding.wizard.confirm.body':
    'Quick recap. Edit anything by going back. Submit and the agent starts watching.',
  'onboarding.wizard.confirm.submit': 'Looks good — start using LeanOpx',
  'onboarding.wizard.confirm.submitting': 'Saving…',
  'onboarding.wizard.confirm.error': "Couldn't save your business context. Try again in a moment.",
  'onboarding.wizard.confirm.incomplete':
    'Some required fields are missing — go back and fill in company name, what we do, industry, stage, size, and the north-star metric before submitting.',
  'onboarding.wizard.confirm.section.identity': 'You + your company',
  'onboarding.wizard.confirm.section.business': 'Business',
  'onboarding.wizard.confirm.section.northstar': 'North star + priorities',
  'onboarding.wizard.confirm.section.goals': 'Goals',
  'onboarding.wizard.confirm.section.signals': 'Signal topics',
  'onboarding.wizard.confirm.section.invites': 'Team invites',

  // Step 1 — Identity & you (new). URL-first ordering — the URL fields
  // kick off async enrichment and pre-fill the rest of the wizard.
  'onboarding.wizard.identity.eyebrow': 'You + your company',
  'onboarding.wizard.identity.title': 'Let the agent get to know you.',
  'onboarding.wizard.identity.sub':
    "Paste your company URL and LinkedIn and we'll pre-fill the rest. You only edit where we got it wrong.",
  'onboarding.wizard.identity.companyUrl': 'Company URL',
  'onboarding.wizard.identity.companyUrl.help':
    'Public homepage. We read it to suggest your industry, stage, headcount, north-star metric, and signal topics.',
  'onboarding.wizard.identity.linkedinUrl': 'Your LinkedIn',
  'onboarding.wizard.identity.linkedinUrl.help':
    'Used once to read your title and a one-line background. Never re-fetched.',
  'onboarding.wizard.identity.companyName': 'Company name',
  'onboarding.wizard.identity.companyName.help': 'How the agent should refer to you in writing.',
  'onboarding.wizard.identity.userRole': 'Your role',
  'onboarding.wizard.identity.userRole.help':
    'CEO, COO, Chief of Staff, Head of …  Tunes how the briefing prioritizes what you see first.',
  'onboarding.wizard.identity.companyDescription': 'What does your company do? (one line)',
  'onboarding.wizard.identity.companyDescription.help':
    'Plain, concrete. The agent uses this as the dossier description and weaves it into the welcome note.',
  'onboarding.wizard.identity.companyDescription.placeholder':
    'AI Chief of Staff for early-stage CEOs.',
  'onboarding.wizard.identity.customerDescription': "Who's your customer? (one line)",
  'onboarding.wizard.identity.customerDescription.help':
    'The audience archetype — not the value prop.',
  'onboarding.wizard.identity.customerDescription.placeholder':
    'Series A/B founder/CEOs at 5-150 person teams.',
  'onboarding.wizard.identity.userBackground': 'Your background (optional)',
  'onboarding.wizard.identity.userBackground.help':
    'A line or two on what you did before LeanOpx. Pulled in from LinkedIn when available.',
  'onboarding.wizard.identity.userBackground.placeholder':
    'Eng leader at Stripe → founder of LeanOpx.',
  'onboarding.wizard.identity.enrich.pending': 'Looking it up…',
  'onboarding.wizard.identity.enrich.merged': 'Auto-filled below',
  'onboarding.wizard.identity.enrich.failed': "Couldn't auto-detect",
  'onboarding.wizard.identity.autoDetected': 'Auto-detected',

  // Step 6 — Invite teammates (new).
  'onboarding.wizard.invites.eyebrow': 'Invite your team',
  'onboarding.wizard.invites.title': 'Bring the rest of the leadership team.',
  'onboarding.wizard.invites.sub':
    "We'll mint each teammate an invite. They sign in, hit MFA, and join your org. Skip if you'd rather come back to this.",
  'onboarding.wizard.invites.empty': 'No invites yet. Add a row to bring someone along.',
  'onboarding.wizard.invites.addRow': '+ Add a teammate',
  'onboarding.wizard.invites.removeRow': 'Remove',
  'onboarding.wizard.invites.help':
    'Admins can change settings and members; members see briefings + dossiers + ask.',
  'onboarding.wizard.invites.role.label': 'Role',
  'onboarding.wizard.invites.role.admin': 'Admin',
  'onboarding.wizard.invites.role.member': 'Member',
  'onboarding.wizard.invites.invalidEmail':
    'Not a valid email — clear it or fix it before continuing.',

  // "Other" write-in affordances on closed-set multi-choice questions.
  // Schema persists these to *Other companion fields next to the typed
  // enum so personalization rules keep pattern-matching on the closed
  // values while the freeform capture rides alongside.
  'onboarding.wizard.business.other': 'Other',
  'onboarding.wizard.business.industry.otherPlaceholder': 'e.g. pet-tech, vertical AI for legal',
  'onboarding.wizard.business.industry.otherLabel': 'Your industry',
  'onboarding.wizard.business.region.otherPlaceholder': 'e.g. apac-australia, latam',
  'onboarding.wizard.business.region.otherLabel': 'Your region(s)',
  'onboarding.wizard.signals.other.label': 'A topic we should add',
  'onboarding.wizard.signals.other.placeholder':
    'e.g. solid-state-battery-news, EU AI Act enforcement',
  'onboarding.wizard.signals.other.help':
    "Comma-separated. We won't subscribe automatically — but we'll review and promote popular write-ins into the catalog.",

  // Warming-up interstitial — post-Confirm transition that holds the
  // user for up to 15s while the seed pipeline runs.
  'onboarding.warmingUp.eyebrow': 'One sec',
  'onboarding.warmingUp.title': 'Warming up your home.',
  'onboarding.warmingUp.agent.label': 'The agent',
  'onboarding.warmingUp.checklist.label': 'What we’re doing',
  'onboarding.warmingUp.step.welcomeNote': 'Drafting your welcome note',
  'onboarding.warmingUp.step.companyPin': 'Pinning your company dossier',
  'onboarding.warmingUp.step.refreshSignals': 'Subscribing to signal topics',
  'onboarding.warmingUp.step.invites': 'Sending team invites',
  'onboarding.warmingUp.narration.0': "Reading what you told us and what's on your site.",
  'onboarding.warmingUp.narration.1':
    'Drafting a welcome note grounded in your north-star and current focus.',
  'onboarding.warmingUp.narration.2':
    'Pinning your company so the first dossier is ready when you land.',
  'onboarding.warmingUp.narration.3': 'Lining up the first signals for your watchlist.',
  'onboarding.warmingUp.narration.4': 'Almost there. Your home is waking up.',
  'onboarding.warmingUp.tip.0': 'Tip · bookmark any entity to spin up a living dossier.',
  'onboarding.warmingUp.tip.1': 'Tip · every AI claim cites the underlying source event.',
  'onboarding.warmingUp.tip.2':
    'Tip · Ask is for one-off questions; the briefing is what changed since yesterday.',
  'onboarding.warmingUp.tip.3':
    'Tip · You can edit your business context anytime under Settings → Organization.',

  // Agent-led wizard (v6 default at /onboarding/agent-led) — only the
  // strings introduced or load-bearing in PR #116 live here; the page
  // still has chip labels + AgentLine copy that predate i18n and follow
  // up alongside the broader agent-led i18n sweep.
  'onboarding.agentLed.governance.formationState.label': 'State of inc.',
  'onboarding.agentLed.governance.formationState.placeholder': 'DE',
  'onboarding.agentLed.governance.fiscalYearEnd.label': 'Fiscal year end',
  'onboarding.agentLed.governance.fiscalYearEnd.placeholder': 'Dec 31',
  'onboarding.agentLed.governance.boardCadence.label': 'Board cadence',
  'onboarding.agentLed.governance.boardCadence.placeholder': 'Quarterly',
  'onboarding.agentLed.governance.boardSize.label': 'Board size',
  'onboarding.agentLed.governance.boardSize.placeholder': '5 (2 founders, 2 investors, 1 indep)',
  'onboarding.agentLed.governance.empStates.label': 'States with employees (comma-separated)',
  'onboarding.agentLed.governance.empStates.placeholder': 'CA, NY, WA',
  'onboarding.agentLed.finish.cta': 'Finish setup →',
  'onboarding.agentLed.finish.saving': 'Saving…',
  // Step 2 URL-blur enrichment status + error toasts — the chat-style
  // wizard surfaces a tight loading → success/error footer band while
  // /api/onboarding/enrich-company runs. `scanning` is the spinner
  // line, `exploring` is the secondary "what's the agent reading"
  // hint, `inferred` is the success header (interpolates the URL),
  // `unavailable` fires when the endpoint returns 503 (Vertex
  // unreachable in this env), and `failed` covers every other error
  // (502 enrichment_failed, network, schema). Error variants leave
  // the inferred fields blank so the user fills them in manually —
  // never fall back to fake values.
  'onboarding.agentLed.scan.status.scanning': '● Reading homepage…',
  'onboarding.agentLed.scan.status.exploring': 'Looking at homepage → about → recent press →',
  'onboarding.agentLed.scan.status.inferred': '✓ Inferred from {url}',
  'onboarding.agentLed.scan.error.unavailable': "Couldn't auto-detect — fill in the rest below.",
  'onboarding.agentLed.scan.error.failed': 'Auto-detect ran into a hiccup. Fill in the rest below.',
  // Submit fallbacks — the agent-led variant intentionally doesn't ask
  // about companyDescription / northStarMetric (those land in Step 2's
  // enrichment). When neither the user nor enrichment filled them, we
  // stamp a short scoped placeholder so the strict-required schema
  // doesn't 400 on submit; the user refines in Settings later.
  'onboarding.agentLed.fallback.companyDescription': 'Set up in Settings → Business context.',
  'onboarding.agentLed.fallback.northStarMetric': 'Set up in Settings → Business context.',
  // Default companyName when the user leaves the company field blank —
  // BusinessContext.companyName is required, so we stamp a short
  // recognizable placeholder the next-edit UI can detect.
  'onboarding.agentLed.fallback.companyName': 'My company',
  // Industry write-in when the free-text industry didn't match any of
  // the closed enum entries — the backend `industry === 'other'` create
  // path requires a non-empty `industryOther`.
  'onboarding.agentLed.fallback.industryOther': 'Onboarding write-in (refine in Settings).',

  // Field labels (input chrome + right-pane FieldsPanel rows).
  'onboarding.agentLed.field.companyUrl.label': 'Company URL',
  'onboarding.agentLed.field.companyUrl.placeholder': 'foundrylabs.com',
  'onboarding.agentLed.field.companyName.label': 'Company name',
  'onboarding.agentLed.field.companyName.placeholder': 'Foundry Labs',
  'onboarding.agentLed.field.stage': 'Stage',
  'onboarding.agentLed.field.headcount': 'Headcount',
  'onboarding.agentLed.field.industry': 'Industry',
  'onboarding.agentLed.field.name': 'Name',
  'onboarding.agentLed.field.role': 'Role',
  'onboarding.agentLed.field.email': 'Email',
  'onboarding.agentLed.field.company': 'Company',
  'onboarding.agentLed.field.website': 'Website',
  'onboarding.agentLed.field.voice': 'Voice',
  'onboarding.agentLed.field.cadence': 'Cadence',
  'onboarding.agentLed.field.autonomy': 'Autonomy',
  'onboarding.agentLed.field.connectors': 'Connectors',
  'onboarding.agentLed.field.entity': 'Entity',
  'onboarding.agentLed.field.lastRound': 'Last round',
  'onboarding.agentLed.field.board': 'Board',
  'onboarding.agentLed.field.empStates': 'Emp. states',
  'onboarding.agentLed.field.entityType': 'Entity type',
  'onboarding.agentLed.field.lastFundraise': 'Last fundraise round',

  // Section chrome + nav buttons.
  'onboarding.agentLed.top.label': 'Setup · 2-minute interview',
  'onboarding.agentLed.top.step': 'Step',
  'onboarding.agentLed.top.saveExit': 'Save & exit',
  'onboarding.agentLed.nav.back': 'Back',
  'onboarding.agentLed.nav.continue': 'Continue →',
  'onboarding.agentLed.right.heading': 'What I know about you so far',
  'onboarding.agentLed.right.aria': 'What the agent has learned so far',
  'onboarding.agentLed.right.howWorks': 'How the agent works',
  'onboarding.agentLed.right.persists': 'Persists on Finish',
  'onboarding.agentLed.right.nothingPersists.prefix': 'Nothing persists until you confirm. The ',
  'onboarding.agentLed.right.nothingPersists.tag': 'INFERRED',
  'onboarding.agentLed.right.nothingPersists.suffix':
    ' tag marks anything the agent guessed — override anytime.',

  // Reusable primitive: per-row "inferred" pill rendered next to a
  // derived value inside `<EditableFieldRow>`. Lowercase visual matches
  // the existing right-rail row treatment (the SURFACE-specific
  // `onboarding.agentLed.right.nothingPersists.tag` uppercase variant is
  // used in the heading copy, not the per-row pill).
  'onboarding.editableFieldRow.inferredTag': 'inferred',

  // Step 1 — identity.
  'onboarding.agentLed.step1.email.prefix': 'Confirming your email:',
  'onboarding.agentLed.step1.email.unverified': 'unverified',
  'onboarding.agentLed.step1.email.suffix': '— change in Settings → Account.',
  'onboarding.agentLed.step1.role.ceo': 'CEO / Founder',
  'onboarding.agentLed.step1.role.coo': 'COO',
  'onboarding.agentLed.step1.role.cos': 'Chief of Staff',
  'onboarding.agentLed.step1.role.vpOps': 'VP Ops',
  'onboarding.agentLed.step1.role.other': 'Other exec',

  // Step 3 — voice.
  'onboarding.agentLed.step3.cadence.daily8': 'Daily 8am',
  'onboarding.agentLed.step3.cadence.weekly': 'Weekly',
  'onboarding.agentLed.step3.cadence.dailyMonday': 'Daily + Monday recap',
  'onboarding.agentLed.step3.cadence.onChange': 'Only when something changes',
  'onboarding.agentLed.step3.autonomy.draftOnly': 'Draft, never send',
  'onboarding.agentLed.step3.autonomy.autoSafe': 'Draft + auto-send safe categories',
  'onboarding.agentLed.step3.autonomy.full': 'Full autonomy where you trust',

  // Step 4 — connect.
  'onboarding.agentLed.step4.note':
    'OAuth happens in Settings → Connectors after you finish — this is just so I know what to ask you for.',

  // Step 5 — governance.
  'onboarding.agentLed.step5.entity.delawareCcorp': 'Delaware C-Corp',
  'onboarding.agentLed.step5.entity.delawareLlc': 'Delaware LLC',
  'onboarding.agentLed.step5.entity.caCcorp': 'CA C-Corp',
  'onboarding.agentLed.step5.entity.other': 'Other',
  'onboarding.agentLed.step5.round.preseed': 'Pre-seed',
  'onboarding.agentLed.step5.round.seed': 'Seed',
  'onboarding.agentLed.step5.round.a': 'Series A',
  'onboarding.agentLed.step5.round.b': 'Series B',
  'onboarding.agentLed.step5.round.cPlus': 'Series C+',
  'onboarding.agentLed.step5.round.bootstrapped': 'Bootstrapped',
  'onboarding.agentLed.step5.next.heading': 'What happens next',
  // Step-5 closing paragraph with the entity-type + last-round inserts.
  // Localizers translate the whole sentence as a unit (placeholder order
  // can shift between locales). The inserts are pre-substituted on the
  // call site via tFormat with the user's selections or the matching
  // fallback keys when the wizard fields are empty.
  'onboarding.agentLed.step5.next.body':
    "Once your governance timeline is wired, I'll pre-fill items coming up for a {entity} that raised {round}. Delaware franchise tax, Form D (if recent), board cadence, and state payroll registrations — all on autopilot until you tell me otherwise.",
  'onboarding.agentLed.step5.fallback.stage': 'company at your stage',
  'onboarding.agentLed.step5.fallback.round': '(round)',
  'onboarding.agentLed.step5.defaultOperatorName': 'Operator',

  // Agent narrative lines.
  'onboarding.agentLed.line.step2':
    "Now your company. Drop the URL and I'll read your homepage, About page, and any recent press to fill out the rest.",
  'onboarding.agentLed.line.step3':
    'How should I sound, and how often should I show up? You can change all of this later in Settings → Agent behavior.',
  'onboarding.agentLed.line.step4':
    'Plug in the systems you actually run on. I read every one of them every morning — and the more I see, the better the briefing.',
  'onboarding.agentLed.line.step5':
    "Last one. Governance — filings, board meetings, equity. I'll start tracking what's coming for your stage so nothing surprises you.",
  'onboarding.agentLed.bubble.eyebrow': 'Agent ·',

  // /admin/settings — multi-tab settings hub (epic #16).
  'adminSettings.eyebrow': 'Admin',
  'adminSettings.title': 'Settings',
  'adminSettings.sub':
    'Org-level preferences — agent behavior, cadence, security, billing. Changes log to the audit feed.',
  'adminSettings.loading': 'Loading settings…',
  'adminSettings.loadError': 'Could not load settings. orgAdmin role required for this page.',
  'adminSettings.tab.org': 'Organization',
  'adminSettings.tab.agent': 'Agent behavior',
  'adminSettings.tab.cadence': 'Cadence',
  'adminSettings.tab.security': 'Security',
  'adminSettings.tab.billing': 'Billing',
  'adminSettings.tab.display': 'Display',
  'adminSettings.tab.danger': 'Danger zone',

  'adminSettings.save': 'Save changes',
  'adminSettings.saving': 'Saving…',

  // Organization tab
  'adminSettings.org.title': 'Organization',
  'adminSettings.org.desc': 'Display name, invite-allowlist domains, timezone.',
  'adminSettings.org.row.name': 'Name',
  'adminSettings.org.row.slug': 'Slug',
  'adminSettings.org.row.id': 'Org id',
  'adminSettings.org.row.emailAllowlist': 'Email allowlist',
  'adminSettings.org.row.emailAllowlist.any': 'Any domain',
  'adminSettings.org.row.timezone': 'Timezone',

  // Danger
  'adminSettings.danger.title': 'Danger zone',
  'adminSettings.danger.desc':
    'Destructive actions. Deferred to a follow-up — see the open epic backlog.',
  'adminSettings.danger.delete.label': 'Delete org',
  'adminSettings.danger.delete.value': 'Coming soon — contact support to deprovision an org today.',

  // Agent tab
  'adminSettings.agent.title': 'Agent behavior',
  'adminSettings.agent.desc': 'How the agent writes and what it can act on without your sign-off.',
  'adminSettings.agent.toast.success': 'Agent behavior saved.',
  'adminSettings.agent.toast.error': 'Could not save agent behavior. Try again.',
  'adminSettings.agent.choice.voice': 'Voice',
  'adminSettings.agent.choice.length': 'Length',
  'adminSettings.agent.choice.stance': 'Stance',
  'adminSettings.agent.toggle.draftDocs': 'Draft docs',
  'adminSettings.agent.toggle.draftDocs.on': 'Agent drafts and queues for review',
  'adminSettings.agent.toggle.draftDocs.off': 'Off',
  'adminSettings.agent.toggle.scheduleMeetings': 'Schedule meetings',
  'adminSettings.agent.toggle.sendReplies': 'Send replies',
  'adminSettings.agent.toggle.sendReplies.on': 'Agent can send on your behalf',
  'adminSettings.agent.toggle.sendReplies.off': 'All replies require approval',
  'adminSettings.agent.toggle.pauseCampaigns': 'Pause campaigns',
  'adminSettings.agent.toggle.runWarehouseSql': 'Run warehouse SQL',
  'adminSettings.agent.toggle.requireSourceCitation': 'Require source citation',
  'adminSettings.agent.toggle.tagInterpretation': 'Tag interpretation',
  'adminSettings.agent.toggle.showConfidence': 'Show confidence',

  // Cadence tab
  'adminSettings.cadence.title': 'Cadence',
  'adminSettings.cadence.desc':
    'When the agent ships the daily briefing, when L10 fires, when quarterly Rocks auto-draft.',
  'adminSettings.cadence.toast.success': 'Cadence saved.',
  'adminSettings.cadence.toast.error': 'Could not save cadence. Try again.',
  'adminSettings.cadence.row.briefingTime': 'Briefing time',
  'adminSettings.cadence.choice.l10Day': 'L10 day',
  'adminSettings.cadence.row.l10Time': 'L10 time',
  'adminSettings.cadence.toggle.l10AutoDraft': 'L10 auto-draft',
  'adminSettings.cadence.choice.quarterStart': 'Quarter start',
  'adminSettings.cadence.row.quarterLead': 'Quarter draft lead time',
  'adminSettings.cadence.row.quarterLead.suffix': 'days before quarter end',

  // Security tab
  'adminSettings.security.title': 'Security & access',
  'adminSettings.security.desc':
    'SSO, 2FA, residency region, compliance posture. Prompt retention is locked at zero — flipping it requires a schema change.',
  'adminSettings.security.toast.success': 'Security saved.',
  'adminSettings.security.toast.error': 'Could not save security. Try again.',
  'adminSettings.security.row.sso': 'SSO',
  'adminSettings.security.row.sso.enforced': 'enforced',
  'adminSettings.security.row.sso.off': 'Off',
  'adminSettings.security.choice.region': 'Region',
  'adminSettings.security.row.twoFA': '2FA required for',
  'adminSettings.security.row.twoFA.optional': 'Optional',
  'adminSettings.security.row.session': 'Session timeout',
  'adminSettings.security.row.session.suffix': 'hours',
  'adminSettings.security.row.retention': 'Prompt retention',
  'adminSettings.security.row.retention.value': '0 days (locked — schema literal)',
  'adminSettings.security.row.compliance': 'Compliance',
  'adminSettings.security.row.compliance.none': 'None declared',

  // Billing tab
  'adminSettings.billing.title': 'Billing',
  'adminSettings.billing.desc':
    'Per-company pricing. The leadership team sees the same picture without throttling.',
  'adminSettings.billing.toast.success': 'Billing saved.',
  'adminSettings.billing.toast.error': 'Could not save billing. Try again.',
  'adminSettings.billing.choice.tier': 'Tier',
  'adminSettings.billing.row.status': 'Status',
  'adminSettings.billing.row.renews': 'Renews',
  'adminSettings.billing.row.cardOnFile': 'Card on file',
  'adminSettings.billing.row.managePortal': 'Manage billing',
  'adminSettings.billing.portal.open': 'Open Stripe portal →',
  'adminSettings.billing.portal.unavailable': 'Stripe portal session not provisioned',

  // Display tab
  'adminSettings.display.title': 'Display',
  'adminSettings.display.desc':
    "Personal preference — stored on this device only. Won't affect teammates.",
  'adminSettings.display.choice.theme': 'Theme',
  'adminSettings.display.choice.density': 'Density',
  'adminSettings.display.choice.typeScale': 'Type scale',
  'adminSettings.display.choice.darkPalette': 'Dark palette',
  'adminSettings.display.choice.lightPalette': 'Light palette',
  'adminSettings.display.agent.title': 'Agent behavior',
  'adminSettings.display.agent.desc':
    'How the AI agent talks back. Voice changes draft tone and how explanations get phrased; quiet limits the agent to escalations only.',
  'adminSettings.display.agent.choice.voice': 'Agent voice',
  'adminSettings.display.agent.choice.onboardingShape': 'Onboarding shape',
  'adminSettings.display.tour.title': 'Tour',
  'adminSettings.display.tour.desc':
    'Replay the first-run walkthrough or jump to the onboarding interview.',
  'adminSettings.display.tour.firstRun': 'First-run tour',
  'adminSettings.display.tour.firstRun.replay': 'Replay tour',
  'adminSettings.display.tour.firstRun.reset': 'Tour reset — refresh /today to replay.',
  'adminSettings.display.tour.firstRun.resetError': "Couldn't reset the tour: {err}",
  'adminSettings.display.tour.interview': 'Onboarding interview',
  'adminSettings.display.tour.interview.open': 'Open agent-led interview',

  // /sources — operator-facing source tile grid.
  'sources.eyebrow': 'Sources · {n} live',
  'sources.title': 'Five systems, one read.',
  'sources.sub':
    "Every source listed below streams into the agent's graph. Click any tile to manage its scope or disconnect.",
  'sources.connect': 'Connect a source',
  'sources.loading': 'Loading…',
  'sources.error.load': 'Failed to load source state.',
  'sources.tile.status.notConnected': 'not connected',
  'sources.tile.lastSync': 'last sync',

  // /dossiers — operator's live-folder grid.
  'dossiers.eyebrow': 'Dossiers · {n} watched',
  'dossiers.title': 'Every account, person, rock — read up to the minute.',
  'dossiers.sub':
    'Each dossier reads from your sources as new events land. Synthesis on the detail page; provenance underneath. Bookmark any entity to make it a living dossier.',
  'dossiers.watchlist': 'Watchlist',
  'dossiers.loading': 'Loading dossiers…',
  'dossiers.empty.eyebrow': 'Dossiers',
  'dossiers.empty.title': 'No dossiers yet',
  'dossiers.empty.body':
    'Bookmark a person, customer, topic, or product. The agent will start watching it and surface signals as they land — and a dossier card appears here.',
  'dossiers.filter.all': 'All',
  'dossiers.filter.company': 'Company',
  'dossiers.filter.noMatch': 'No dossiers match the current filter.',
  'dossiers.card.added': 'Added',
  'dossiers.card.pinned': 'pinned',
  'dossiers.card.pinned.org': 'Pinned for your org',
  'dossiers.card.pinned.user': 'Pinned',
  'dossiers.card.open': 'Open dossier',
  'dossiers.empty.preview.eyebrow': "What you'll have shortly",
  'dossiers.empty.preview.customer': 'customer dossiers',
  'dossiers.empty.preview.person': 'team-member dossiers',
  'dossiers.empty.preview.partner': 'partner dossiers',
  'dossiers.empty.preview.investor': 'investor dossiers',

  // Dossier detail head — crumb labels + per-kind chips.
  'dossier.crumb.root': 'DOSSIERS',
  'dossier.crumb.kind.person': '● person',
  'dossier.crumb.kind.topic': '● topic',

  // Person dossier — body copy + member-missing state.
  'dossierPerson.memberNotFound':
    'That member is no longer in this org. Notes you previously took against them remain in your history.',
  'dossierPerson.summary.withSubtitle':
    '{subtitle}. Personal notes are private to you; everything else fills in as the connector pipeline indexes activity.',
  'dossierPerson.summary.bare': 'Personal notes are private to you.',

  // Topic dossier — summary + signals empty state.
  'dossierTopic.summary.valid':
    'Living folder the agent maintains for this topic — signals + notes + connected people.',
  'dossierTopic.summary.unknown':
    'Unknown topic key. The catalog may have changed; you can still revisit any notes you took against it.',
  'dossierTopic.signals.loading': 'Loading…',
  'dossierTopic.signals.empty.prefix': 'No recent signals for this topic. Try a refresh on ',
  'dossierTopic.signals.empty.linkLabel': 'Outside view',
  'dossierTopic.signals.empty.suffix': '.',

  // Dossier sub-views — empty-state copy.
  'dossier.tab.synthesis': 'Synthesis',
  'dossier.tab.relationships': 'Relationships',
  'dossier.tab.timeline': 'Timeline',
  'dossier.tab.mentionedInBriefs': 'Mentioned in briefs',
  'dossier.tab.signals': 'Signals',
  'dossier.tab.sources': 'Sources',
  'dossier.tab.notes': 'Notes',
  'dossier.tab.overview': 'Overview',
  'dossier.tab.graph': 'Knowledge graph',
  'dossier.tab.people': 'People',
  'dossier.tab.files': 'Files',
  'dossier.overview.empty':
    'Overview populates once the cross-tool graph (epic #9) starts indexing {entity}. Until then this tab degrades to an empty state — never invents stats, activity, or linked-rock connections.',
  'dossier.overview.recentActivity': 'Recent activity',
  'dossier.overview.linkedRocks': 'Linked rocks',
  'dossier.graph.body':
    'Knowledge-graph slice scoped to {entity} — people, opportunities, contracts, and documents that mention this entity. Read from Neo4j; the agent uses the same graph to decide which signals matter.',
  'dossier.graph.comingSoon': 'Coming soon',
  'dossier.graph.comingSoonBody': 'Graph rendering lands in Phase 1 with a Sigma.js view scoped to',
  'dossier.signals.body':
    'Signals filtered to this entity — outside news, mentions, and graph-derived metric movements. Phase 1 wires this to the listSignals + listExternalSignals endpoints with the entity filter applied.',
  'dossier.timeline.empty':
    'Timeline populates once the connector pipeline ingests audit events for this entity. Until then this tab degrades to an empty state — never invents activity.',
  'dossier.people.empty':
    'People connected to {entity} populate once the CRM + Slack + email connectors index the relationship graph. Until then this tab degrades to an empty state — never invents contacts.',
  'dossier.people.intro':
    'People connected to {entity} — pulled from your CRM, Slack workspace, and email connectors. The agent updates this list automatically when champions move or new contacts enter the relationship.',
  'dossier.files.empty':
    'Files attached to notes on this entity will appear here. Wired through notes.attachments[] once the dossier-scoped notes query returns results.',
  'dossier.synthesis.empty':
    'Synthesis cards land here once the cross-tool graph indexes {entity} and the agent’s pass produces grounded claims with cite chains. Until then this tab degrades to an empty state — never invents content.',
  'dossier.synthesis.confidenceSuffix': '% conf.',
  'dossier.relationships.empty':
    'Relationships ({entity} ↔ everything else) populate once the Neo4j cross-tool graph (epic #9) is wired. Each row shows the kind (mentions / owns / reports to / champion), who, meta, and confidence — same shape as the designer mock.',
  'dossier.sources.empty':
    'The Sources tab will list every connector record (Slack message id, Salesforce row id, Stripe event id, etc.) the agent used to synthesize claims about {entity}. Wired by the connector pipeline + agent action log; no fabrication in the meantime.',
  'dossier.sources.col.id': 'record id',
  'dossier.sources.col.source': 'source',
  'dossier.sources.col.date': 'date',
  'dossier.sources.col.note': 'note',
  'dossier.head.meta.generated': 'generated {when}',
  'dossier.head.meta.relationships': '{n} relationships indexed',
  'dossier.head.meta.sourceDocs': '{n} source records',
  'dossier.head.meta.confidence': '{pct}% confidence',

  // Dossier chat — persistent-memory thread.
  'dossierChat.backLink': 'All dossiers',
  'dossierChat.eyebrow': '{kind} · persistent thread',
  'dossierChat.title': 'Chat about {entity}.',
  'dossierChat.sub.prefix':
    'Every question and answer is remembered. Ask in March, recall in May. Scoped to ',
  'dossierChat.placeholder': 'Ask anything about {entity}…',
  'dossierChat.role.agent': 'Agent',
  'dossierChat.role.operator': 'You',
  'dossierChat.send': 'Send',
  'dossierChat.memory.title': 'Thread memory',
  'dossierChat.memory.messages': 'messages',
  'dossierChat.memory.since': 'since',
  'dossierChat.memory.facts': 'facts',
  'dossierChat.memory.openMemory': 'Open memory',
  'dossierChat.facts.title': 'Known facts',

  // /admin/audit — provenance log.
  'adminAudit.eyebrow': 'Audit · everything that happened',
  'adminAudit.title': 'Provenance log.',
  'adminAudit.sub':
    'Every agent action, every human edit, every system event — timestamped and queryable. What was decided, who decided it, and what data was looked at.',
  'adminAudit.loadError': 'Could not load audit feed. orgAdmin role required to view this page.',
  'adminAudit.loading': 'Loading audit feed…',
  'adminAudit.empty.eyebrow': 'Audit',
  'adminAudit.empty.title': 'No audit events yet',
  'adminAudit.empty.body':
    'Mutating endpoints record events here as the org grows. Connect a source or invite a teammate to seed activity.',
  'adminAudit.filter.all': 'All',
  'adminAudit.filter.org': 'Org',
  'adminAudit.filter.data': 'Data',
  'adminAudit.filter.view': 'View',
  'adminAudit.filter.security': 'Security',
  'adminAudit.filter.auth': 'Auth',

  // /metric-drill — measurable drill-through.
  'metricDrill.eyebrow': 'Measurable',
  'metricDrill.error.title': "Couldn't load metric",
  'metricDrill.error.body.title': 'Something went wrong',
  'metricDrill.error.body.body':
    "We couldn't fetch this measurable's history. Refresh the page or pick another row from the scorecard.",
  'metricDrill.loading': 'Loading…',
  'metricDrill.drill.heading': 'Drill-through · the rows inside this number',
  'metricDrill.compare.heading': 'Compare',
  'metricDrill.trend.empty.title': 'No history yet',
  'metricDrill.trend.empty.body':
    "This measurable doesn't have any recorded values. Once a weekly number lands, the trend will start drawing here.",
  'metricDrill.trend.empty.eyebrow': 'Trend · still indexing',
  'metricDrill.trend.aria': 'Trend over the last {n} weeks',
  'metricDrill.drill.empty.title': 'Nothing to drill into yet',
  'metricDrill.drill.empty.body':
    'Once a connector pipeline is wired for this measurable, the underlying rows will appear here.',
  'metricDrill.drill.empty.eyebrow': 'Drill-through · provisional',
  'metricDrill.drill.table.row': 'Row',
  'metricDrill.drill.table.value': 'Value',
  'metricDrill.drill.table.meta': 'Meta',

  // Governance pages — error / not-found chrome.
  'governance.eyebrow': 'Governance',
  'governance.loading': 'Loading…',
  'governance.item.notFound.title': 'Item not found',
  'governance.item.notFound.body':
    "The agent doesn't know about this governance item — check the URL.",
  'governance.boardPackage.notFound.title': 'Board package not found',
  'governance.boardPackage.notFound.body':
    'No package found for {quarterKey}. The agent drafts a package each quarter once the meeting date is set.',
  'governance.calendar.eyebrow': 'Governance › Year calendar · {windowLabel}',
  'governance.calendar.title': 'The whole governance year — laid out so nothing surprises you.',
  'governance.calendar.sub':
    'Twelve months. Each tone maps to a governance category. Click a day to jump to its first item.',
  'governance.calendar.back': 'Back to timeline',
  'governance.calendar.sampleData': 'Sample data',
  'governance.help.back': 'Back to governance',
  'governance.help.eyebrow': 'Marketplace · stage-matched & vetted',
  'governance.help.title': 'The people you need next, before you knew you needed them.',
  'governance.help.sub':
    'The agent matches counsel, CPAs, auditors, brokers, and admin platforms to your stage and state — and watches the work they do for you.',
  'governance.list.title': 'Everything you have to do, file, pay, or hire — in one timeline.',
  'governance.list.sub':
    "Built for operators who haven't been at this stage before. The agent reads your incorporation docs, term sheets, hires, and state expansions — and surfaces what's coming, what it costs, who owns it, and how it gets done.",
  'governance.list.sampleData': 'Sample data',
  // Eyebrow renders demo-org context — stage + entity + headcount + states.
  // `tFormat` so the inserts localize cleanly when we add a non-English locale.
  'governance.list.eyebrow': 'Your stage · {stage} · {entity} · {fte} FTE · {states}',
  'governance.list.loadError': "Couldn't load governance — please retry.",

  // /governance — category filter chip labels.
  'governance.list.category.all': 'All',
  'governance.list.category.filing': 'Filing',
  'governance.list.category.equity': 'Equity',
  'governance.list.category.board': 'Board',
  'governance.list.category.hr': 'HR',
  'governance.list.category.compliance': 'Compliance',
  'governance.list.category.insurance': 'Insurance',

  // /governance — bucket section headers.
  'governance.list.bucket.overdue': '● Overdue · act now',
  'governance.list.bucket.thisMonth': 'This month',
  'governance.list.bucket.next90': 'Next 90 days',
  'governance.list.bucket.later': 'Later this year',
  'governance.list.bucket.done': 'Recently done',

  // /governance — status pill labels.
  'governance.list.status.overdue': '● overdue',
  'governance.list.status.due': '◆ due soon',
  'governance.list.status.upcoming': '○ upcoming',
  'governance.list.status.inProgress': '○ in progress',
  'governance.list.status.agentReady': '◆ agent ready',
  'governance.list.status.done': '✓ done',

  // /governance — relative time copy used in event cards.
  'governance.list.relative.today': 'today',
  'governance.list.relative.tomorrow': 'tomorrow',
  'governance.list.relative.yesterday': 'yesterday',
  'governance.list.relative.inDays': 'in {n} days',
  'governance.list.relative.daysAgo': '{n} days ago',
  'governance.list.relative.inMonths': 'in {n} months',
  'governance.list.relative.monthsAgo': '{n} months ago',

  // /governance — StatStrip + filter row + event card chrome.
  'governance.list.stat.today': 'Today',
  'governance.list.stat.overdue': 'Overdue',
  'governance.list.stat.dueWeek': 'Due this week',
  'governance.list.stat.agentPrepped': 'Agent-prepped',
  'governance.list.stat.cost90d': 'Est. cost · 90d',
  'governance.list.filter.label': 'Filter',
  'governance.list.filter.viewCalendar': 'View calendar →',
  'governance.list.card.whyMatters': 'Why this matters: ',
  'governance.list.card.agentSurfaced':
    'Agent surfaced this item — read the detail page for context.',
  'governance.list.card.owner': 'Owner',
  'governance.list.card.cost': 'Cost',
  'governance.list.card.method': 'Method',
  'governance.list.card.method.tbd': 'Method TBD',
  'governance.list.card.agentPrepped': 'Agent prepped',
  'governance.list.card.openTask': 'Open task →',
  'governance.list.card.snooze': 'Snooze',

  // /governance — Series-C stage banner.
  'governance.list.stage.eyebrow': "You're approaching Series C territory",
  'governance.list.stage.title': 'Three things change once you cross $30M ARR or close Series C.',
  'governance.list.stage.body':
    'Audited financials replace reviewed. Founders need 10b5-1 plans for any secondary. EEO-1 is mandatory at 100 FTE. Your timeline now shows the work — none of this was hidden from you. The agent has been preparing for months.',
  'governance.list.stage.cta': 'See the Series-C playbook →',

  // /governance — right rail.
  'governance.list.rail.yearAhead': 'Year ahead',
  'governance.list.rail.eventsTracked': 'events tracked',
  'governance.list.rail.overdue': 'overdue',
  'governance.list.rail.dueWeek': 'due this week',
  'governance.list.rail.agentPrepped': 'agent-prepped',
  'governance.list.rail.cost90d': 'est. cost · 90d',
  'governance.list.rail.agentWatching': 'Agent watching',
  'governance.list.rail.agentWatching.body':
    'I read your bylaws, term sheet, and Carta. New hires, fundraises, and state expansions auto-trigger items here. Nothing surprises us.',

  // /governance/$itemId — detail page chrome.
  'governance.item.back': 'Back to timeline',
  'governance.item.eyebrow': 'Filing · State of Delaware · DGCL §503',
  'governance.item.filing.title': "Delaware franchise tax — I'd file Assumed Par Value.",
  'governance.item.filing.sub':
    'Every DE C-Corp owes franchise tax annually. There are two ways to compute it. The state lets you pick the cheaper one. I ran both.',
  'governance.item.sampleData': 'Sample data',
  'governance.item.rail.thisTask': 'This task',
  'governance.item.rail.due': 'due',
  'governance.item.rail.estCost': 'est. cost',
  'governance.item.rail.ifYouMiss': 'if you miss',
  'governance.item.rail.ifYouMiss.value': '$200 + 1.5%/mo',
  'governance.item.rail.owner': 'owner',
  'governance.item.rail.role': 'role',
  'governance.item.rail.dueRelToday': 'today',
  'governance.item.rail.dueRelFuture': 'in {n}d',
  'governance.item.rail.dueRelPast': '{n}d ago',
  'governance.item.agentDid.title': 'What the agent did',
  'governance.item.agentDid.needsSignoff': 'Needs your sign-off',
  'governance.item.whyMatters': 'Why this matters',
  'governance.item.whyMatters.body.lead': 'Default method (Authorized Shares) would bill ',
  'governance.item.whyMatters.body.tail': '. APV bills {apv}. Same legal outcome.',
  'governance.item.source.title': 'Where this came from',
  'governance.item.picker.recommended': 'Recommended',
  'governance.item.picker.apv.label': 'Assumed Par Value Capital',
  'governance.item.picker.apv.body':
    'Calc: (Total Assets / Issued Shares) × Auth Shares × $0.0004 — capped at $200k. With your post-B balance sheet, the formula yields the minimum tax bracket.',
  'governance.item.picker.default': "Default · don't pick this",
  'governance.item.picker.auth.label': 'Authorized Shares',
  'governance.item.picker.auth.body':
    'Calc: 35M authorized shares × $0.00075/share + $400 base. DE bills this by default unless you explicitly elect APV. Most first-time founders pay this.',
  'governance.item.draft.title': 'Draft filing · ready for your signature',
  'governance.item.draft.row.filer': 'Filer',
  'governance.item.draft.row.filer.value': 'LeanOpx, Inc.',
  'governance.item.draft.row.fileNumber': 'File number',
  'governance.item.draft.row.taxYear': 'Tax year',
  'governance.item.draft.row.methodElected': 'Method elected',
  'governance.item.draft.row.totalAssets': 'Total assets',
  'governance.item.draft.row.issuedShares': 'Issued shares',
  'governance.item.draft.row.authorizedShares': 'Authorized shares',
  'governance.item.draft.row.computedTax': 'Computed tax',
  'governance.item.draft.row.payment': 'Payment',
  'governance.item.draft.row.signatory': 'Signatory',
  'governance.item.draft.sign': 'Sign & file →',
  'governance.item.draft.preview': 'Preview PDF',
  'governance.item.draft.reroute': 'Reroute to my counsel',
  'governance.item.draft.filedVia': 'filed via {connector} · 4-6 business hours',
  'governance.item.draft.connectorFallback': 'connector',
  'governance.item.nonFiling.title': 'What the agent has on this',
  'governance.item.nonFiling.body':
    'No additional context yet. The agent will surface relevant connector data here as it lands.',
  'governance.item.nonFiling.detailHint.lead':
    'Detail fields render once the relevant connector is wired. Connect from ',
  'governance.item.nonFiling.detailHint.sources': 'Sources',

  // /governance/calendar — month names + page chrome.
  'governance.calendar.month.january': 'January',
  'governance.calendar.month.february': 'February',
  'governance.calendar.month.march': 'March',
  'governance.calendar.month.april': 'April',
  'governance.calendar.month.may': 'May',
  'governance.calendar.month.june': 'June',
  'governance.calendar.month.july': 'July',
  'governance.calendar.month.august': 'August',
  'governance.calendar.month.september': 'September',
  'governance.calendar.month.october': 'October',
  'governance.calendar.month.november': 'November',
  'governance.calendar.month.december': 'December',
  'governance.calendar.month.short.jan': 'Jan',
  'governance.calendar.month.short.feb': 'Feb',
  'governance.calendar.month.short.mar': 'Mar',
  'governance.calendar.month.short.apr': 'Apr',
  'governance.calendar.month.short.may': 'May',
  'governance.calendar.month.short.jun': 'Jun',
  'governance.calendar.month.short.jul': 'Jul',
  'governance.calendar.month.short.aug': 'Aug',
  'governance.calendar.month.short.sep': 'Sep',
  'governance.calendar.month.short.oct': 'Oct',
  'governance.calendar.month.short.nov': 'Nov',
  'governance.calendar.month.short.dec': 'Dec',
  'governance.calendar.legend.filing': 'Filing',
  'governance.calendar.legend.equity': 'Equity / admin',
  'governance.calendar.legend.board': 'Board',
  'governance.calendar.legend.hr': 'HR / hires',
  'governance.calendar.legend.compliance': 'Compliance / insurance',
  'governance.calendar.rail.yearTotals': 'Year totals',
  'governance.calendar.rail.events': 'events',
  'governance.calendar.rail.filings': 'filings',
  'governance.calendar.rail.boardCadences': 'board cadences',
  'governance.calendar.rail.taxMilestones': 'tax milestones',
  'governance.calendar.rail.estSpend': 'est. spend',
  'governance.calendar.rail.estSpend.value': '${k}k',
  'governance.calendar.rail.legend': 'Legend',
  'governance.calendar.rail.export': 'Export',
  'governance.calendar.rail.export.body':
    "Push to Google Calendar, Outlook, or your team's Notion. Each event includes the agent's prep notes and source links.",
  'governance.calendar.rail.export.target.google': 'Google',
  'governance.calendar.rail.export.target.outlook': 'Outlook',
  'governance.calendar.rail.export.target.notion': 'Notion',
  'governance.calendar.rail.export.target.ics': 'ICS',
  'governance.calendar.more': '+{n} more',

  // /governance/help — chrome (provider list data stays inline as seed).
  'governance.help.rail.yourStack': 'Your stack',
  'governance.help.rail.lawFirms': 'law firms',
  'governance.help.rail.lawFirms.value': '1 active · 1 backup',
  'governance.help.rail.taxCpa': 'tax / cpa',
  'governance.help.rail.taxCpa.value': '2 quotes',
  'governance.help.rail.audit': 'audit',
  'governance.help.rail.audit.value': 'RFP open',
  'governance.help.rail.insurance': 'insurance',
  'governance.help.rail.insurance.value': 'Woodruff Sawyer',
  'governance.help.rail.hrPayroll': 'HR / payroll',
  'governance.help.rail.hrPayroll.value': 'Justify',
  'governance.help.rail.totalAnnual': 'total annual',
  'governance.help.rail.totalAnnual.value': '$284k',
  'governance.help.rail.whyPicks': 'Why these picks',
  'governance.help.rail.whyPicks.body':
    'Every recommendation is filtered by your stage, state, and 14 anonymized LeanOpx-comparable companies. No referral fees.',
  'governance.help.tone.current': '✓ in your stack',
  'governance.help.tone.rec': '◆ recommended',
  'governance.help.tone.quoted': '○ quote pending',
  'governance.help.cta.current': 'Open thread',
  'governance.help.cta.rec': 'Request intro',
  'governance.help.cta.quoted': 'Review quote',
  'governance.help.card.bestAt': 'Best at',
  'governance.help.card.rate': 'Rate',
  'governance.help.card.compare': 'Compare',
  'governance.help.ask.eyebrow': "Don't see what you need?",
  'governance.help.ask.body':
    'Describe the help in one line — IP counsel, exec comp specialist, international entity, whatever — and the agent will surface 3 vetted matches with comparable references.',
  'governance.help.ask.cta': 'Ask for a match →',

  // /governance/board/$quarterKey — board package builder chrome.
  'governance.boardPackage.eyebrow': 'Quarterly · per bylaws §4.2',
  'governance.boardPackage.title': 'Q2 board package — I drafted everything you usually write.',
  'governance.boardPackage.sub':
    'Edit the cover memo + asks before sending. Live sections (scorecard, rocks, cash) refresh on every render.',
  'governance.boardPackage.meetingPrefix': 'Meeting:',
  'governance.boardPackage.meetingTimeSuffix': '· 10am PT',
  'governance.boardPackage.stat.pages': 'Pack pages',
  'governance.boardPackage.stat.sources': 'Sources cited',
  'governance.boardPackage.stat.open': 'Open items',
  'governance.boardPackage.stat.drafted': 'Drafted by agent',
  'governance.boardPackage.stat.daysToShip': 'Days to ship',
  'governance.boardPackage.sectionsHeading': 'Sections · drag to reorder',
  'governance.boardPackage.ready.eyebrow': 'Ready to send',
  'governance.boardPackage.ready.body':
    "Pack assembles to a single PDF + Box folder. I'll email the link to all 5 directors with iCal hold.",
  'governance.boardPackage.ready.preview': 'Preview',
  'governance.boardPackage.ready.send': 'Send to board →',
  'governance.boardPackage.section.status.live': '● live',
  'governance.boardPackage.section.status.sign': '◆ sign',
  'governance.boardPackage.section.status.draft': '○ draft',
  'governance.boardPackage.section.status.ready': '✓ ready',
  'governance.boardPackage.source.draftAgent': 'Agent drafted',
  'governance.boardPackage.source.draftLegal': 'Legal drafted',
  'governance.boardPackage.source.draftMarcos': 'Added per Marcos',
  'governance.boardPackage.source.draftSusan': 'Added per Susan',
  'governance.boardPackage.source.liveScorecard': 'Live from /scorecard',
  'governance.boardPackage.source.liveRocks': 'Auto from /rocks',
  'governance.boardPackage.source.liveStripeBank': 'From Stripe + bank',
  'governance.boardPackage.rail.meeting': 'Meeting',
  'governance.boardPackage.rail.date': 'date',
  'governance.boardPackage.rail.format': 'format',
  'governance.boardPackage.rail.format.value': 'Hybrid · SF HQ',
  'governance.boardPackage.rail.packDue': 'pack due',
  'governance.boardPackage.rail.attendees': 'attendees',
  'governance.boardPackage.rail.attendees.value': '5 of 5',
  'governance.boardPackage.rail.packStatus': 'Pack status',
  'governance.boardPackage.rail.preReadFlags': 'Pre-read flags',
  'governance.boardPackage.rail.preReadFlags.body':
    'Susan asked last Q about gross margin trajectory. I added a 6-quarter trend slide. Marcos flagged ARR composition — added cohort view.',
  'governance.boardPackage.back': 'Back to governance',

  // /insights/$insightId — drill-through (designer demo).
  'insightDossier.back': 'All insights',
  'insightDossier.eyebrow': 'Insight · drill-through',
  'insightDossier.demo.title': 'Engineering velocity dropped 12% over the last 14 days',
  'insightDossier.demo.sub':
    'Median PR review-wait time rose from 1.4d to 4.7d. Open PRs by reviewer concentrate on Marcus (+8). Likely root cause: pair-review rotation broke after two departures.',
  'insightDossier.action.watch': 'Watch',
  'insightDossier.cites.heading': 'Supporting cites',
  'insightDossier.actions.heading': 'Suggested actions',
  'insightDossier.actions.item.moveCandidates':
    'Move 2 candidates to final round this week (R-206)',
  'insightDossier.actions.item.pairReview': 'Pair-review rotation · spread review load',
  'insightDossier.actions.item.addVelocity': 'Add velocity to next L10 issues list',
  'insightDossier.aside.linkedHeading': 'Linked dossiers',
  'insightDossier.aside.linked.marcus': '· Marcus A.',
  'insightDossier.aside.linked.priya': '· Priya A.',
  'insightDossier.aside.linked.hiring': '· Hiring (R-206)',
  'insightDossier.aside.surfacedHeading': '◆ Agent first surfaced',
  'insightDossier.aside.surfacedDate': 'May 9, 2026',
  'insightDossier.aside.surfacedBody':
    'Confidence trended high once cycle-time crossed 2σ on May 7.',
  'insightDossier.cites.github.detail':
    'PRs opened in the last 14d · cycle-time mean 4.7d (was 3.2d)',
  'insightDossier.cites.bamboohr.detail': 'Eng departures since Mar · pair-review rotation broken',
  'insightDossier.cites.linear.detail': 'Open issues > 7d in code-review state',
  'insightDossier.cites.slack.detail': '#eng-ops mentions of "review backlog"',

  // /superadmin/audit — cross-tenant audit (platform-wide).
  'superadminAudit.backAria': 'Back to superadmin',
  'superadminAudit.title': 'Cross-tenant audit',
  'superadminAudit.filter.allOrgs': 'All orgs',
  'superadminAudit.filter.platform': 'Platform',
  'superadminAudit.filter.placeholder': 'Filter by action, target, actor…',
  'superadminAudit.loading': 'Loading…',
  'superadminAudit.empty': 'No audit entries.',
  'superadminAudit.col.when': 'When',
  'superadminAudit.col.org': 'Org',
  'superadminAudit.col.actor': 'Actor',
  'superadminAudit.col.action': 'Action',
  'superadminAudit.col.target': 'Target',
  'superadminAudit.col.severity': 'Severity',

  // Admin · Integrations — placeholder tooltips on disabled controls
  // (the real handler "lands with the adapter" — issue #114).
  'adminIntegrations.tooltip.gws.persistence':
    'Persistence lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.forceResync':
    'Force resync lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.pdfExport': 'PDF export lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.revoke': 'Revoke lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.csvExport': 'CSV export lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.lands': 'Lands with the GWS adapter (issue #114)',
  'adminIntegrations.tooltip.gws.continue':
    'Continue lands with the Google Workspace adapter (issue #114)',
  'adminIntegrations.tooltip.installFlow': 'Install flow lands with the connector adapter',

  // ── Shared onboarding fallback strings.
  //    Used across welcome, walkthrough, first-briefing-ready, and the
  //    welcome-email artifact when the user's displayName is unset.
  'onboarding.fallback.firstname': 'there',

  // ── Welcome (`/onboarding/welcome`). Post-email-verification landing
  //    that sits between the verification redirect and the user's pick
  //    of agent-led vs form-first wizard. Mirrors the designer's
  //    `WelcomePostSignup` mock (onboarding-extras-1.jsx).
  'onboarding.welcome.brand': 'LeanOpx',
  'onboarding.welcome.verified-pill': '● {email} · verified',
  'onboarding.welcome.sign-out': 'Sign out',
  'onboarding.welcome.eyebrow': '✓ Account verified · welcome aboard',
  'onboarding.welcome.greeting': 'Hi {firstName}.',
  'onboarding.welcome.hero-line-2': "Let's spend ",
  'onboarding.welcome.hero-accent': 'four minutes',
  'onboarding.welcome.hero-line-2-suffix': ' together.',
  'onboarding.welcome.body':
    "I'm the LeanOpx agent. I'll learn your company, your voice, and where your data lives — then start reading. Your first briefing lands Monday at 8am.",
  'onboarding.welcome.primary-cta': "Let's go →",
  'onboarding.welcome.secondary-cta': "I'll set it up myself",
  'onboarding.welcome.step-01-label': 'Identity',
  'onboarding.welcome.step-02-label': 'Company',
  'onboarding.welcome.step-03-label': 'Voice',
  'onboarding.welcome.step-04-label': 'Connect',
  'onboarding.welcome.step-05-label': 'Read',

  'onboarding.agentLed.line.step1':
    "Hey {firstName} — I'm the LeanOpx agent. Two minutes of setup and I'll start the Monday briefing tomorrow. Drop your LinkedIn and I'll fill in the rest.",

  // Step 1 — LinkedIn enrichment chrome (input label, help, scanning,
  // and the per-row labels rendered inside the inferred-fields card).
  'onboarding.agentLed.step1.linkedin.label': 'Your LinkedIn',
  'onboarding.agentLed.step1.linkedin.placeholder': 'linkedin.com/in/yourhandle',
  'onboarding.agentLed.step1.linkedin.help.prefix':
    "Optional but recommended. I'll read your public profile to tailor briefings to your background. ",
  'onboarding.agentLed.step1.linkedin.help.persist': 'Never persisted',
  'onboarding.agentLed.step1.linkedin.help.suffix': ' until you confirm.',
  'onboarding.agentLed.step1.linkedin.scanning': '● Reading LinkedIn profile…',
  'onboarding.agentLed.step1.linkedin.exploring':
    'Reading profile → experience → headline → current role →',
  'onboarding.agentLed.step1.linkedin.inferred': '✓ Inferred from LinkedIn',
  'onboarding.agentLed.step1.linkedin.fieldHeadline': 'Headline',
  'onboarding.agentLed.step1.linkedin.fieldCurrentRole': 'Current role',
  'onboarding.agentLed.step1.linkedin.fieldTenure': 'Tenure',
  'onboarding.agentLed.step1.linkedin.fieldPriorRole': 'Prior role',
  'onboarding.agentLed.step1.linkedin.confirmRole': 'Confirm role',
  'onboarding.agentLed.step1.linkedin.pickRole': 'Or pick a role',

  // Step 1 → right-rail extra row labels.
  'onboarding.agentLed.field.linkedin': 'LinkedIn',
  'onboarding.agentLed.field.headline': 'Headline',
  'onboarding.agentLed.field.tenure': 'Tenure',

  // Step 3 — labels for the chip-pick groups (used by the agent-led
  // Step-3 component AND the right-rail FieldsPanel summary row).
  'onboarding.agentLed.step3.label.voice': 'Voice',
  'onboarding.agentLed.step3.label.cadence': 'Briefing cadence',
  'onboarding.agentLed.step3.label.autonomy': 'Autonomy',
  'onboarding.agentLed.step3.footer':
    "I'll always cite the source and tag interpretation. No orphan claims, ever.",

  // Step 4 — connector tile copy. Five `kind` values from the shared
  // `CONNECTOR_KINDS` enum drive the tile grid; each gets a one-line
  // note explaining the access boundary.
  'onboarding.agentLed.step4.heading':
    "Last step. Pick at least two systems and I'll start stitching them together.",
  'onboarding.agentLed.step4.tile.slack.note': "Reads channels you're in. Never DMs.",
  'onboarding.agentLed.step4.tile.gmail.note': 'Drafts; sends only with approval.',
  'onboarding.agentLed.step4.tile.salesforce.note': 'Pipeline + accounts.',
  'onboarding.agentLed.step4.tile.github.note': 'Repos you own. PRs and issues.',
  'onboarding.agentLed.step4.tile.stripe.note': 'MRR, churn, plan changes.',
  'onboarding.agentLed.step4.connected': '● Connected',
  'onboarding.agentLed.step4.clickToConnect': 'CLICK TO CONNECT',
  'onboarding.agentLed.step4.counter': '{count} / 5 connected',
  'onboarding.agentLed.step4.scopeNote.prefix': "Each connector is org-scoped. You're ",
  'onboarding.agentLed.step4.scopeNote.role': 'admin · this workspace',
  'onboarding.agentLed.step4.scopeNote.suffix': ' — only you can grant data access.',

  // Step 5 — governance step chrome (auto-fill, scanning card, inferred
  // panel, stage chip-pick group label, worry chip-pick group label).
  'onboarding.agentLed.step5.autofill.cta': '◆ Auto-fill from your Carta + Drive',
  'onboarding.agentLed.step5.scanning.title': '● Reading incorporation docs…',
  'onboarding.agentLed.step5.scanning.body':
    'Cert of inc → bylaws → last 8-K (if any) → cap table →',
  'onboarding.agentLed.step5.inferred.title': '✓ Inferred · review and edit',
  'onboarding.agentLed.step5.label.entityType': 'Entity type',
  'onboarding.agentLed.step5.label.incState': 'State of incorporation',
  'onboarding.agentLed.step5.label.fundingRound': 'Last fundraise round',
  'onboarding.agentLed.step5.label.fiscalYear': 'Fiscal year start month',
  'onboarding.agentLed.step5.label.ein': 'EIN (optional)',
  'onboarding.agentLed.step5.label.boardSize': 'Board size',
  'onboarding.agentLed.step5.label.empStates': 'States where you employ (multi-select)',
  'onboarding.agentLed.step5.label.worries': 'What are you least confident about?',
  'onboarding.agentLed.step5.label.stage': 'Stage',
  'onboarding.agentLed.step5.label.specialAttention': 'Special attention',
  'onboarding.agentLed.step5.placeholder.ein': '87-3221987',
  'onboarding.agentLed.step5.placeholder.boardSize': '5',
  'onboarding.agentLed.step5.placeholder.specialAttention':
    "Anything I should pay special attention to? (a board prep, a hard week ahead, a deal you're worried about)",
  'onboarding.agentLed.step5.cta': "Let's go, I'm ready →",
  'onboarding.agentLed.step5.worries.note':
    "Whatever you pick, I'll show explanations in plain English next to every task — and connect you to counsel or a vetted provider when you need one.",
  // Connector display labels for the right-rail FieldsPanel rollup.
  'onboarding.agentLed.connectorLabel.slack': 'Slack',
  'onboarding.agentLed.connectorLabel.gmail': 'Gmail',
  'onboarding.agentLed.connectorLabel.salesforce': 'Salesforce',
  'onboarding.agentLed.connectorLabel.github': 'GitHub',
  'onboarding.agentLed.connectorLabel.stripe': 'Stripe',

  // ── Reveal interstitial (`/onboarding/reveal`). The "magic moment"
  //    page that sits between the agent-led wizard's completion and the
  //    user's first /today view. Three reveal panels (people / rituals /
  //    metrics) + a vocabulary translation strip. Every panel carries a
  //    confidence pill; user confirmations audit through
  //    `synthesis.override.saved`.
  'onboarding.reveal.eyebrow.complete': 'Read complete · {duration}',
  'onboarding.reveal.eyebrow.indexing': 'Indexing · just getting started',
  'onboarding.reveal.title.before': "Here's what I learned about",
  'onboarding.reveal.title.serif': 'your business',
  'onboarding.reveal.title.after': 'while you were gone.',
  'onboarding.reveal.sub.complete':
    "I read {messages} messages, {docs} docs, and {records} records. Nod when I'm right. Correct me when I'm not. I'll get better.",
  'onboarding.reveal.sub.partial':
    "I've only just started reading. Come back in 24h for the full picture. Until then, confirm what I have so far.",
  'onboarding.reveal.duration.placeholder': 'a few minutes',
  'onboarding.reveal.confidence.pill': '{pct}% conf',
  'onboarding.reveal.confidence.aria': 'Panel confidence {pct} percent',
  // `<RevealPanelShell>` confirm button — flips to this glyph after the
  // local `confirmed` state lands. Kept here (not as a literal) so a
  // future redesign that swaps the checkmark for copy lands in one site.
  'onboarding.reveal.shell.confirmed': '✓',

  // People panel.
  'onboarding.reveal.people.eyebrow': 'The {count} people you care about',
  'onboarding.reveal.people.eyebrow.empty': 'The people you care about',
  'onboarding.reveal.people.sub':
    "Based on who you @-mention, schedule, and reference. Yours, not your org's.",
  'onboarding.reveal.people.sub.empty':
    'No teammates yet — invite someone or wait for the agent to surface signal from your inbox.',
  'onboarding.reveal.people.confirm': 'Looks right →',
  'onboarding.reveal.people.add': 'Add someone',
  'onboarding.reveal.people.added': 'Added',
  'onboarding.reveal.people.add.placeholder': 'name@company.com',
  'onboarding.reveal.people.add.submit': 'Add',
  'onboarding.reveal.people.add.cancel': 'Cancel',

  // Rituals panel.
  'onboarding.reveal.rituals.eyebrow': 'The rituals you run on',
  'onboarding.reveal.rituals.sub': 'Detected from your calendar patterns + Slack channel rhythm.',
  'onboarding.reveal.rituals.sub.fromOnboarding':
    "From what you told me during setup — I'll refine this once your calendar is connected.",
  'onboarding.reveal.rituals.empty':
    'No rituals detected yet. Connect your calendar (Settings → Sources) so I can read your standing meetings.',
  'onboarding.reveal.rituals.confirm': 'Looks right →',
  'onboarding.reveal.rituals.edit': 'Edit',
  'onboarding.reveal.rituals.cadence.weekly': 'Weekly',
  'onboarding.reveal.rituals.cadence.biweekly': 'Bi-weekly',
  'onboarding.reveal.rituals.cadence.monthly': 'Monthly',
  'onboarding.reveal.rituals.cadence.quarterly': 'Quarterly',
  'onboarding.reveal.rituals.boardSync': 'Board sync',

  // Metrics panel.
  'onboarding.reveal.metrics.eyebrow': 'The metrics you check most',
  'onboarding.reveal.metrics.sub': 'Based on what you open, screenshot, and bring up in syncs.',
  'onboarding.reveal.metrics.sub.fromOnboarding':
    "Built from your north star + priorities — I'll add live values once your data tools are connected.",
  'onboarding.reveal.metrics.empty':
    'No metrics captured yet. Connect a data source (Stripe, Salesforce, your warehouse) so I can track them.',
  'onboarding.reveal.metrics.confirm': 'Looks right →',
  'onboarding.reveal.metrics.pick': 'Pick others',
  'onboarding.reveal.metrics.pending': '—',
  'onboarding.reveal.metrics.label.northStar': 'North star',
  'onboarding.reveal.metrics.label.priority': 'Priority',
  'onboarding.reveal.metrics.label.goalTarget': 'Goal target',

  // Vocabulary strip.
  'onboarding.reveal.vocab.eyebrow': 'And your vocabulary',
  'onboarding.reveal.vocab.title': "I'll talk like you.",
  'onboarding.reveal.vocab.empty':
    "I haven't picked up on your phrasing yet — I'll start mirroring it once we've talked for a few days.",

  // Final CTA row.
  'onboarding.reveal.cta.primary': "Looks good — write me Monday's briefing",
  'onboarding.reveal.cta.secondary': 'Walk me through what you got wrong',

  // Loading + error states.
  'onboarding.reveal.loading': 'Pulling together what I learned…',
  'onboarding.reveal.error':
    "Couldn't load your reveal. Continue to /today — I'll show what I have there.",
  'onboarding.reveal.error.continue': 'Continue to /today',
  'onboarding.reveal.error.retry': 'Try again',
  // Duration format helpers — used by the eyebrow when `readDurationMs`
  // is non-zero. Sub-hour reads format as "Ym Zs"; longer ones as "Xh Ym".
  'onboarding.reveal.duration.hm': '{h}h {m}m',
  'onboarding.reveal.duration.ms': '{m}m {s}s',

  // Redesigned reveal hero (row-03) — new format derives from
  // `OnboardingRevealSummary` directly (readDurationMinutes + integer
  // counts). Reuses the .title.* / .vocab.* / .cta.* keys above.
  'onboarding.reveal.hero.eyebrow.complete': '● Read complete · {hours}h {minutes}m',
  'onboarding.reveal.hero.eyebrow.indexing': '● Indexing · just getting started',
  'onboarding.reveal.hero.sub':
    "I read {messages} messages, {docs} docs, and {records} records. Nod when I'm right. Correct me when I'm not. I'll get better.",

  // "Walk me through what you got wrong" slide-over.
  'onboarding.reveal.corrections.title': 'Tell me what I got wrong.',
  'onboarding.reveal.corrections.sub':
    "I'll keep the row I had, but I'll learn from your correction. Add what I missed below.",
  'onboarding.reveal.corrections.empty':
    "I don't have anything to correct yet — confirm the panels above and I'll record your approvals.",
  'onboarding.reveal.corrections.placeholder': 'What I should have said…',
  'onboarding.reveal.corrections.save': 'Save correction',
  'onboarding.reveal.corrections.saving': 'Saving…',
  'onboarding.reveal.corrections.saved': '✓ Saved',
  'onboarding.reveal.corrections.close': 'Close',
  'onboarding.reveal.corrections.section.people': 'People',
  'onboarding.reveal.corrections.section.rituals': 'Rituals',
  'onboarding.reveal.corrections.section.metrics': 'Metrics',
  // Templated per-row claim copy rendered inside the corrections slide-
  // over (one row per people/rituals/metrics item). `tFormat` interpolates
  // the placeholders against the row's data so translators can re-order
  // tokens without code changes.
  'onboarding.reveal.corrections.claim.people': '{name} is one of the people you care about.',
  'onboarding.reveal.corrections.claim.ritual': '{title} happens {when}.',
  'onboarding.reveal.corrections.claim.metric': '{label}: {value} ({delta})',

  // ── Ingestion (`/onboarding/ingestion`). Post-wizard step 5 client-
  //    side animation that ticks through five stack sources (Slack /
  //    Gmail / Salesforce / GitHub / Stripe). v1 is a mock simulation —
  //    real ingestion-status SSE wires in once the connector backend
  //    catches up.
  'onboarding.ingestion.brand': 'LeanOpx',
  'onboarding.ingestion.topbar': 'SETUP · STEP 5 OF 5 · READING YOUR STACK',
  'onboarding.ingestion.topbar.close': 'Close · keep ingesting',
  'onboarding.ingestion.eyebrow': 'EST. {minutes} MIN REMAINING · {done} OF {total} SOURCES DONE',
  'onboarding.ingestion.title': 'Reading 90 days of your stack.',
  'onboarding.ingestion.sub':
    "I'm pulling everything I have permission for into your private workspace. Nothing leaves. Nothing trains anyone else's model. You can close this tab and I'll email you when the first briefing is ready.",
  'onboarding.ingestion.progress.title': 'Overall progress',
  'onboarding.ingestion.progress.started': 'Started {seconds}s ago',
  'onboarding.ingestion.progress.indexed': 'Total items indexed: {count}',
  'onboarding.ingestion.source.slack': 'Slack',
  'onboarding.ingestion.source.gmail': 'Gmail',
  'onboarding.ingestion.source.salesforce': 'Salesforce',
  'onboarding.ingestion.source.github': 'GitHub',
  'onboarding.ingestion.source.stripe': 'Stripe',
  'onboarding.ingestion.source.slack.note': '14 channels · 90 days',
  'onboarding.ingestion.source.gmail.note': '90 days of inbox',
  'onboarding.ingestion.source.salesforce.note': 'Pipeline + accounts',
  'onboarding.ingestion.source.github.note': 'PRs + issues + reviews',
  'onboarding.ingestion.source.stripe.note': 'MRR, churn, plan changes',
  'onboarding.ingestion.source.state.done': 'DONE',
  'onboarding.ingestion.source.state.active': 'READING',
  'onboarding.ingestion.source.state.queued': 'WAITING',
  'onboarding.ingestion.source.items.queued': 'Queued · starts next',
  'onboarding.ingestion.source.items.reading': 'Reading · {pct}% complete',
  'onboarding.ingestion.source.items.done': 'Indexed and deduplicated',
  'onboarding.ingestion.log.eyebrow': "What I'm doing right now",
  // Live-log row templates (rendered inside the activity feed).
  'onboarding.ingestion.log.done': '{name} ingest complete · {note}',
  'onboarding.ingestion.log.reading': 'Reading {name} · {pct}% complete',
  'onboarding.ingestion.log.queued': '{name} queued — starts after {prev}',
  'onboarding.ingestion.log.queued.previous': 'previous',
  'onboarding.ingestion.footer.title': "I'll email you when your first briefing is ready.",
  'onboarding.ingestion.footer.sub':
    'Friday afternoon, latest. Monday 8am briefing locks at 6am Monday morning.',
  'onboarding.ingestion.footer.close': 'Close · keep ingesting',
  'onboarding.ingestion.footer.continue': 'See what’s already indexed →',

  // ── Invite team (`/onboarding/invite-team`). Post-wizard optional
  //    step: surface detected calendar attendees + manual-add input.
  //    v1 is a mock detected-attendees list — wires to the live
  //    calendar-attendees endpoint once the backend lands.
  'onboarding.invite-team.brand': 'LeanOpx',
  'onboarding.invite-team.topbar': 'SETUP · INVITE TEAM',
  'onboarding.invite-team.topbar.skip': 'Skip for now →',
  'onboarding.invite-team.eyebrow': 'OPTIONAL · TAKES 30 SECONDS',
  'onboarding.invite-team.title': 'Who else should be in here?',
  'onboarding.invite-team.sub':
    'I noticed these folks in your calendar from the last 90 days. Add the ones who should see briefings, ask the agent, or run reviews. Roles are suggestions — you can change anything.',
  'onboarding.invite-team.detected.heading': 'From your calendar · {count} co-workers detected',
  'onboarding.invite-team.detected.select-all': 'Select all',
  'onboarding.invite-team.detected.meetings': '{count} meetings',
  'onboarding.invite-team.manual.eyebrow': 'Or add manually',
  'onboarding.invite-team.manual.placeholder': 'email@company.com',
  'onboarding.invite-team.manual.add': '+ Add',
  'onboarding.invite-team.manual.hint':
    'Each person gets their own briefing tailored to their role. Members can ask the agent and read everything.',
  'onboarding.invite-team.role.admin': 'Admin',
  'onboarding.invite-team.role.member': 'Member',
  // Aria-labels — screen-reader exposed control labels for the picker
  // checkboxes + per-row role selector + manual-add role selector.
  'onboarding.invite-team.aria.select': 'Select {name}',
  'onboarding.invite-team.aria.role': 'Role for {name}',
  'onboarding.invite-team.aria.manual-role': 'Manual invite role',
  'onboarding.invite-team.summary': '{count} PEOPLE SELECTED · {count} INVITES WILL BE EMAILED',
  'onboarding.invite-team.skip': 'Skip',
  'onboarding.invite-team.send': 'Send {count} invites →',
  'onboarding.invite-team.sending': 'Sending…',
  'onboarding.invite-team.error.partial':
    '{sent} sent · {failed} couldn’t be sent. Try those again or continue.',
  'onboarding.invite-team.error.network':
    'Couldn’t reach the server. Try again — the invites haven’t been sent yet.',

  // Industry / stage / size human labels.
  'onboarding.wizard.industry.b2b-saas': 'B2B SaaS',
  'onboarding.wizard.industry.consumer-saas': 'Consumer SaaS',
  'onboarding.wizard.industry.fintech': 'Fintech',
  'onboarding.wizard.industry.healthtech': 'Healthtech',
  'onboarding.wizard.industry.recruiting': 'Recruiting / staffing',
  'onboarding.wizard.industry.agency': 'Agency',
  'onboarding.wizard.industry.ecommerce': 'E-commerce',
  'onboarding.wizard.industry.marketplace': 'Marketplace',
  'onboarding.wizard.industry.developer-tools': 'Developer tools',
  'onboarding.wizard.industry.professional-services': 'Professional services',
  'onboarding.wizard.industry.real-estate': 'Real estate',
  'onboarding.wizard.industry.edtech': 'Edtech',
  'onboarding.wizard.industry.media': 'Media',
  'onboarding.wizard.industry.other': 'Other',
  'onboarding.wizard.stage.pre-seed': 'Pre-seed',
  'onboarding.wizard.stage.seed': 'Seed',
  'onboarding.wizard.stage.series-a': 'Series A',
  'onboarding.wizard.stage.series-b': 'Series B',
  'onboarding.wizard.stage.series-c-plus': 'Series C+',
  'onboarding.wizard.stage.profitable': 'Profitable',
  'onboarding.wizard.stage.bootstrapped': 'Bootstrapped',
  'onboarding.wizard.size.5-25': '5–25',
  'onboarding.wizard.size.25-75': '25–75',
  'onboarding.wizard.size.75-150': '75–150',
  'onboarding.wizard.size.150-plus': '150+',
  'onboarding.wizard.region.us-bayarea': 'US — Bay Area',
  'onboarding.wizard.region.us-nyc': 'US — New York',
  'onboarding.wizard.region.us-other': 'US — Other',
  'onboarding.wizard.region.eu-london': 'EU — London',
  'onboarding.wizard.region.eu-other': 'EU — Other',
  'onboarding.wizard.region.apac-singapore': 'APAC — Singapore',
  'onboarding.wizard.region.apac-tokyo': 'APAC — Tokyo',
  'onboarding.wizard.region.apac-other': 'APAC — Other',
  'onboarding.wizard.region.remote-first': 'Remote-first / global',
  // External signals surface.
  'signals.external.eyebrow': 'Surface · outside view',
  'signals.external.title': 'Outside view',
  'signals.external.sub':
    "What's relevant in the world for your business — funding, layoffs, regulation, peer pricing.",
  'signals.external.empty':
    'No signals yet. Add a signal topic in the picker below — the connector pipeline will populate the feed.',
  'signals.external.error': "Couldn't load external signals. Refresh to try again.",
  'signals.external.subscribed.title': 'Subscribed topics',
  'signals.external.subscribed.empty':
    'No topics yet. Pick topics from the catalog below to start receiving signals.',
  'signals.external.catalog.title': 'Topic catalog',
  'signals.external.refresh': 'Refresh signals',
  'signals.external.refreshing': 'Refreshing…',
  'signals.external.refresh.no-context':
    'No business context yet — run the onboarding wizard first.',
  'signals.external.refresh.no-topics': 'No subscribed topics. Pick some below to start.',
  'signals.external.refresh.success': 'Refreshed — {count} signals.',
  'signals.external.refresh.fallback':
    'Refreshed with {count} sample signals — live source unavailable. Check that the runtime service account has roles/aiplatform.user, then retry.',
  'signals.external.refresh.error': "Couldn't refresh — try again in a moment.",
  'signals.external.topic.no-context': 'Run the onboarding wizard before editing topics.',
  'signals.external.topic.update-error': "Couldn't update subscription.",
  'signals.external.severity.info': 'info',
  'signals.external.severity.watch': 'watch',
  'signals.external.severity.critical': 'critical',
  'signals.external.relevance': 'Relevance · {pct}%',
  'signals.external.published': '{when}',
  'signals.external.openSource': 'Open source',
  'signals.external.subscribe': 'Subscribe',
  'signals.external.unsubscribe': 'Unsubscribe',
  'signals.external.filter.topic': 'Topic',
  'signals.external.filter.topic.all': 'All topics',
  'signals.external.filter.severity': 'Severity',
  'signals.external.filter.severity.all': 'All severities',
  'signals.external.filter.since': 'Since',
  'signals.external.filter.since.7d': 'Last 7 days',
  'signals.external.filter.since.14d': 'Last 14 days',
  'signals.external.filter.since.30d': 'Last 30 days',
  'signals.external.filter.since.all': 'All time',
  'signals.external.watchlist.title': 'Watchlist',
  'signals.external.watchlist.empty':
    'No outside signals tracking yet. Open the catalog to subscribe.',
  // Sidebar nav for the new external-signals surface.
  'nav.external-signals': 'Outside view',

  // Notes — multi-media personal notes (markdown body + images + files +
  // voice memos with AI-generated transcripts). Both standalone and
  // bound to dossier entities.
  'nav.notes': 'Notes',
  'notes.title': 'Notes',
  'notes.eyebrow': 'Personal · multi-media',
  'notes.sub':
    'Markdown notes with image, file, and voice-memo attachments. Voice memos transcribe automatically.',
  'notes.new': 'New note',
  'notes.empty.title': 'No notes yet',
  'notes.empty.body':
    'Start a thought, drop in a screenshot, or record a voice memo — anything you capture here is private to you.',
  'notes.placeholder.body': 'Start typing — markdown supported (e.g. **bold**, # heading, - list).',
  'notes.title.placeholder': 'Untitled note',
  'notes.target.standalone': 'Personal',
  'notes.target.person': 'Person',
  'notes.target.customer': 'Customer',
  'notes.target.topic': 'Topic',
  'notes.target.product': 'Product',
  'notes.target.entity': 'Entity',
  'notes.attachment.add-image': 'Add image',
  'notes.attachment.add-file': 'Attach file',
  'notes.attachment.uploading': 'Uploading…',
  'notes.attachment.transcribing': 'Transcribing…',
  'notes.attachment.transcription-failed': 'Transcription failed — audio is still attached.',
  'notes.attachment.no-transcript-yet': 'Transcript will appear here in a few seconds.',
  'notes.delete.confirm': 'Delete this note?',
  'notes.error.load': 'Could not load notes.',
  'notes.error.save': 'Could not save note.',
  'notes.error.upload': 'Attachment upload failed.',
  'notes.saved': 'Saved',
  'notes.saving': 'Saving…',
  // Designer's timeline + linked-entities layout.
  'notes.group.today': 'Today',
  'notes.group.yesterday': 'Yesterday',
  'notes.flag.voice': 'voice',
  'notes.select.empty': 'Select a note on the left, or create a new one.',
  'notes.timeline.aria': 'Notes timeline',
  'notes.attachment.stored': 'Image stored at {path}',
  'notes.attachment.silent': '(silent recording)',

  // Admin entity-resolution surface (epic #9).
  'admin.er.title': 'Entity resolution',
  'admin.er.sub':
    'Mid-band candidate merges queued for your approval. High-band merges auto-applied; low-band pairs stay separate.',
  'admin.er.band.high': 'High',
  'admin.er.band.mid': 'Mid',
  'admin.er.band.low': 'Low',
  'admin.er.status.pending': 'Pending',
  'admin.er.status.approved': 'Approved',
  'admin.er.status.rejected': 'Rejected',
  'admin.er.status.auto-approved': 'Auto-approved',
  'admin.er.approve': 'Approve merge',
  'admin.er.reject': 'Reject',
  'admin.er.empty': 'No candidates in this band.',
  'admin.er.migrate': 'Run graph migration',
  'admin.er.migrate.success': 'Applied {applied} migration statement(s).',
  'admin.er.migrate.error': 'Migration failed.',

  // Admin connectors surface (epics #7 + #8).
  'admin.connectors.title': 'Connectors',
  'admin.connectors.sub':
    "Connect the tools your company runs on. LeanOpx stitches them into a queryable graph and writes Monday's briefing from what changed.",
  'admin.connectors.kind.slack': 'Slack',
  'admin.connectors.kind.gmail': 'Gmail',
  'admin.connectors.kind.salesforce': 'Salesforce',
  'admin.connectors.kind.github': 'GitHub',
  'admin.connectors.kind.stripe': 'Stripe',
  'admin.connectors.status.disconnected': 'Not connected',
  'admin.connectors.status.installing': 'Installing…',
  'admin.connectors.status.active': 'Active',
  'admin.connectors.status.backfilling': 'Backfilling…',
  'admin.connectors.status.error': 'Error',
  'admin.connectors.status.paused': 'Paused',
  'admin.connectors.connect': 'Connect',
  'admin.connectors.connecting': 'Connecting…',
  'admin.connectors.disconnect': 'Disconnect',
  'admin.connectors.backfill': 'Run backfill',
  'admin.connectors.last-sync': 'Last sync',
  'admin.connectors.errors': 'Errors',
  'admin.connectors.no-errors': 'No errors logged.',
  'admin.connectors.vendor-not-configured':
    'Platform admin has not configured this connector yet — try again later.',
  'admin.connectors.connect-failed':
    'Could not start the install. Try again or check the audit log.',
  'admin.connectors.disconnect-failed': 'Could not disconnect. Try again.',
  'admin.connectors.backfill-failed': 'Backfill failed.',
  'admin.connectors.stripe.paste-key': 'Restricted API key (rk_…)',
  'admin.connectors.stripe.paste-webhook': 'Webhook signing secret (whsec_…)',
  'admin.connectors.stripe.connect': 'Save Stripe credentials',
  'admin.connectors.stripe.help':
    'Stripe doesn’t use OAuth. Create a restricted API key + webhook endpoint in your Stripe dashboard, then paste the values here.',
  'admin.connectors.slack.channels': 'Channels',
  'admin.connectors.slack.channels.sub':
    'Pick which channels Slack should backfill into the graph. Live ingest covers all channels once subscribed.',
  'admin.connectors.slack.channels.save': 'Save selection + start backfill',

  // v6 Integrations directory (issue #114). New marketplace surface
  // under /admin/integrations/*; the existing /admin/connectors stays
  // for post-install management.
  'integrations.eyebrow': 'Integrations',
  'integrations.title': 'The pipes that feed LeanOpx.',
  'integrations.sub':
    'Every event is org-scoped, cited to source, and written to your graph with provenance. Read-only by default; write actions queue for approval.',
  'integrations.cta.request': 'Request connector',
  'integrations.cta.add': '+ Add integration',
  'integrations.filter.all': 'All',
  'integrations.filter.identity': 'Identity',
  'integrations.filter.crm': 'CRM',
  'integrations.filter.communications': 'Communications',
  'integrations.filter.finance': 'Finance',
  'integrations.filter.code': 'Code',
  'integrations.filter.hr': 'HR',
  'integrations.filter.knowledge': 'Knowledge',
  'integrations.filter.product': 'Product',
  'integrations.error.load': 'Could not load integrations.',
  'integrations.section.connected': 'Connected',
  'integrations.section.suggested': 'Suggested for your shape',
  'integrations.section.all': 'Browse all',
  'integrations.row.manage': 'Manage',
  'integrations.row.health': 'Health',
  'integrations.suggested.cta.connect': 'Connect',
  'integrations.suggested.cta.details': 'Details',
  'integrations.suggested.recommended': '★ Recommended',
  'integrations.rail.marketplace': 'Marketplace',
  'integrations.rail.connected': 'connected',
  'integrations.rail.available': 'available',
  'integrations.rail.drift': 'in drift',
  'integrations.rail.errored': 'errored',
  'integrations.rail.recommends': 'LeanOpx recommends',
  'integrations.rail.recommends.body':
    'Connect Google Workspace next — it’s the highest-ROI single source for your shape.',
  'integrations.rail.recommends.cta': '+ Connect Google Workspace',
  'integrations.rail.trust': 'Trust posture',
  'integrations.rail.trust.body':
    'All connectors are read-only by default. Write actions require approval per draft.',
  'integrations.rail.trust.compliance': 'SOC 2 · GDPR · CCPA',
  'integrations.empty.loading': 'Loading integrations…',
  'integrations.empty.connected':
    'No live connectors yet. Pick one from Suggested below or Browse all to install.',
  'integrations.empty.no-matches':
    'No integrations match this category. Clear the filter to see everything.',
  // Shared back-link used by every sub-route under /admin/integrations/*
  // (configure / health / permissions / google-workspace). One key so
  // the label tracks across surfaces.
  'integrations.back.all': 'All integrations',
  // Connector status labels — operator-facing reads of the `ConnectorStatus`
  // enum (in packages/shared). The schema uses `active` internally but the
  // UI prefers `live` (same state, friendlier word). Every status has an
  // entry so a new enum value can't silently render under the wrong word.
  'integrations.status.live': 'live',
  'integrations.status.paused': 'paused',
  'integrations.status.error': 'error',
  'integrations.status.installing': 'installing',
  'integrations.status.backfilling': 'backfilling',
  'integrations.status.disconnected': 'disconnected',
  // Suggested-connector cards on /admin/integrations. Each suggestion
  // has three editorial fields surfaced on the card: category label
  // (right of the brand mark), one-line "why" (the reason this is
  // suggested), and an estimated install time. Three suggestions wired
  // today — google workspace / hubspot / rippling / zoom.
  'integrations.suggested.gws.category-label': 'Identity · Calendar · Email',
  'integrations.suggested.gws.why':
    'Directory + calendar stats unlock org-graph + focus-time. Most-requested.',
  'integrations.suggested.gws.time': '~6 min',
  'integrations.suggested.hubspot.category-label': 'CRM',
  'integrations.suggested.hubspot.why':
    'Detected pipeline data in your Notion — likely a duplicate source.',
  'integrations.suggested.hubspot.time': '~4 min',
  'integrations.suggested.rippling.category-label': 'HR · Identity',
  'integrations.suggested.rippling.why': 'Cleaner directory than email-domain inference.',
  'integrations.suggested.rippling.time': '~10 min · admin',
  'integrations.suggested.zoom.category-label': 'Communications',
  'integrations.suggested.zoom.why': 'Meeting outcomes for QBRs · transcript opt-in.',
  'integrations.suggested.zoom.time': '~3 min',
  // Per-kind scope-summary fallbacks. Slack falls back to a count line
  // when channelIds is populated (rendered via tFormat with {n}); the
  // other kinds have a single static line. Editorial copy.
  'integrations.scope-summary.slack.channels': '{n} channel{s} · 1 workspace',
  'integrations.scope-summary.slack.all': 'Live workspace · all channels',
  'integrations.scope-summary.gmail': 'Per-user · metadata only',
  'integrations.scope-summary.salesforce': 'Opps · Accounts · Contacts',
  'integrations.scope-summary.github': 'Org-wide · PRs + commits + issues',
  'integrations.scope-summary.stripe': 'Live mode · subs + invoices',
  'integrations.scope-summary.default': 'Live · read-only',

  // Google Workspace connect (install flow). Four-step wizard:
  // method → scopes → consent → verify. Step 1 is the only interactive
  // page today — steps 2-4 follow when the OAuth + service-account
  // backends land. TODO(issue #114): wire methods to real install URLs.
  'integrations.gws.connect.eyebrow': 'Step 1 of 4 · Choose how to connect',
  'integrations.gws.connect.title': 'Connect Google Workspace.',
  'integrations.gws.connect.sub':
    'Three paths. The Marketplace install is what most ops + finance teams pick — one admin consent, every user covered, fully audited in your Workspace logs.',
  'integrations.gws.connect.method.marketplace': 'Google Workspace Marketplace',
  'integrations.gws.connect.method.marketplace.tag': 'Recommended',
  'integrations.gws.connect.method.marketplace.cta': 'Open Marketplace listing',
  'integrations.gws.connect.method.oauth': 'OAuth · admin sign-in',
  'integrations.gws.connect.method.oauth.cta': 'Continue with OAuth',
  'integrations.gws.connect.method.sa': 'Service account · domain-wide delegation',
  'integrations.gws.connect.method.sa.tag': 'Advanced',
  'integrations.gws.connect.method.sa.cta': 'Request setup call',
  'integrations.gws.connect.preview.eyebrow': 'Preview · 9 scopes (6 required, 3 optional)',
  'integrations.gws.connect.preview.col.scope': 'OAUTH SCOPE',
  'integrations.gws.connect.preview.col.what': 'WHAT WE READ',
  'integrations.gws.connect.preview.col.status': 'STATUS',
  'integrations.gws.connect.preview.required': 'required',
  'integrations.gws.connect.preview.optional': 'optional',
  'integrations.gws.connect.back': '← Back',
  // CTA labels — derived from the active install method so the button
  // copy matches whichever method radio is selected (see continueLabel).
  'integrations.gws.connect.continue.marketplace': 'Continue with Marketplace →',
  'integrations.gws.connect.continue.oauth': 'Continue with OAuth →',
  'integrations.gws.connect.continue.sa': 'Continue with Service account →',
  'integrations.gws.connect.progress.method': 'Choose method',
  'integrations.gws.connect.progress.scopes': 'Review scopes',
  'integrations.gws.connect.progress.consent': 'Admin consent',
  'integrations.gws.connect.progress.verify': 'Verify & test',
  'integrations.gws.connect.progress.eyebrow': 'Install progress',
  'integrations.gws.connect.unlock.eyebrow': "What you'll unlock",
  // Per-method meta + bullets — three install methods (marketplace /
  // oauth / sa), each with who/time/coverage facts shown above the
  // bullet list. The bullet copy is intentionally editorial — translate
  // verbatim, don't summarize.
  'integrations.gws.connect.method.marketplace.who': 'Super-admin (one-time)',
  'integrations.gws.connect.method.marketplace.time': '~6 min',
  'integrations.gws.connect.method.marketplace.coverage': 'Whole organization',
  'integrations.gws.connect.method.marketplace.bullet.1':
    'Domain-wide install — one admin consent covers every user',
  'integrations.gws.connect.method.marketplace.bullet.2':
    'Inherits user lifecycle (provisioning, deprovisioning) from Workspace',
  'integrations.gws.connect.method.marketplace.bullet.3':
    'Listed in Workspace audit logs · auditable by your IT team',
  'integrations.gws.connect.method.marketplace.bullet.4':
    'Scopes pre-declared in our Marketplace listing — no surprise prompts',
  'integrations.gws.connect.method.oauth.who': 'Super-admin or delegated admin',
  'integrations.gws.connect.method.oauth.time': '~3 min',
  'integrations.gws.connect.method.oauth.coverage': 'Workspace-wide (admin scopes)',
  'integrations.gws.connect.method.oauth.bullet.1':
    'Standard Google OAuth consent screen — admin grants on behalf of org',
  'integrations.gws.connect.method.oauth.bullet.2':
    'No Marketplace dependency — works on restricted Workspace tenants',
  'integrations.gws.connect.method.oauth.bullet.3':
    'Token refresh handled by LeanOpx; revoke any time at admin.google.com',
  'integrations.gws.connect.method.oauth.bullet.4':
    'Scopes shown one screen before consent (next step)',
  'integrations.gws.connect.method.sa.who': 'Workspace admin + LeanOpx security review',
  'integrations.gws.connect.method.sa.time': '~25 min',
  'integrations.gws.connect.method.sa.coverage': 'Programmatic, headless',
  'integrations.gws.connect.method.sa.bullet.1':
    'JSON service-account key + DwD client ID pinned in admin.google.com',
  'integrations.gws.connect.method.sa.bullet.2':
    'Required for: air-gapped tenants, BYOK, custom audit forwarding',
  'integrations.gws.connect.method.sa.bullet.3':
    'Our security team confirms scope set with your CISO before enable',
  'integrations.gws.connect.method.sa.bullet.4': 'Token rotation: every 24h · automatic',
  // Per-scope "what we read" copy. The OAuth scope identifier itself
  // (`admin.directory.user.readonly`, etc.) is rendered verbatim; this
  // is the human-readable explanation shown next to each row.
  'integrations.gws.connect.scope.user.what': 'Users, names, titles, manager',
  'integrations.gws.connect.scope.group.what': 'Groups + memberships',
  'integrations.gws.connect.scope.orgunit.what': 'Org units / hierarchy',
  'integrations.gws.connect.scope.calendar-events.what':
    'Meeting metadata (times, attendees) — not bodies',
  'integrations.gws.connect.scope.calendar-list.what': 'List of calendars in scope',
  'integrations.gws.connect.scope.gmail.what':
    'Email headers only (from / to / time) — no subjects, no bodies',
  'integrations.gws.connect.scope.drive.what':
    'Shared-doc titles, owners, timestamps — no contents',
  'integrations.gws.connect.scope.chat.what': 'Chat space names (not messages)',
  'integrations.gws.connect.scope.audit.what': 'Workspace audit log forwarding (compliance)',
  // Right-rail "What you'll unlock" — four headline/sub pairs that
  // summarize the agent's new capabilities once Workspace is connected.
  'integrations.gws.connect.unlock.org.title': 'Org graph',
  'integrations.gws.connect.unlock.org.sub': 'directory + groups',
  'integrations.gws.connect.unlock.focus.title': 'Focus-time signals',
  'integrations.gws.connect.unlock.focus.sub': 'from calendar density',
  'integrations.gws.connect.unlock.comms.title': 'Comms map',
  'integrations.gws.connect.unlock.comms.sub': 'who talks to whom · headers only',
  'integrations.gws.connect.unlock.docs.title': 'Doc activity',
  'integrations.gws.connect.unlock.docs.sub': 'shared files only · titles only',

  // Google Workspace configure
  'integrations.gws.configure.eyebrow': 'Google Workspace · sync configuration',
  'integrations.gws.configure.title': 'Choose what flows in.',
  'integrations.gws.configure.sub':
    'Each module reads metadata only. Bodies, subjects, and document contents are never transmitted to LeanOpx — even when we have the scope.',
  'integrations.gws.configure.dry-run': 'Dry run',
  'integrations.gws.configure.save': 'Save & resync',
  'integrations.gws.configure.module.directory': 'Directory sync',
  'integrations.gws.configure.module.directory.sub':
    'Users, groups, org units, manager chain. The backbone of your org graph.',
  'integrations.gws.configure.module.directory.badge': 'primary source',
  'integrations.gws.configure.module.calendar': 'Calendar stats',
  'integrations.gws.configure.module.calendar.sub':
    'Meeting density, attendance, focus-time blocks. Helps the agent understand cadence.',
  'integrations.gws.configure.module.gmail': 'Email metadata',
  'integrations.gws.configure.module.gmail.sub':
    'Sender → recipient graph, response latency, volume by domain. No bodies, no subjects.',
  'integrations.gws.configure.module.drive': 'Drive activity',
  'integrations.gws.configure.module.drive.sub':
    'Shared doc titles, owners, last-modified. Useful for surfacing "who’s writing what".',
  'integrations.gws.configure.module.meet': 'Meet · meeting outcomes',
  'integrations.gws.configure.module.meet.sub':
    'Post-meeting summaries + action items (requires Workspace Gemini add-on).',
  'integrations.gws.configure.rail.connection': 'Connection',
  'integrations.gws.configure.rail.never-see': 'What we never see',
  'integrations.gws.configure.rail.compliance': 'Compliance',
  // Per-module row labels (left column "k") + values (right column "v")
  // shown inside each <Section>. Visible English copy → STRINGS so a
  // future translator can rephrase without touching the component tree.
  'integrations.gws.configure.directory.row.include.k': 'Include',
  'integrations.gws.configure.directory.row.include.v': 'All active users + service accounts',
  'integrations.gws.configure.directory.row.exclude.k': 'Exclude',
  'integrations.gws.configure.directory.row.exclude.v': 'OU: /Contractors/Temp (configurable)',
  'integrations.gws.configure.directory.row.attributes.k': 'Attributes',
  'integrations.gws.configure.directory.row.schedule.k': 'Schedule',
  'integrations.gws.configure.directory.row.schedule.v':
    'Every 15 minutes · plus on user-create/update webhook',
  'integrations.gws.configure.directory.row.conflict.k': 'On conflict',
  'integrations.gws.configure.directory.row.conflict.v': 'Workspace wins (your source of truth)',
  'integrations.gws.configure.calendar.row.calendars.k': 'Calendars',
  'integrations.gws.configure.calendar.row.calendars.v': 'All primary calendars · rooms excluded',
  'integrations.gws.configure.calendar.row.extract.k': 'What we extract',
  'integrations.gws.configure.calendar.row.extract.v':
    'Start/end, duration, attendee count + emails, response status, recurring patterns',
  'integrations.gws.configure.calendar.row.privacy.k': 'Privacy',
  'integrations.gws.configure.calendar.row.privacy.pill': 'titles redacted',
  'integrations.gws.configure.calendar.row.privacy.note':
    'Toggle to capture event titles (opt-in per user)',
  'integrations.gws.configure.calendar.row.private.k': 'Private events',
  'integrations.gws.configure.calendar.row.private.v':
    'Skipped — visibility=private events never read',
  'integrations.gws.configure.calendar.row.schedule.k': 'Schedule',
  'integrations.gws.configure.calendar.row.schedule.v': 'Every 5 minutes',
  'integrations.gws.configure.gmail.row.scope.k': 'Scope',
  'integrations.gws.configure.gmail.row.scope.v': 'gmail.metadata · header fields only',
  'integrations.gws.configure.gmail.row.opt-in.k': 'Per-user opt-in',
  'integrations.gws.configure.gmail.row.opt-in.v': 'Off — admin-wide enable (current policy)',
  'integrations.gws.configure.gmail.row.retention.k': 'Retention',
  'integrations.gws.configure.gmail.row.retention.v':
    'Edges aggregated nightly; raw headers purged after 30 days',
  // Connection rail — key/value pairs identifying the install. Values
  // are dynamic in production; today they show "—" placeholders until
  // the GWS adapter wires real metadata.
  'integrations.gws.configure.connection.row.workspace.k': 'workspace',
  'integrations.gws.configure.connection.row.workspace.v': 'leanopx.com',
  'integrations.gws.configure.connection.row.tenant.k': 'tenant id',
  'integrations.gws.configure.connection.row.connected-by.k': 'connected by',
  'integrations.gws.configure.connection.row.connected.k': 'connected',
  'integrations.gws.configure.connection.row.token.k': 'token refresh',
  'integrations.gws.configure.connection.row.token.v': '● auto · 24h',
  // "What we never see" — six privacy assertions shown as a strikethrough
  // list. Editorial copy; each entry maps 1:1 to a UI line.
  'integrations.gws.configure.never-see.bodies': 'Message bodies',
  'integrations.gws.configure.never-see.subjects': 'Email subjects',
  'integrations.gws.configure.never-see.docs': 'Doc contents',
  'integrations.gws.configure.never-see.private': 'Private calendar events',
  'integrations.gws.configure.never-see.chat': 'Chat messages',
  'integrations.gws.configure.never-see.drive': 'Drive file contents',
  // Compliance rail — key/value triplets summarizing data posture.
  'integrations.gws.configure.compliance.row.residency.k': 'data residency',
  'integrations.gws.configure.compliance.row.residency.v': 'US · primary',
  'integrations.gws.configure.compliance.row.retention.k': 'retention',
  'integrations.gws.configure.compliance.row.retention.v': '90d raw · 18mo agg.',
  'integrations.gws.configure.compliance.row.audit.k': 'audit forward',
  'integrations.gws.configure.compliance.row.audit.v': '● enabled',

  // Google Workspace health
  'integrations.gws.health.eyebrow': 'Google Workspace · health & activity',
  'integrations.gws.health.title': "Everything's flowing.",
  'integrations.gws.health.sub':
    'The agent reads Workspace every 5–15 minutes depending on entity. Errors auto-retry with exponential backoff and surface here if they persist.',
  'integrations.gws.health.cta.resync': 'Force resync',
  'integrations.gws.health.cta.admin': 'Open in Workspace admin ↗',
  'integrations.gws.health.tile.users': 'Users in directory',
  'integrations.gws.health.tile.calendars': 'Calendars synced',
  'integrations.gws.health.tile.events': 'Events · last 30d',
  'integrations.gws.health.tile.headers': 'Email headers · 30d',
  'integrations.gws.health.runs.eyebrow': 'Sync runs · last 24 hours',
  'integrations.gws.health.recent.eyebrow': 'Recent sync runs',
  'integrations.gws.health.rail.status': 'Status',
  'integrations.gws.health.rail.status.healthy': 'Healthy',
  'integrations.gws.health.rail.status.adapter-pending': 'Connector adapter pending · UI ready',
  'integrations.gws.health.rail.quota': 'API quota',
  'integrations.gws.health.rail.token': 'Token',
  // Health · metric tile placeholder until adapter lands. Shared across
  // the four KPI tiles (users / calendars / events / headers).
  'integrations.gws.health.tile.awaiting': 'Awaiting sync',
  // Runs panel — feed copy + sync-status legend.
  'integrations.gws.health.runs.empty.body': 'Connect Google Workspace to populate the run feed',
  'integrations.gws.health.runs.summary.runs': 'runs',
  'integrations.gws.health.runs.summary.ok': '0 ok',
  'integrations.gws.health.runs.summary.retried': '0 retried',
  'integrations.gws.health.runs.summary.failed': '0 failed',
  'integrations.gws.health.runs.legend.ok': 'OK',
  'integrations.gws.health.runs.legend.warn': 'WARN',
  'integrations.gws.health.runs.legend.error': 'ERROR',
  // Recent runs table — column headers + per-row placeholders until the
  // adapter emits real run data.
  'integrations.gws.health.recent.col.time': 'TIME',
  'integrations.gws.health.recent.col.kind': 'KIND',
  'integrations.gws.health.recent.col.records': 'RECORDS',
  'integrations.gws.health.recent.col.duration': 'DURATION',
  'integrations.gws.health.recent.col.status': 'STATUS',
  'integrations.gws.health.recent.row.directory': 'DIRECTORY',
  'integrations.gws.health.recent.row.calendar': 'CALENDAR',
  'integrations.gws.health.recent.row.gmail': 'GMAIL.META',
  'integrations.gws.health.recent.row.awaiting': 'Awaiting first sync',
  // Quota rail · per-module labels + their initial unknown/limit strings.
  // Limit strings are placeholders until the adapter reports real values.
  'integrations.gws.health.rail.quota.directory': 'directory · daily',
  'integrations.gws.health.rail.quota.directory.limit': '— / 50k',
  'integrations.gws.health.rail.quota.calendar': 'calendar · qpm',
  'integrations.gws.health.rail.quota.calendar.limit': '— / 600',
  'integrations.gws.health.rail.quota.gmail': 'gmail · qpm',
  'integrations.gws.health.rail.quota.gmail.limit': '— / 600',
  'integrations.gws.health.rail.quota.drive': 'drive · daily',
  'integrations.gws.health.rail.quota.drive.limit': '— / 20k',
  // Token rail · refresh schedule + scope-set summary.
  'integrations.gws.health.rail.token.last': 'last refresh',
  'integrations.gws.health.rail.token.next': 'next refresh',
  'integrations.gws.health.rail.token.scope': 'scope set',
  'integrations.gws.health.rail.token.scope.value': '9 scopes · pending',

  // Integration audit log
  'integrations.audit.eyebrow': 'Audit log · every read, every write',
  'integrations.audit.title': 'Show me what the agent touched.',
  'integrations.audit.sub':
    'One row per API call. Click any row to see the request payload, the response digest, and which agent task or user action triggered it.',
  'integrations.audit.col.time': 'TIME',
  'integrations.audit.col.source': 'SOURCE',
  'integrations.audit.col.operation': 'OPERATION',
  'integrations.audit.col.actor': 'ACTOR',
  'integrations.audit.col.records': 'RECORDS',
  'integrations.audit.col.citation': 'CITATION',
  'integrations.audit.col.status': 'STATUS',
  'integrations.audit.empty':
    'No connector activity in the selected window. Once the agent reads from a source, every call lands here.',
  'integrations.audit.empty.title': 'Audit feed warming up',
  'integrations.audit.filter.search': 'Filter by operation, actor, citation…',
  'integrations.audit.filter.all-sources': 'All sources',
  'integrations.audit.filter.all-actors': 'All actors',
  'integrations.audit.filter.agent': 'Agent only',
  'integrations.audit.filter.user': 'User only',
  // Three window labels — the rail eyebrow tracks the active selector.
  'integrations.audit.rail.window.1h': 'Last 1 hour',
  'integrations.audit.rail.window.24h': 'Last 24 hours',
  'integrations.audit.rail.window.7d': 'Last 7 days',
  // Chip-row mirror of the rail window labels. Three chips on top of
  // the table; values intentionally terser than the rail eyebrow ("1h"
  // vs "Last 1 hour") so the chip row stays compact.
  'integrations.audit.filter.window.1h': 'Last 1h',
  'integrations.audit.filter.window.24h': '24h',
  'integrations.audit.filter.window.7d': '7d',
  'integrations.audit.rail.export': 'Export',
  'integrations.audit.rail.export.cta': 'Download CSV · last 7d',
  // Loading / error states + footer.
  'integrations.audit.loading': 'Loading audit feed…',
  'integrations.audit.error':
    'Could not load audit feed. orgAdmin role required to view this page.',
  // Footer "Showing N of M · last <window>" — the window label is
  // resolved separately via `windowFooter` so the three windows map to
  // the longer human form ("1 hour" / "24 hours" / "7 days"). The
  // shell template lives in `footer.shell` with {shown}/{total}/{win}
  // placeholders so translators can rearrange clauses.
  'integrations.audit.footer.shell': 'Showing {shown} of {total} · last {win}',
  'integrations.audit.footer.window.1h': '1 hour',
  'integrations.audit.footer.window.24h': '24 hours',
  'integrations.audit.footer.window.7d': '7 days',
  // Rail sub-labels under the active window eyebrow.
  'integrations.audit.rail.total': 'total calls',
  'integrations.audit.rail.agent': 'agent',
  'integrations.audit.rail.user': 'user-driven',
  'integrations.audit.rail.errors': 'errors',
  // SIEM forwarding callout above the CSV-export button.
  'integrations.audit.rail.siem': 'SIEM forwarding',
  'integrations.audit.rail.siem.unconfigured': 'not yet configured',

  // Permissions / scopes explorer
  'integrations.perms.eyebrow': 'Permissions · what we can see, why, and how often',
  'integrations.perms.title': '9 scopes, 6 required. Every use is logged.',
  'integrations.perms.sub':
    'Each scope group shows what we actually do with it, how many times we’ve used it in the last 30 days, and a sample API call. Optional scopes can be revoked without breaking the integration.',
  'integrations.perms.export': 'Export scope report (PDF)',
  'integrations.perms.revoke': 'Revoke',
  'integrations.perms.calls': '30d calls',
  'integrations.perms.last': 'Last used',
  'integrations.perms.sample': 'Sample request',
  'integrations.perms.pill.required': 'required',
  'integrations.perms.pill.optional': 'optional · granted',
  'integrations.perms.rail.scope-set': 'Scope set',
  // Rail labels under "Scope set" — short keys paired with dynamic
  // values (granted count / required-of-required / etc.).
  'integrations.perms.rail.scope-set.granted': 'granted',
  'integrations.perms.rail.scope-set.required': 'required',
  'integrations.perms.rail.scope-set.optional': 'optional',
  'integrations.perms.rail.scope-set.last-reviewed': 'last reviewed',
  'integrations.perms.rail.scope-set.by': 'by',
  'integrations.perms.rail.review': 'Quarterly review',
  'integrations.perms.rail.review.body':
    'Quarterly re-consent prompts ship with the GWS adapter. The agent will draft a scope diff for you each cycle.',
  'integrations.perms.rail.review.cta': 'Schedule review',
  'integrations.perms.rail.revoke': 'Revoke entirely',
  'integrations.perms.rail.revoke.body':
    'Disconnects all Google Workspace scopes and purges derived data after 30-day retention window.',
  'integrations.perms.rail.revoke.cta': 'Disconnect Workspace',
  // Per-group title + explainer pairs for the six GROUPS in
  // admin-integrations-permissions.tsx. Slugs (directory / calendar /
  // gmail / drive / chat / audit) match the ScopeGroup identity in
  // that file. Each pair is one row in the GROUPS array.
  'integrations.perms.group.directory.title': 'Directory · users, groups, org units',
  'integrations.perms.group.directory.explainer':
    "Builds the org graph. Without this, the agent can't tell Daria from Marcus or which manager owns which team.",
  'integrations.perms.group.calendar.title': 'Calendar · meeting metadata',
  'integrations.perms.group.calendar.explainer':
    'Powers focus-time, cadence and attendance signals. Event bodies, titles, and descriptions are NOT requested — only times, attendees, response status.',
  'integrations.perms.group.gmail.title': 'Gmail · header metadata only',
  'integrations.perms.group.gmail.explainer':
    "Powers the comms graph (who talks to whom, latency, volume). We can't read subjects, bodies, or attachments — Google enforces this at the scope level.",
  'integrations.perms.group.drive.title': 'Drive · activity, not contents',
  'integrations.perms.group.drive.explainer':
    'Reads doc titles, owners, last-modified. File contents stay in Drive.',
  'integrations.perms.group.chat.title': 'Chat · space names only',
  'integrations.perms.group.chat.explainer':
    'Lets the agent know which Chat spaces exist. Messages are never read.',
  'integrations.perms.group.audit.title': 'Workspace audit · compliance forwarding',
  'integrations.perms.group.audit.explainer':
    'Forwards your Workspace audit log to our SIEM stream so compliance has one timeline.',

  // Sidebar entry
  'sidebar.integrations': 'Integrations',
  'sidebar.approvals': 'Approvals',
  // Design-pass wave (May 2026) — sidebar entries for the new
  // memory / decisions / notes-timeline / overnight retro / flight-plan
  // / near-misses surfaces. Global `/search` is not in the sidebar —
  // the ⌘K command palette is the canonical entry point.
  'sidebar.memory': 'Memory feed',
  'sidebar.decisions': 'Decisions',
  'sidebar.notesTimeline': 'Notes timeline',
  'sidebar.overnightRetro': 'Overnight retro',
  'sidebar.flightPlan': 'Flight plan',
  // Issue #173 PR-B Surface 3 — agent-side flight plan stub. The label
  // differentiates from `sidebar.flightPlan` (the approvals-side grid)
  // because both rows live in the rail.
  'sidebar.agentFlightPlan': 'Agent plan',
  'sidebar.nearMisses': 'Near-misses',

  // Two-tier sidebar (May 2026 design pass): section labels for the
  // contextual panel headers + icon-rail tooltips.
  'sidebar.section.home': 'Home',
  'sidebar.section.operate': 'Operate',
  'sidebar.section.inbox': 'Inbox',
  'sidebar.section.knowledge': 'Knowledge',
  'sidebar.section.trust': 'Trust',
  'sidebar.section.settings': 'Settings',
  'sidebar.personalization': 'Personalization',
  'sidebar.agentSettings': 'Agent behavior',

  // Approvals queue (Phase 4 / Epic #123)
  'approvals.title': 'Approvals',
  'approvals.sub':
    'Every outbound action the agent has drafted. Approve, edit, or reject — auto-approved categories run silently in the background.',
  'approvals.empty.title': 'Nothing waiting on you.',
  'approvals.empty.body':
    "The agent will queue drafts here when something needs your call. Until then, you're clear.",
  'approvals.bulk.cta': 'Approve all routine',
  'approvals.bulk.disabled': 'No routine drafts to approve',
  // Plural-aware bulk-approve label — keep `approvals.bulk.confirm`
  // for back-compat with any consumer that has not migrated yet, but
  // the page picks `.one` / `.other` based on `routineDrafts.length`.
  'approvals.bulk.confirm': 'Approve {count} routine drafts',
  'approvals.bulk.confirm.one': 'Approve {count} routine draft',
  'approvals.bulk.confirm.other': 'Approve {count} routine drafts',
  'approvals.row.approve': 'Approve & send',
  'approvals.row.edit': 'Edit',
  'approvals.row.reject': 'Reject',
  'approvals.row.toLabel': 'To',
  'approvals.row.channelLabel': 'via',
  'approvals.row.routinenessRoutine': 'Routine',
  'approvals.row.routinenessReviewRequired': 'Review',
  'approvals.slideOver.eyebrow': 'Approvals · edit draft',
  'approvals.slideOver.title': 'Review the draft',
  'approvals.slideOver.subjectLabel': 'Subject',
  'approvals.slideOver.bodyLabel': 'Body',
  'approvals.slideOver.saveCta': 'Save & approve',
  'approvals.slideOver.cancelCta': 'Cancel',
  'approvals.slideOver.approveCta': 'Approve as-is',
  'approvals.rail.queueToday': "Today's queue",
  'approvals.rail.routineCount': 'Routine',
  'approvals.rail.reviewCount': 'Review-required',
  'approvals.rail.signedToday': 'Signed today',
  'approvals.error.loadFailed': "Couldn't load the approvals queue. Try again.",
  'approvals.error.saveFailed': "Couldn't save your decision. Try again.",
  // Partial-success copy for bulk-approve. Renders when *some* of the
  // routine drafts saved and others didn't — the audit log carries the
  // per-draft reason; the toast just surfaces the headline ratio.
  'approvals.error.partialSuccess':
    'Approved {success} of {total} drafts ({failed} failed — see audit log).',
  'sidebar.rules': 'Rules',

  // v7 Phase 5 (#123) — Rules & automations surface (`/rules`).
  // Operator-defined "When X → Then Y" rules grouped into four
  // categories. Admin-only. Card per category; each card has its own
  // header copy + empty-state subline.
  'rules.eyebrow': 'Settings · Trust',
  'rules.title': 'Rules & automations',
  'rules.sub':
    'Tell the agent what to do automatically. Each rule reads as "When X → then Y" and can be paused or edited.',
  'rules.empty.title': 'No rules yet.',
  'rules.empty.body':
    'Start by adding a rule for the things you want the agent to handle without asking — Monday briefing, critical alert routing, auto-drafts, escalation.',
  'rules.newRule.cta': 'New rule',
  'rules.category.briefing-cadence.title': 'Briefing cadence',
  'rules.category.briefing-cadence.sub':
    'When and how the agent assembles + delivers your daily / weekly briefings.',
  'rules.category.briefing-cadence.empty':
    'No briefing-cadence rules yet — add one to lock in your morning rhythm.',
  'rules.category.alert-routing.title': 'Alert routing',
  'rules.category.alert-routing.sub':
    'Which signals page you immediately versus roll up into the next briefing.',
  'rules.category.alert-routing.empty':
    'No alert-routing rules yet — add one so critical signals reach the right channel.',
  'rules.category.auto-drafts.title': 'Auto-drafts',
  'rules.category.auto-drafts.sub':
    'Replies, recaps, and outreach the agent drafts on your behalf — auto-send or queue for review.',
  'rules.category.auto-drafts.empty':
    'No auto-draft rules yet — add one to let the agent ghostwrite the routine.',
  'rules.category.escalation.title': 'Escalation',
  'rules.category.escalation.sub':
    'When the agent should interrupt you instead of waiting for the next briefing.',
  'rules.category.escalation.empty':
    'No escalation rules yet — add one so the agent knows what is worth your attention right now.',
  'rules.row.rename': 'Rename rule',
  'rules.row.enable': 'Enable rule',
  'rules.row.disable': 'Disable rule',
  'rules.row.edit': 'Edit rule',
  'rules.row.delete': 'Delete rule',
  // Per-rule aria-labels: include the rule's name so screen-reader
  // users can tell which row a control belongs to. `{name}` is the
  // visible rule name from `AutomationRule.name`.
  'rules.row.enable.aria': 'Enable rule "{name}"',
  'rules.row.disable.aria': 'Disable rule "{name}"',
  'rules.row.edit.aria': 'Edit rule "{name}"',
  'rules.row.delete.aria': 'Delete rule "{name}"',
  'rules.row.when': 'When',
  'rules.row.then': 'then',
  'rules.row.savingLabel': 'Saving…',
  'rules.row.savingFailedLabel': 'Couldn’t save. Try again.',
  'rules.loadingLabel': 'Loading rules…',
  'rules.editor.eyebrow': 'Rule',
  'rules.editor.titleCreate': 'New rule',
  'rules.editor.titleEdit': 'Edit rule',
  'rules.editor.categoryLabel': 'Category',
  'rules.editor.nameLabel': 'Name',
  'rules.editor.whenLabel': 'When',
  'rules.editor.thenLabel': 'Then',
  'rules.editor.enabledLabel': 'Enabled',
  'rules.editor.saveCta': 'Save rule',
  'rules.editor.cancelCta': 'Cancel',
  'rules.error.loadFailed': 'Couldn’t load rules.',
  'rules.error.saveFailed': 'Couldn’t save rule.',
  'rules.error.deleteFailed': 'Couldn’t delete rule.',
  // Distinct error-state copy (vs the toast `loadFailed` copy above) —
  // surfaces when the rules list fails to load and we want a dedicated
  // banner with a retry CTA instead of falling back to the zero-rules
  // empty state.
  'rules.error.title': "We couldn't load your rules.",
  'rules.error.retryCta': 'Retry',
  // Per-category "+" button aria-label — `{category}` is the localized
  // category title so the four buttons are distinguishable.
  'rules.newRule.ariaLabel': 'New {category} rule',

  // Quarterly Rocks board (epic #3)
  'rocks.eyebrow': 'Surface · rocks',
  'rocks.title': 'Quarterly Rocks',
  'rocks.sub':
    "Each leader's 3–7 priorities for the next 90 days. Status tracked weekly; the agent writes the update on each card from connector activity.",
  'rocks.quarter.label': 'Quarter',
  'rocks.new': 'New rock',
  'rocks.empty.title': 'No rocks for this quarter yet.',
  'rocks.empty.body':
    'Add the first 3–7 priorities the leadership team is committing to. Each rock has one owner and a hard end-of-quarter deadline.',
  'rocks.column.on-track': 'On track',
  'rocks.column.off-track': 'Off track',
  'rocks.column.done': 'Done',
  'rocks.column.not-done': 'Not done',
  'rocks.action.edit': 'Edit',
  'rocks.action.delete': 'Delete',
  'rocks.action.cancel': 'Cancel',
  'rocks.action.save': 'Save',
  'rocks.action.set-on-track': 'Set on track',
  'rocks.action.set-off-track': 'Set off track',
  'rocks.action.set-done': 'Mark done',
  'rocks.action.set-not-done': 'Mark not done',
  'rocks.action.unassigned': 'Unassigned',
  'rocks.action.assign': 'Assign owner',
  'rocks.action.sync-update': 'Refresh agent update',
  'rocks.form.title.label': 'Rock',
  'rocks.form.title.placeholder': 'What are you committing to ship this quarter?',
  'rocks.form.description.label': 'Description (optional)',
  'rocks.form.description.placeholder':
    'Why this matters; what done looks like; any context the agent should weave into the weekly update.',
  'rocks.form.owner.label': 'Owner',
  'rocks.form.due.label': 'Due',
  'rocks.form.quarter.label': 'Quarter',
  'rocks.form.status.label': 'Status',
  'rocks.form.milestones.label': 'Milestones',
  'rocks.form.milestones.add': 'Add milestone',
  'rocks.form.milestones.remove': 'Remove',
  'rocks.form.milestones.placeholder': 'Smaller checkpoint…',
  'rocks.form.milestones.empty': 'No milestones yet.',
  'rocks.created': 'Rock created',
  'rocks.updated': 'Rock saved',
  'rocks.deleted': 'Rock deleted',
  'rocks.delete.confirm': 'Delete this rock? This cannot be undone.',
  'rocks.error.load': 'Could not load rocks.',
  'rocks.error.save': 'Could not save rock.',
  'rocks.error.delete': 'Could not delete rock.',
  'rocks.error.forbidden': 'You can only edit a rock you own — ask an admin to assign it to you.',
  'rocks.agent.empty':
    'No agent update yet — the agent will synthesize one from connector activity once sources are wired.',
  'rocks.agent.sync.deferred': 'Agent update synthesis lands with the connector pipeline.',

  // Scorecard — weekly measurables. KpiTile grid + record-this-week
  // form + drill-in modal. Server computes delta + status + spark; UI
  // surfaces the derived fields and lets the team capture this week's
  // value without leaving the page.
  'scorecard.title': 'Weekly Scorecard',
  'scorecard.eyebrow': 'Weekly · measurables',
  'scorecard.sub':
    'The numbers that predict the business. Owner, goal, color band on miss, 13-week trend.',
  'scorecard.empty.body':
    'No measurables yet. An admin can seed the default scorecard from the QA fixtures or add rows manually.',
  'scorecard.error.load': 'Could not load scorecard.',
  'scorecard.grid.aria': 'Scorecard rows',
  'scorecard.tile.no-value': '—',
  'scorecard.tile.no-data': 'No data yet',
  'scorecard.tile.vs-goal': 'vs goal',
  'scorecard.record.heading': 'Record this week',
  'scorecard.record.section': 'Record this week',
  'scorecard.record.placeholder': 'value',
  'scorecard.record.submit': 'Save',
  'scorecard.record.aria': 'New value for {title}',
  'scorecard.record.bad-value': 'Enter a number.',
  'scorecard.record.saved': 'Saved',
  'scorecard.record.failed': 'Could not save measurement.',
  'scorecard.drill.eyebrow': 'Trending · last 13 weeks',
  'scorecard.drill.goal': 'Goal',
  'scorecard.drill.col.week': 'Week',
  'scorecard.drill.col.value': 'Value',
  'scorecard.drill.empty': 'No measurements recorded yet.',
  // Designer's tabular layout: chip row + 6-column grid header.
  'scorecard.chip.week': '● W{n} · {range}',
  'scorecard.chip.owner': 'Owner: all',
  'scorecard.chip.color': 'Color: 13w',
  'scorecard.col.measurable': 'Measurable',
  'scorecard.col.this-week': 'This week',
  'scorecard.col.goal': 'Goal',
  'scorecard.col.delta': 'Δ vs goal',
  'scorecard.col.thirteen-week': '13-week',
  'scorecard.col.owner': 'Owner',
  'scorecard.record.inline': 'Record →',

  // Today briefing — proactive AI-drafted hero + supporting cards.
  // (today.eyebrow is defined above with the rest of today.* keys.)
  'today.headline.loading': 'drafting your headline',
  'today.headline.fallback': "Welcome back. The agent is gathering today's signal.",
  // AIHeadline strap labels — distinguishes a Vertex Gemini headline from
  // the deterministic stub the server falls back to when the LLM is
  // unreachable (so the operator can tell synth quality at a glance).
  'today.headline.model.fallback': 'Deterministic stub',
  'today.headline.model.ai': 'AI-drafted',
  'today.scorecard.title': 'Scorecard at a glance',
  'today.scorecard.empty':
    'No scorecard signals yet — the briefing will surface measurables once the connectors are wired.',
  'today.scorecard.connector-pending': 'connector pending',
  'today.cal.title': 'Your day',
  'today.cal.empty':
    "No calendar yet. The Google Calendar connector will populate this strip once it's wired.",
  'today.drafts.title': 'Drafted by the agent · awaiting your call',
  'today.drafts.empty':
    'Nothing in flight. The agent surfaces drafts here when it wants your approval before sending.',
  'today.signals.strap':
    '{active} active · {sinceMidnight} since midnight · {watchlistCrossed} crossed today',

  // Sidebar — agent watching panel (bottom of nav).
  'sidebar.agent.strap': '{sources} sources live · {signals} signals open',
  'sidebar.agent.strap.loading': '—',
  'sidebar.agent.tap-hint': 'Tap to ask.',

  // Right rail — "agent · acting now" block. Latest agent action gets
  // a verb prefix derived from its `kind`.
  'rail.agent.title': 'Agent · acting now',
  'rail.agent.empty': 'No agent activity today.',
  'rail.agent.meta': '{meta} · {when}',
  'rail.agent.verb.drafted': 'Drafting',
  'rail.agent.verb.sent': 'Sent',
  'rail.agent.verb.scheduled': 'Scheduled',
  'rail.agent.verb.watched': 'Watching',
  'rail.agent.verb.proposed': 'Proposed',
  'rail.agent.verb.synthesized': 'Synthesizing',
  'rail.agent.verb.errored': 'Failed syncing',

  // Insights — proactive feed.
  'insights.eyebrow': 'Insights',
  'insights.title': 'Insights',
  'insights.sub':
    "Recent narrative cards from the agent. Each cites the source events. Dismiss what's not useful — the next round calibrates.",
  'insights.empty':
    'No insights yet. The agent will surface narrative cards here once it has enough signal.',
  'insights.error': "Couldn't load insights. Refresh to try again.",
  'insights.filter.all': 'All',
  'insights.filter.critical': 'Critical',
  'insights.filter.watch': 'Watch',
  'insights.filter.ok': 'Ok',
  'insights.filter.status.new': 'New',
  'insights.filter.status.open': 'Open',
  'insights.filter.status.dismissed': 'Dismissed',
  'insights.action.dismiss': 'Dismiss',
  'insights.action.dismissed': 'Dismissed',
  'insights.role.top-concern': 'Top concern',
  'insights.role.recovering': 'Recovering',
  'insights.role.needs-call': 'Needs your call',

  // Internal signals — graph-derived metric movements.
  'signals.eyebrow': 'Signals',
  'signals.title': 'Signals',
  'signals.sub':
    'Threshold crossings, watchlist movements, and metric anomalies pulled from the connectors. Distinct from the outside-news feed.',
  'signals.empty.internal':
    'No internal signals yet — the synthesis pipeline will surface metric movements here once the graph is connected.',
  'signals.error': "Couldn't load signals. Refresh to try again.",
  'signals.filter.all-severity': 'All',
  'signals.filter.info': 'Info',
  'signals.filter.watch': 'Watch',
  'signals.filter.critical': 'Critical',

  // Alerts — severity-tiered subset of signals.
  'alerts.eyebrow': 'Alerts',
  'alerts.title': 'Alerts',
  'alerts.sub':
    'Watch and critical signals only. Triage from here when something needs immediate attention.',
  'alerts.empty': "No alerts. Either nothing's wrong, or no signals are wired yet.",
  'alerts.error': "Couldn't load alerts. Refresh to try again.",
  // Dynamic headline: "{n} item|items[ · critical only].". Two keys so
  // a localizer can pick the right plural form per locale; the suffix
  // ` · critical only` is appended conditionally and lives in its own key.
  'alerts.headline.itemSingular': '{n} item',
  'alerts.headline.itemPlural': '{n} items',
  'alerts.headline.criticalSuffix': ' · critical only',
  // Designer's unified inbox: filter pills + per-row action + right rail digest/delivery.
  'alerts.facet.all': 'All',
  'alerts.facet.alerts': 'Alerts',
  'alerts.facet.signals': 'Signals',
  'alerts.facet.agent': 'Agent',
  'alerts.facet.wins': 'Wins',
  'alerts.facet.empty': 'No items match this filter.',
  'alerts.row.vs': 'vs',
  'alerts.row.open-dossier': 'Open dossier →',
  'alerts.row.review-draft': 'Review draft →',
  'alerts.rail.digest': "Today's digest",
  'alerts.rail.delivered': 'delivered',
  'alerts.rail.critical': 'critical',
  'alerts.rail.watch': 'watch',
  'alerts.rail.delivery': 'Delivery',
  'alerts.delivery.in-app': 'In-app',
  'alerts.delivery.email': 'Email digest · daily',
  'alerts.delivery.slack': 'Slack DM · critical only',
  'alerts.delivery.sms': 'SMS',
  'alerts.delivery.on': 'on',
  'alerts.delivery.off': 'off',

  // Confidence-meter breakdown popover — issue #173 PR-B "confidence as
  // visible". When a ConfidenceMeter is rendered with a `breakdown` prop,
  // the meter becomes a clickable HoverCard trigger; the popover body
  // sources these strings. Keys live alongside the agent-* family because
  // the popover is part of the agent-transparency surface set.
  'agent.confidence.popover.title': 'How confident I am',
  'agent.confidence.popover.unsureLabel': "Why I'm {pct}% unsure",
  'agent.confidence.popover.contradictionsLabel': 'Contradicting sources',
  'agent.confidence.popover.weightsLabel': 'Source weights',
  'agent.confidence.popover.reasoningLabel': 'My reasoning',
  'agent.confidence.popover.noContradictions': 'No contradicting sources.',

  // Agent rehearsal — single-draft focus surface (`/agent/rehearsal`).
  // Reuses the approvals API + PendingDraft schema; the page picks the
  // highest-priority pending draft as the hero and tails the rest into
  // the right-rail queue. Distinct from `/approvals` which is the bulk
  // queue view — rehearsal is the "consider one draft carefully" mode.
  'agent.rehearsal.eyebrow': 'Agent rehearsal · awaiting approval',
  'agent.rehearsal.titleFormat': 'I want to {kind}.',
  'agent.rehearsal.titleFallback': 'I have a draft for you.',
  'agent.rehearsal.subFormat':
    "I've prepared a draft — review it and approve, edit, or discard. Routine drafts auto-approve in {seconds}s if you don't act.",
  'agent.rehearsal.subjectLabel': 'Subject',
  'agent.rehearsal.previewLabel': 'Preview · draft',
  'agent.rehearsal.previewBodyLabel': 'Body',
  'agent.rehearsal.previewToLabel': 'To',
  'agent.rehearsal.previewChannelLabel': 'Channel',
  'agent.rehearsal.previewRoutinenessLabel': 'Class',
  'agent.rehearsal.reasoningLabel': "Why I'm proposing this",
  'agent.rehearsal.reasoningFallback':
    'The agent drafted this from your connected sources. Open the linked target in the dossier feed to see the evidence that shaped this proposal.',
  'agent.rehearsal.interpTag': 'INTERP',
  'agent.rehearsal.interpHint': 'This is inference, not direct instruction.',
  'agent.rehearsal.confidenceLabel': 'Confidence',
  'agent.rehearsal.confidenceUnknown': 'Confidence not scored for this draft.',
  'agent.rehearsal.approveCta': 'Approve & send',
  'agent.rehearsal.editCta': 'Edit details',
  'agent.rehearsal.discardCta': 'Discard',
  'agent.rehearsal.autoCancelFormat': '{seconds}s auto-approve · routine drafts only',
  'agent.rehearsal.autoCancelHeld': 'Hold — review-required · waiting on your call',
  'agent.rehearsal.queueLabel': 'Pending your approval',
  'agent.rehearsal.queueEmpty': 'No other drafts queued.',
  'agent.rehearsal.empty.eyebrow': '● Inbox zero',
  'agent.rehearsal.empty.title': 'Inbox zero — the agent has nothing waiting on you.',
  'agent.rehearsal.empty.body':
    "When the agent drafts something that needs your call, it'll surface here. Until then, you're clear.",
  'agent.rehearsal.empty.status': 'Agent watching · will queue the next draft as it lands',
  'agent.rehearsal.error': "Couldn't load the rehearsal queue. Try again.",
  'agent.rehearsal.saveError': "Couldn't save your decision. Try again.",
  'agent.rehearsal.loading': 'Loading the next draft…',
  'agent.rehearsal.disposition.approved': 'Approved · the agent will send and log it to /agent.',
  'agent.rehearsal.disposition.edited':
    'Edit saved · the agent will send with your changes and log it to /agent.',
  'agent.rehearsal.disposition.rejected':
    'Discarded · the agent will not send and the decision is in /admin/audit.',
  'agent.rehearsal.editSlideOver.eyebrow': 'Rehearsal · edit draft',
  'agent.rehearsal.editSlideOver.title': 'Edit before approving',
  'agent.rehearsal.editSlideOver.bodyLabel': 'Body',
  'agent.rehearsal.editSlideOver.saveCta': 'Save & approve',
  'agent.rehearsal.editSlideOver.cancelCta': 'Cancel',

  // Agent log — every action the agent took.
  'agent.eyebrow': 'Agent · log',
  'agent.title': 'Agent log',
  'agent.sub':
    'Everything the agent did — drafted, sent, scheduled, watched, proposed. Click a row to see the source.',
  'agent.empty':
    'No agent actions yet. Once the synthesis pipeline runs, the actions show up here in real time.',
  'agent.error': "Couldn't load the agent log. Refresh to try again.",
  'agent.filter.today': 'Today',
  'agent.filter.7d': 'Last 7 days',
  'agent.filter.30d': 'Last 30 days',
  'agent.filter.all': 'All',
  'agent.actor.agent': 'Agent',
  'agent.actor.scheduler': 'Scheduler',

  'bookmarks.error.add': 'Could not save bookmark.',
  'bookmarks.error.remove': 'Could not remove bookmark.',
  'bookmarks.eyebrow': 'Saved · {n} bookmarks',
  'bookmarks.title': 'Your knowledge graph.',
  'bookmarks.sub':
    "Anything you bookmark — a customer, a person, a topic, a product — becomes a living dossier. The agent watches it, traces its connections, and surfaces the context next time it's relevant.",
  'bookmarks.view.list': '☰ List',
  'bookmarks.view.graph': '◇ Graph',
  'bookmarks.loading': 'Loading bookmarks…',
  'bookmarks.empty.eyebrow': 'Bookmarks',
  'bookmarks.empty.title': 'No bookmarks yet',
  'bookmarks.empty.body':
    'Open a dossier, person, topic, or product, and tap the ☆ in its header. The agent will start watching it and surface signals as they arrive.',
  'bookmarks.filter.all': 'All',
  'bookmarks.filter.no-match': 'No bookmarks match the current filter.',
  'bookmarks.drawer.remove': 'Remove bookmark',
  'bookmarks.drawer.agents-read': "Agent's read",
  'bookmarks.drawer.agents-read.body':
    "The agent will start drafting a fresh read on every briefing. Until enough signal accrues, this drawer surfaces the bookmark's tags + links to its source dossier.",
  'bookmarks.drawer.watch-status': 'Watch status',
  'bookmarks.drawer.agent-watching': 'Agent watching',
  'bookmarks.drawer.connections': 'Connections',
  'bookmarks.drawer.connections.body':
    'Connections will populate once the cross-tool graph (epic #9) starts indexing this entity. Synthetic edges are visible in the Graph view today.',

  // Admin · People — designer's "who has access to what" directory.
  'admin.people.eyebrow': 'People · {n} in this org',
  'admin.people.title': 'Who has access to what.',
  'admin.people.sub':
    'Owners attached to scorecard metrics, rocks, and watchlist entries. Invites and role changes are logged to the audit feed.',
  'admin.people.search.placeholder': 'Search name, role, team…',
  'admin.people.filter.all': 'All',
  'admin.people.filter.admins': 'Admins',
  'admin.people.filter.members': 'Members',
  'admin.people.col.person': 'Person',
  'admin.people.col.email': 'Email',
  'admin.people.col.role': 'Role',
  'admin.people.col.last-active': 'Last active',
  'admin.people.col.owns': 'Owns',
  'admin.people.never': 'Never',
  'admin.people.loading': 'Loading members…',
  'admin.people.error': 'Could not load members.',
  'admin.people.empty.title': 'No members yet',
  'admin.people.empty.body':
    "Invite teammates from /settings/org/members. Once they accept, they'll show up here.",
  'admin.people.no-match.title': 'No matches',
  'admin.people.no-match.body':
    'No members match the current search or role filter. Clear the filter or search to see everyone.',
  'admin.people.rail.shape': 'Org shape',
  'admin.people.rail.people': 'people',
  'admin.people.rail.admins': 'admins',
  'admin.people.rail.members': 'members',
  'admin.people.rail.unresolved': 'unresolved',

  // ── Redesign v6 Phase F · overlays + states ─────────────────────
  // §11 artboards in `/tmp/design-leanopx-v6/leanopx/project/screens7.jsx`.

  // Topbar bell popover (`<NotificationsDropdown>` + `<NotifDropdown>`).
  'topbar.notifications': 'Notifications',
  'notifications.title': 'Alerts',
  'notifications.new-suffix': 'new',
  'notifications.mark-all-read': 'Mark all read',
  'notifications.empty': 'You’re caught up.',
  'notifications.see-all': 'Open inbox →',

  // Full inbox page (`/notifications` — `NotificationsPage`).
  'notifications.inbox.eyebrow': 'Inbox',
  'notifications.inbox.title': 'Notifications',
  'notifications.inbox.sub':
    'Everything the agent thinks you should see — alerts, signals, drafts awaiting your call, and todo state flips.',
  'notifications.inbox.empty-title': 'No notifications yet',
  'notifications.inbox.empty-body':
    'The agent will surface alerts, drafts, and signal crossings here as they happen. Backend wiring lands with epic #11.',

  // Topbar avatar dropdown (`<UserMenu>`).
  'menu.personalization': 'Personalization',
  'menu.keyboard-shortcuts': 'Keyboard shortcuts',
  'menu.whats-new': 'What’s new',
  'menu.invite-teammates': 'Invite teammates',

  // Sidebar org-chip (`<OrgSwitcherChip>`). `org.switch` lives above
  // — reused here for the chip's switch-affordance aria-label.
  'org.active': 'Active organization',
  'org.your-orgs': 'Your orgs',
  'org.create-new': '+ Create new org',

  // Issue slide-over (`<IssueDetailSlideover>`).
  'issue.owner': 'Owner',
  'issue.linked-rock': 'Linked rock',
  'issue.source': 'Source',
  'issue.votes': 'Votes',
  'issue.added': 'Added',
  'issue.context': 'Context · agent synthesis',
  'issue.proposed-actions': 'Proposed actions',
  'issue.convert-to-todo': 'Convert to todo',
  'issue.move-to-discussing': 'Move to discussing →',
  'issue.solve': 'Solve',

  // Full-screen search results (`<SearchResultsOverlay>`).
  'search.title': 'Search results',
  'search.description':
    'Filtered view of all matches across people, dossiers, notes, signals, decisions, rocks, and playbooks.',
  'search.filters': 'Filters',
  'search.results-count': 'Results · {n}',
  'search.empty': 'No matches in this filter. Try the “all” pivot.',
  'search.kind.all': 'All',
  'search.kind.people': 'People',
  'search.kind.dossiers': 'Dossiers',
  'search.kind.notes': 'Notes',
  'search.kind.signals': 'Signals',
  'search.kind.decisions': 'Decisions',
  'search.kind.rocks': 'Rocks',
  'search.kind.playbooks': 'Playbooks',

  // Global search page (`/search`) — `<SearchPage>` + filter rail +
  // agent-answer card + related-dossiers rail. Distinct namespace from
  // the overlay (`search.title` / `search.kind.*`) above; those keys
  // are shared with the overlay component while these keys back the
  // full-page surface.
  'search.page.input.placeholder': 'Ask anything across your work…',
  'search.page.input.aria': 'Search query',
  'search.page.input.glyph-aria': 'Command palette shortcut',
  'search.page.stat': '{n} results · {ms}',
  'search.page.stat.empty': 'No results yet',
  'search.page.stat.coming-soon': 'Search wiring pending',
  'search.page.eyebrow': 'Search',
  'search.page.title.empty': 'Type a question and press Enter.',
  'search.page.title.results': 'Results for "{q}"',
  'search.page.results.heading': 'Top results',
  'search.page.results.heading-count': 'Top results · {n}',
  'search.page.results.empty.title': 'No search backend yet',
  'search.page.results.empty.body':
    "I don't have search wired yet — try /ask instead. The agent will read across your connected tools and synthesize an answer with citations.",
  'search.page.results.empty.cta': 'Open Ask →',
  'search.page.results.load-more': 'Load more results',
  // Filter rail — `<SearchFilterRail>`.
  'search.filter.label': 'Filter',
  'search.filter.type': 'Type',
  'search.filter.source': 'Source',
  'search.filter.time': 'Time range',
  'search.filter.confidence': 'Confidence',
  'search.filter.type.all': 'Everything',
  'search.filter.type.insights': 'Insights',
  'search.filter.type.dossiers': 'Dossiers',
  'search.filter.type.notes': 'Notes',
  'search.filter.type.threads': 'Threads',
  'search.filter.type.decisions': 'Decisions',
  'search.filter.type.files': 'Files',
  'search.filter.source.all': 'All sources',
  'search.filter.source.salesforce': 'Salesforce',
  'search.filter.source.slack': 'Slack',
  'search.filter.source.gmail': 'Gmail',
  'search.filter.source.notion': 'Notion',
  'search.filter.source.drive': 'Drive',
  'search.filter.source.github': 'GitHub',
  'search.filter.time.today': 'Today',
  'search.filter.time.7d': 'Past 7 days',
  'search.filter.time.30d': 'Past 30 days',
  'search.filter.time.quarter': 'Past quarter',
  'search.filter.time.year': 'Past year',
  'search.filter.time.all': 'All time',
  'search.filter.confidence.high': 'High (>85%)',
  'search.filter.confidence.medium': 'Medium (60-85%)',
  'search.filter.confidence.low': 'Low (<60%)',
  'search.filter.confidence.all': 'Show all',
  // Agent answer card — `<AgentAnswerCard>`.
  'search.answer.eyebrow': "Agent's answer",
  'search.answer.confidence-suffix': 'confidence',
  'search.answer.sources-cited': '{n} sources cited',
  'search.answer.cta.reasoning': 'See the full reasoning →',
  'search.answer.cta.save': 'Save this answer',
  'search.answer.cta.refine': 'Refine question',
  'search.answer.empty.title': 'Agent answer',
  'search.answer.empty.body':
    "I don't have search wired yet — try /ask instead and the agent will reason across your connected tools.",
  'search.answer.citation-aria': 'Open citation {n}',
  // Right rail — `<RelatedDossiersRail>`.
  'search.rail.related': 'Related dossiers',
  'search.rail.related.empty': 'Related dossiers will appear here once search is live.',
  'search.rail.refine': 'Refine question',
  'search.rail.refine.empty': 'Refinement prompts appear here once search is live.',
  'search.refine.moreFrom': 'Show me more from {label}',
  // Loading / error / no-results — `<SearchPage>` query branches.
  'search.page.loading': 'Reading the graph…',
  'search.page.error.title': 'Search failed',
  'search.page.error.body':
    'Something went wrong on the server. Try the query again, or open /ask if it keeps failing.',
  'search.page.error.retry': 'Retry',
  'search.page.no-results.title': 'No matches for that query',
  'search.page.no-results.body':
    'Try refining the query or widening the filter facets. The agent only reads sources you have connected.',
  'search.page.results.kind.insight': 'Insight',
  'search.page.results.kind.dossier': 'Dossier',
  'search.page.results.kind.note': 'Note',
  'search.page.results.kind.audit-event': 'Agent action',
  'search.page.results.kind.decision': 'Decision',
  'search.page.results.kind.file': 'File',
  'search.page.results.took': '{n} results · {ms}ms',

  // Connector connect modal (`<ConnectorModal>`).
  'connector.connect': 'Connect {vendor}',
  'connector.continue': 'Continue to {vendor} →',
  'connector.scope-on': '● ON',
  'connector.scope-off': '○ OFF',
  // `common.cancel` lives above — `common.close` is new.
  'common.close': 'Close',

  // Personalization page (`<DiscoveryPage>` / renamed surface).
  'personalization.eyebrow': 'Personalization',
  'personalization.title': 'How the agent reads you.',
  'personalization.sub':
    'Everything below is inferred from your behavior. Edit anything that’s wrong — the agent recalibrates within a session.',
  'personalization.job.label': 'What I think your job is',
  'personalization.job.empty':
    'A one-paragraph job description renders here once the synthesis pipeline has enough behavioral signal — the agent doesn’t guess.',
  'personalization.decide.label': 'How you decide',
  'personalization.weight.evidence': 'Evidence weight',
  'personalization.weight.evidence-hint': 'data > intuition',
  'personalization.weight.speed': 'Speed bias',
  'personalization.weight.speed-hint': 'ship > polish',
  'personalization.weight.conflict': 'Conflict tolerance',
  'personalization.weight.conflict-hint': 'consensus-leaning',
  'personalization.focus.label': 'Things you care about',
  'personalization.focus.add': 'Add focus topic',
  'personalization.focus.add-disabled':
    'Editing focus topics lights up once the personalization synthesis pipeline lands.',
  'personalization.focus.empty':
    'Focus topics populate from your top priorities once you complete onboarding.',
  'personalization.ignore.label': 'Things you’ve asked me to ignore',
  'personalization.ignore.add': 'Add an ignore-list item',
  'personalization.ignore.add-disabled':
    'Editing the ignore list lights up once the personalization synthesis pipeline lands.',
  'personalization.ignore.empty':
    'The agent keeps an explicit list of patterns you’ve told it to silence — empty until you mute your first signal.',

  // Global keyboard shortcuts cheatsheet — opened by `?` from anywhere in
  // the portal (logged-in surfaces only). Categories follow the v7
  // designer reference in `docs/designs/v7/project/screens-missing.jsx`.
  'shortcuts.eyebrow': 'Keyboard shortcuts',
  'shortcuts.title': 'Move LeanOpx without touching the mouse.',
  'shortcuts.footer': 'Press ? any time to bring this up.',
  'shortcuts.dismiss': 'Got it',
  'shortcuts.close': 'Close',
  'shortcuts.open.aria': 'Show keyboard shortcuts',

  // Category headers
  'shortcuts.category.navigation': 'Navigation',
  'shortcuts.category.inbox': 'Inbox',
  'shortcuts.category.drafts': 'Drafts',
  'shortcuts.category.editing': 'Editing',

  // Navigation
  'shortcuts.nav.briefing.keys': 'g b',
  'shortcuts.nav.briefing.label': 'Go to Briefing',
  'shortcuts.nav.ask.keys': 'g a',
  'shortcuts.nav.ask.label': 'Go to Ask',
  'shortcuts.nav.dossiers.keys': 'g d',
  'shortcuts.nav.dossiers.label': 'Go to Dossiers',
  'shortcuts.nav.scorecard.keys': 'g s',
  'shortcuts.nav.scorecard.label': 'Go to Scorecard',
  'shortcuts.nav.rocks.keys': 'g r',
  'shortcuts.nav.rocks.label': 'Go to Rocks',
  'shortcuts.nav.issues.keys': 'g i',
  'shortcuts.nav.issues.label': 'Go to Issues',

  // Inbox
  'shortcuts.inbox.unread.keys': 'u',
  'shortcuts.inbox.unread.label': 'Toggle unread',
  'shortcuts.inbox.archive.keys': 'e',
  'shortcuts.inbox.archive.label': 'Archive',
  'shortcuts.inbox.cheatsheet.keys': '?',
  'shortcuts.inbox.cheatsheet.label': 'Show this overlay',
  'shortcuts.inbox.esc.keys': 'Esc',
  'shortcuts.inbox.esc.label': 'Dismiss any overlay',

  // Drafts
  'shortcuts.drafts.approve.keys': 'Cmd+Enter',
  'shortcuts.drafts.approve.label': 'Approve',
  'shortcuts.drafts.edit.keys': 'Cmd+E',
  'shortcuts.drafts.edit.label': 'Edit',
  'shortcuts.drafts.reject.keys': 'Cmd+Shift+R',
  'shortcuts.drafts.reject.label': 'Reject',

  // Editing
  'shortcuts.editing.bold.keys': 'Cmd+B',
  'shortcuts.editing.bold.label': 'Bold',
  'shortcuts.editing.italic.keys': 'Cmd+I',
  'shortcuts.editing.italic.label': 'Italic',
  'shortcuts.editing.link.keys': 'Cmd+K',
  'shortcuts.editing.link.label': 'Link',
  'shortcuts.editing.undo.keys': 'Cmd+Z',
  'shortcuts.editing.undo.label': 'Undo',

  // Topbar — mobile/narrow-viewport sidebar reveal added with the v7
  // foundation. Same hamburger button surfaces on every responsive
  // breakpoint below 1180px.
  'topbar.menu.open': 'Open menu',
  'topbar.menu.close': 'Close menu',
  'topbar.search.placeholder': 'Ask anything…',
  'topbar.search.ariaLabel': 'Open command palette',
  'topbar.notifications.ariaLabel': 'Notifications',
  'topbar.help.ariaLabel': 'Help',

  // Shared design-system primitives — the v7 foundation adds <SlideOver>,
  // <EntityLink>, <InlineEdit>. The primitives accept these labels as
  // required props (the design-system package can't read STRINGS
  // directly), so call sites in apps/web wire them through.
  'ds.slideOver.close': 'Close panel',
  'ds.inlineEdit.saving': 'Saving…',
  'ds.inlineEdit.savingFailed': 'Couldn’t save. Please try again.',

  // Outbound briefing preview — Phase 7 of Epic #123. `/outbound/preview`
  // renders the day's briefing through three channel renderers (email /
  // Slack DM / iOS push). Pure presentation; delivery wiring deferred.
  // Today-page link that opens the preview surface.
  'today.outbound.previewCta': 'Preview delivery →',

  // Page chrome.
  'outbound.eyebrow': 'Outbound · how the briefing arrives',
  'outbound.title': 'The same briefing, three places.',
  'outbound.sub':
    'Most operators read the briefing in email or Slack, not in LeanOpx. Preview how today’s briefing will arrive on each surface.',
  'outbound.backCta': 'Back to Today',

  // Email preview.
  'outbound.email.eyebrow': 'Delivery · Email',
  'outbound.email.from': 'From:',
  'outbound.email.to': 'To:',
  'outbound.email.subject': 'Subject:',
  'outbound.email.subject.fallback': 'Your morning briefing',
  'outbound.email.date.fallback': 'Today',
  'outbound.email.headline.fallback':
    'The agent is still gathering today’s signal — preview will refresh once the morning sync runs.',
  'outbound.email.intro': 'Three things changed this weekend that move the operating loop.',
  'outbound.email.hero.title.placeholder': 'Hero insight',
  'outbound.email.hero.body.placeholder':
    'The full insight body renders here once today’s synthesis pass completes.',
  'outbound.email.empty.body':
    'No hero insights yet — the agent will fill this section in once the morning sync runs.',
  'outbound.email.cal.summary': '{n} calendar events on deck',
  'outbound.email.drafts.summary': '{n} drafts waiting on your approval',
  'outbound.email.cta.open': 'Open in LeanOpx →',
  'outbound.email.cta.listen': 'Listen (0:58)',
  'outbound.email.cta.cadence': 'Manage cadence',
  'outbound.email.footer.disclaimer':
    'Delivered to your inbox because your cadence is Daily 6:55 AM. Reply to chat with the agent.',
  'outbound.email.send.cta': 'Send via email',
  'outbound.email.send.disabled.tooltip':
    'Delivery wiring lands in a follow-up — see Epic #123 open questions.',

  // Slack preview.
  'outbound.slack.eyebrow': 'Delivery · Slack DM',
  'outbound.slack.timestamp': '6:55 AM',
  'outbound.slack.eyebrow.fallback': 'TODAY',
  'outbound.slack.headline.fallback': 'Morning brief — preview pending.',
  'outbound.slack.intro': 'Here’s what moved overnight — reply in thread to dig in.',
  'outbound.slack.hero.title.placeholder': 'Hero insight',
  'outbound.slack.hero.body.placeholder':
    'Cited body renders here once the morning sync completes.',
  'outbound.slack.empty.body':
    'Nothing flagged this morning — the agent will surface insights here as they cross the watch bar.',
  'outbound.slack.tail': '{cal} events today · {drafts} drafts awaiting',
  'outbound.slack.action.open': 'Open in LeanOpx',
  'outbound.slack.action.listen': 'Listen 0:58',
  'outbound.slack.action.snooze': 'Snooze 1h',
  'outbound.slack.reply.hint':
    'Reply in thread to chat with LeanOpx — it’ll cite back into your graph.',
  'outbound.slack.send.cta': 'Send via Slack',
  'outbound.slack.send.disabled.tooltip':
    'Delivery wiring lands in a follow-up — see Epic #123 open questions.',

  // iOS push preview.
  'outbound.ios.eyebrow': 'Delivery · iOS push notification',
  'outbound.ios.appName': 'LeanOpx',
  'outbound.ios.timestamp': '6:55 AM',
  'outbound.ios.statusbar.time': '6:55',
  'outbound.ios.heading': 'Monday brief · ready',
  'outbound.ios.body.summary': '{n} things on deck · {cal} on your calendar today',
  'outbound.ios.body.empty': 'The agent is still synthesizing — your briefing will arrive shortly.',
  'outbound.ios.notificationGroup': 'LeanOpx · grouped',
  'outbound.ios.groupBody': 'Tap to expand earlier alerts from today.',
  'outbound.ios.send.cta': 'Send via push',
  'outbound.ios.send.disabled.tooltip':
    'Delivery wiring lands in a follow-up — see Epic #123 open questions.',

  // Error banner (non-404 failure from `GET /api/orgs/:o/briefing`).
  // 404 (briefing not synthesized yet) is the empty state below; any
  // other failure (5xx, auth, network) lands here so the operator
  // knows the surface is broken rather than silently empty.
  'outbound.error.title': 'Couldn’t load today’s briefing.',
  'outbound.error.body':
    'The previews need the briefing endpoint to respond — try again in a moment, or check the agent log for what failed.',

  // Empty state (no briefing for today yet).
  'outbound.empty.eyebrow': 'No briefing yet',
  'outbound.empty.title': 'Today’s briefing isn’t ready yet.',
  'outbound.empty.body':
    'Check back after the morning sync — the previews refresh once the agent has stitched the day together.',

  // ── Brief-as-Dossier (issue #166 PR B) ──────────────────────────────
  // `/today` is now a thin redirect/empty-state surface; the real brief
  // reading mode lives at `/briefs/$briefId`. `/briefs` is the archive.
  // Frontend-B owns the detail-page tab copy; this block is the archive
  // + empty-state + outbound-preview shared keys.

  // Overnight retro section (`<OvernightRetro>` on `/today`). Issue #173
  // PR-B Surface 2 — "while you were away" recap above the brief content.
  // Reads agent actions from the last 18h via the existing
  // `GET /api/orgs/:o/agent/actions?since=<iso>` route. Verbs are
  // past-tense one-liners read alongside `<EntityName>`.
  'briefs.overnight.eyebrow': 'OVERNIGHT',
  'briefs.overnight.title': 'While you were away',
  'briefs.overnight.empty': 'Agent had a quiet night. Nothing surfaced since you signed off.',
  'briefs.overnight.timeRange.format': '{from} – {to}',
  'briefs.overnight.seeAll.format': 'See all {n}',
  'briefs.overnight.verb.drafted': 'Drafted',
  'briefs.overnight.verb.sent': 'Sent',
  'briefs.overnight.verb.scheduled': 'Scheduled',
  'briefs.overnight.verb.watched': 'Watching',
  'briefs.overnight.verb.proposed': 'Proposed',
  'briefs.overnight.verb.synthesized': 'Synthesized',
  'briefs.overnight.verb.errored': 'Flagged a failure on',
  // Fallback pill rendered when an agent-inference row has no
  // `draftConfidence`. CLAUDE.md: never fabricate a confidence value;
  // surface the "unknown" state explicitly so the CEO doesn't read a
  // synthesized 0.72 as a real signal.
  'briefs.overnight.confidence.unknown': 'confidence unknown',

  // `/today` empty hero (no-brief-today envelope from `api.briefs.today`).
  'briefs.today.empty.eyebrow': 'No brief today',
  'briefs.today.empty.title': 'Nothing to brief on today.',
  'briefs.today.empty.titleWithName': 'Nothing to brief on today, {firstName}.',
  'briefs.today.empty.body':
    'It’s {date}. The agent watched the day and found nothing material enough to interrupt you for. The next briefing is queued.',
  'briefs.today.empty.nextBriefing': 'Next briefing · {when}',
  'briefs.today.empty.nextBriefing.tomorrow': 'tomorrow at 6:55 AM',
  'briefs.today.empty.nextBriefing.today': 'this morning at 6:55 AM',
  'briefs.today.empty.cta': 'See what I’m watching',
  // Redirect-in-flight placeholder copy.
  'briefs.today.redirecting': 'Opening today’s brief…',

  // `/briefs` archive page.
  'briefs.archive.eyebrow': 'Brief archive',
  'briefs.archive.title': 'Every brief, ever.',
  'briefs.archive.sub':
    'Each row is a brief the agent generated — open one to see what was material, what you decided, and what happened afterward.',
  'briefs.archive.searchPlaceholder': 'Search headlines…',
  'briefs.archive.compareCta': 'Compare two',
  'briefs.archive.compareCta.tooltip': 'Side-by-side compare lands in a follow-up.',
  'briefs.archive.weekGroup': 'Week {weekNum}',
  'briefs.archive.row.acted': '{n} acted',
  'briefs.archive.row.declined': '{n} declined',
  'briefs.archive.row.deferred': '{n} deferred',
  'briefs.archive.row.opened': 'opened · acted',
  'briefs.archive.row.unread': 'unread',
  'briefs.archive.filters.title': 'Filters',
  'briefs.archive.filter.kind.daily': 'Daily',
  'briefs.archive.filter.kind.weekly': 'Weekly',
  'briefs.archive.filter.kind.adHoc': 'Ad-hoc',
  'briefs.archive.filter.kind.meetingPrep': 'Meeting prep',
  'briefs.archive.filter.kind.catchUp': 'Catch-up',
  'briefs.archive.filter.unreadOnly': 'Unread only',
  'briefs.archive.filter.actedOn': 'Acted on',
  'briefs.archive.stats.title': 'Last 90 days',
  'briefs.archive.stat.briefsSent': 'Briefs sent',
  'briefs.archive.stat.actedOn': 'Acted on',
  'briefs.archive.stat.declined': 'Declined',
  'briefs.archive.stat.avgOpen': 'Avg open time',
  'briefs.archive.stat.avgRead': 'Avg read time',
  'briefs.archive.empty.title': 'No briefs yet.',
  'briefs.archive.empty.body':
    'Briefs land here as the agent generates them — your first daily brief shows up the morning after onboarding completes.',
  'briefs.archive.loading': 'Loading briefs…',
  'briefs.archive.error.title': 'Couldn’t load your brief archive.',
  'briefs.archive.error.body': 'Try again in a moment, or check the agent log for what failed.',

  // Outbound preview — empty + error states that lived under
  // `outbound.empty.*` already; this is the no-brief variant that the
  // post-#166 outbound preview falls back to.
  'briefs.outbound.empty.title': 'No brief today.',
  'briefs.outbound.empty.body':
    'The previews refresh as soon as the agent has something material to brief — check `/agent` to see what it’s watching in the meantime.',

  // Permissions matrix (`/permissions`) — Phase 6 of Epic #123 (v7
  // redesign). Read-only RBAC grid; mutate path is a follow-up. The
  // founder-facing copy explicitly notes that editing requires an
  // engineer until the write path ships — see `docs/designs/v7/chats/
  // chat2.md` line 1226.
  'permissions.eyebrow': 'Trust · roles & permissions',
  'permissions.title': 'What each role can do.',
  'permissions.sub':
    'A read-only view of the org’s RBAC policy. Read-only — editing requires an engineer until the mutate path lands. Reach out via #internal-tools.',
  'permissions.matrix.ariaLabel': 'Role and permissions matrix',
  'permissions.matrix.col.capability': 'Capability',
  'permissions.matrix.row.expandAriaLabel': 'Open details for {capability}',

  // Sidebar — Trust section entry for the matrix (admin-only).
  'sidebar.permissions': 'Permissions',

  // Role headers — sourced from `OrgRole.options` so adding a role
  // means one line in `packages/shared/src/org.ts` plus a label here.
  'permissions.role.admin': 'Admin',
  'permissions.role.member': 'Member',

  // Cell vocabulary — graduated access tones. Tone mapping lives in
  // `<AccessPill>`; the labels here are what the operator reads.
  'permissions.grade.full': 'Yes',
  'permissions.grade.self-only': 'Self only',
  'permissions.grade.with-approval': 'w/ approval',
  'permissions.grade.single-dossier': 'Single dossier',
  'permissions.grade.none': 'Denied',

  // 24 capability rows — one per `PermissionCapability.options`. Each
  // gets a short clickable label + a fuller description that surfaces
  // in the slide-over drill-down. Phase 6 keeps descriptions terse
  // and operator-facing; the audit-action registry wiring is a
  // follow-up.
  'permissions.capability.view-briefing.label': 'View daily briefing',
  'permissions.capability.view-briefing.description':
    'Read the Monday-morning briefing and the day-of headline plus three hero insights.',
  'permissions.capability.view-audit.label': 'View audit log',
  'permissions.capability.view-audit.description':
    'Read the cross-tool audit trail of every mutation the agent or a teammate made in this workspace.',
  'permissions.capability.approve-drafts.label': 'Approve / reject agent drafts',
  'permissions.capability.approve-drafts.description':
    'Sign off on (or discard) the agent’s drafted replies, scheduled actions, and outbound messages before they send.',
  'permissions.capability.mutate-rocks.label': 'Create + edit Rocks',
  'permissions.capability.mutate-rocks.description':
    'Add quarterly Rocks, edit their milestones, change owner, and roll status forward each week.',
  'permissions.capability.edit-playbook.label': 'Edit playbook',
  'permissions.capability.edit-playbook.description':
    'Edit the company + department playbooks the agent uses as its reasoning ground truth.',
  'permissions.capability.mutate-scorecard.label': 'Record + edit scorecard',
  'permissions.capability.mutate-scorecard.description':
    'Add measurables, record this-week values, change the 13-week goal line, and re-own metrics.',
  'permissions.capability.invite-members.label': 'Invite teammates',
  'permissions.capability.invite-members.description':
    'Send invitations to new members and re-send pending invites.',
  'permissions.capability.change-roles.label': 'Change roles',
  'permissions.capability.change-roles.description':
    'Promote / demote teammates between Admin and Member, and remove members from the workspace.',
  'permissions.capability.connect-integration.label': 'Connect integration',
  'permissions.capability.connect-integration.description':
    'Connect new connectors (Slack, Gmail, Salesforce, GitHub, Stripe, Google Workspace) to the workspace graph.',
  'permissions.capability.disconnect-integration.label': 'Disconnect integration',
  'permissions.capability.disconnect-integration.description':
    'Disconnect a connected integration and revoke the agent’s access to that source.',
  'permissions.capability.export-audit.label': 'Export audit log',
  'permissions.capability.export-audit.description':
    'Export the workspace audit trail to CSV for compliance or external review.',
  'permissions.capability.mutate-settings.label': 'Edit workspace settings',
  'permissions.capability.mutate-settings.description':
    'Change org-level settings — agent behavior, cadence, security, and display preferences.',
  'permissions.capability.mutate-billing.label': 'Manage billing',
  'permissions.capability.mutate-billing.description':
    'Change the workspace plan, update the payment method, and view past invoices.',
  'permissions.capability.run-onboarding.label': 'Run onboarding wizard',
  'permissions.capability.run-onboarding.description':
    'Walk through the first-run agent-led onboarding flow (identity → company → voice → connect → governance).',
  'permissions.capability.reset-onboarding.label': 'Reset onboarding',
  'permissions.capability.reset-onboarding.description':
    'Re-trigger onboarding for the workspace from scratch. Destructive — wipes the post-onboarding seed.',
  'permissions.capability.mutate-personalization.label': 'Edit personalization',
  'permissions.capability.mutate-personalization.description':
    'Tune the agent’s voice, cadence, autonomy, and the per-user nav order.',
  'permissions.capability.mutate-company-profile.label': 'Edit company profile',
  'permissions.capability.mutate-company-profile.description':
    'Update the workspace’s identity, business context, and governance snapshot.',
  'permissions.capability.mutate-personal-profile.label': 'Edit personal profile',
  'permissions.capability.mutate-personal-profile.description':
    'Update your own name, avatar, role, timezone, and notification preferences.',
  'permissions.capability.view-permissions.label': 'View permissions matrix',
  'permissions.capability.view-permissions.description':
    'See this read-only role-and-capability matrix for the workspace.',
  'permissions.capability.mutate-permissions.label': 'Edit permissions policy',
  'permissions.capability.mutate-permissions.description':
    'Override per-cell access grades for the workspace. (Write path lands in a follow-up.)',
  'permissions.capability.view-rules.label': 'View rules & automations',
  'permissions.capability.view-rules.description':
    'Read the configured Watch / Auto-approve / Auto-draft / Quiet rules and their per-channel pills.',
  'permissions.capability.mutate-rules.label': 'Edit rules & automations',
  'permissions.capability.mutate-rules.description':
    'Add, edit, pause, or delete agent automation rules across the four rule categories.',
  'permissions.capability.override-trust-dial.label': 'Override trust dial',
  'permissions.capability.override-trust-dial.description':
    'Change the per-connector trust weights that influence how the Ask pipeline cites each source.',
  'permissions.capability.receive-outbound-briefing.label': 'Receive outbound briefing',
  'permissions.capability.receive-outbound-briefing.description':
    'Subscribe to the daily outbound briefing email + Slack DM the agent assembles each morning.',

  // Slide-over — the capability drill-down panel.
  'permissions.slideOver.eyebrow': 'Capability detail',
  'permissions.slideOver.descriptionLabel': 'What this gates',
  'permissions.slideOver.surfacesLabel': 'Where it shows up',
  'permissions.slideOver.auditActionsLabel': 'Auditable actions it gates',
  'permissions.slideOver.closeCta': 'Close',
  'permissions.slideOver.followUpNote':
    'Surface and audit-action wiring lands with the mutate-permissions path.',

  // ── Sources trust dial (/admin/connectors/trust, epic #143) ─────────────
  // The per-connector 0..1 trust weights the Ask synthesis consumes.
  // Slider release autosaves via PATCH /preferences/connector-trust.
  'sourcesTrust.backLabel': 'All connectors',
  'sourcesTrust.head.eyebrow': 'Connector trust · org-level',
  'sourcesTrust.head.title': 'How much should the agent believe each source?',
  'sourcesTrust.head.sub':
    'Drag a dial down for noisy sources, up for structured truth. Saved as you release the slider; the Ask pipeline weights citations by these dials on the next question.',
  'sourcesTrust.aside.eyebrow': 'Why this matters',
  'sourcesTrust.aside.body':
    'The agent weighs each source by these dials when it synthesizes. Lower Slack means hearsay-style signals need stronger corroboration to surface.',
  'sourcesTrust.row.weightLabel': 'weight',
  'sourcesTrust.row.ariaTrustWeight': 'Trust weight for {name}',
  'sourcesTrust.saving': 'Saving…',
  'sourcesTrust.saved': 'Saved',
  'sourcesTrust.saveError': "Couldn't save the trust weight. Try again.",

  // ── v7 PR-comment sweep (2026-05-14) ────────────────────────────────────
  // Strings added by the bundled cleanup PR addressing reviewer feedback
  // across PRs #125–#132. Grouped here so the diff is easy to review;
  // ungroup into the relevant section the next time a feature touches
  // these surfaces.
  'profile.reRun.body':
    "Re-run the agent-led onboarding to update what we know about you — your role, focus areas, and the connectors we'll mine for context.",
  'profile.learned.empty.errorBody':
    "We couldn't reach the enrichment service. Try again in a minute, or come back later.",

  // ── Personal profile v7 deferred items (2026-05-15) ─────────────────────
  // Hero, agent preferences ("How LeanOpx shows up for you"), network +
  // working-pattern empty cards, narrative dossier, channels, rocks,
  // and danger zone. Added on `fix/prod-issues`.
  'profile.hero.namePlaceholder': 'Your name',
  'profile.hero.nameAriaLabel': 'Your name',
  'profile.hero.emptyJoin': 'Add your role + company below',

  'profile.agent.eyebrow': 'How LeanOpx shows up for you',
  'profile.agent.sub':
    'Sets the tone of every briefing, when it arrives, and how much the agent can do without asking.',
  'profile.agent.row.voice': 'Voice',
  'profile.agent.voice.coach': 'Coach',
  'profile.agent.voice.analyst': 'Analyst',
  'profile.agent.voice.operator': 'Operator',
  'profile.agent.voice.quiet': 'Quiet',
  'profile.agent.row.cadence': 'Briefing cadence',
  'profile.agent.cadence.daily': 'Every day',
  'profile.agent.cadence.weekdays': 'Weekdays only',
  'profile.agent.cadence.weekly': 'Weekly digest',
  'profile.agent.cadence.on-change': 'Only when something changes',
  'profile.agent.row.autonomy': 'Autonomy',
  'profile.agent.autonomy.suggest-only': 'Draft, never send',
  'profile.agent.autonomy.draft-and-safe-send': 'Auto-send safe categories',
  'profile.agent.autonomy.full-auto': 'Full autonomy where I trust',
  'profile.agent.row.quietHours': 'Quiet hours',
  'profile.agent.quietHours.startAria': 'Quiet hours start time',
  'profile.agent.quietHours.endAria': 'Quiet hours end time',
  'profile.agent.quietHours.separator': 'to',

  'profile.network.eyebrow': 'Your network at work',
  'profile.network.sub':
    'Top correspondents and active channels — synthesized from Gmail, Calendar, and Slack metadata when those connectors are wired.',
  'profile.network.row.correspondents.label': 'Top correspondents',
  'profile.network.row.correspondents.body':
    'Connect Gmail to surface the people you email most often.',
  'profile.network.row.channels.label': 'Active Slack channels',
  'profile.network.row.channels.body':
    'Connect Slack to surface the channels you participate in most.',
  'profile.network.row.meetings.label': 'Most-frequent meetings',
  'profile.network.row.meetings.body':
    'Connect Calendar to surface your most-frequent meeting partners.',
  'profile.network.cta.gmail': 'Connect Gmail',
  'profile.network.cta.slack': 'Connect Slack',
  'profile.network.cta.calendar': 'Connect Calendar',

  'profile.weekRun.eyebrow': 'How you actually run your week',
  'profile.weekRun.sub':
    'Meeting load, focus blocks, response latency — derived from Calendar + Gmail + Slack metadata once connected.',
  'profile.weekRun.row.meetingLoad.label': 'Meeting load',
  'profile.weekRun.row.meetingLoad.body': 'Connect Calendar to track meeting hours per week.',
  'profile.weekRun.row.focusBlocks.label': 'Focus blocks',
  'profile.weekRun.row.focusBlocks.body':
    'Connect Calendar to surface the focus windows you protect.',
  'profile.weekRun.row.responseLatency.label': 'Response latency',
  'profile.weekRun.row.responseLatency.body':
    'Connect Gmail + Slack to track your typical reply window.',
  'profile.weekRun.cta.calendar': 'Connect Calendar',
  'profile.weekRun.cta.gmail': 'Connect Gmail',
  'profile.weekRun.cta.slack': 'Connect Slack',

  'profile.narrative.eyebrow': 'Career & professional context',
  'profile.narrative.sub':
    'A grounded prose dossier the agent maintains from public web sources. Numbered citations resolve to the sources beneath.',
  'profile.narrative.sourcesEyebrow': 'Sources',
  'profile.narrative.empty.title': 'Nothing to write yet.',
  'profile.narrative.empty.body':
    'Add your LinkedIn URL above so the agent has a strong disambiguator for the web search.',

  'profile.learned.row.currentCompanyWebsite.label': 'Current company website',

  'profile.rocks.eyebrow': 'Your Rocks',
  'profile.rocks.sub': 'The quarterly priorities you own this quarter.',
  'profile.rocks.empty.title': 'No Rocks owned yet.',
  'profile.rocks.empty.body':
    'Rocks are the 90-day priorities each leader owns. Head to /rocks to start one.',
  'profile.rocks.empty.cta': 'Open Rocks',
  'profile.rocks.status.on-track': 'On track',
  'profile.rocks.status.off-track': 'Off track',
  'profile.rocks.status.done': 'Done',
  'profile.rocks.status.not-done': 'Not done',

  'profile.channels.eyebrow': 'Where to find you',
  'profile.channels.sub': 'Pick the channels the agent uses to deliver your briefing and alerts.',
  'profile.channels.row.email': 'Email',
  'profile.channels.row.slack': 'Slack',
  'profile.channels.row.sms': 'SMS',
  'profile.channels.row.push': 'iOS push',
  'profile.channels.enable': 'Enable',
  'profile.channels.notWired': 'Not yet wired',

  'profile.danger.eyebrow': 'Danger zone',
  'profile.danger.sub': 'Destructive actions. Confirmation required.',
  'profile.danger.reset.title': 'Reset onboarding answers',
  'profile.danger.reset.body':
    'Wipes the onboarding state for this org. The agent-led wizard re-fires on your next portal load.',
  'profile.danger.reset.cta': 'Reset answers',
  'profile.danger.reset.confirmTitle': 'Reset onboarding for this org?',
  'profile.danger.reset.confirmBody':
    'This clears the onboarding state on the active org so the wizard re-fires. Your told fields and learned facts are untouched.',
  'profile.danger.reset.confirmCta': 'Reset onboarding',
  'profile.danger.delete.title': 'Delete account',
  'profile.danger.delete.body':
    'Marks your account deleted and signs you out everywhere. Your data is retained for admin restore — nothing is hard-deleted.',
  'profile.danger.delete.cta': 'Delete account',
  'profile.danger.delete.confirmTitle': 'Delete your account?',
  'profile.danger.delete.confirmBody':
    'You will be signed out everywhere and unable to sign back in until an admin restores your account. Your data is retained but inaccessible.',
  'profile.danger.delete.confirmCta': 'Delete account',
  'profile.danger.cancel': 'Cancel',
  'profile.danger.working': 'Working…',
  'companyProfile.empty.learned.errorBody':
    "We couldn't reach the enrichment service. Try again in a minute, or come back later.",
  'companyProfile.error.loadTitle': "We couldn't load your company profile.",
  'companyProfile.error.loadBody':
    'Try again in a minute, or refresh the page. The agent will retry on its next sweep.',
  // ---------------------------------------------------------------------------
  // Decisions register (`/decisions`). Surfaces every meaningful call the
  // CEO has made, derived from approvals + agent audit events.
  // ---------------------------------------------------------------------------
  'decisions.eyebrow': 'Decisions · the register',
  'decisions.title': 'Every meaningful call, cited.',
  'decisions.sub':
    "Decisions made + the reasoning + when we'll revisit. Drawn from approvals + Slack threads + meeting notes.",
  'decisions.stat.quarter': 'this quarter',
  'decisions.stat.active': 'still in force',
  'decisions.stat.revisit': 'flagged for revisit',
  'decisions.stat.reversed': 'reversed',
  'decisions.filter.status': 'Status',
  'decisions.filter.decidedBy': 'Decided by',
  'decisions.filter.topic': 'Topic',
  'decisions.filter.confidence': 'Confidence',
  'decisions.filter.all': 'All',
  'decisions.filter.any': 'Any',
  'decisions.status.ACTIVE': 'Active',
  'decisions.status.REVISIT': 'Revisit',
  'decisions.status.REVERSED': 'Reversed',
  'decisions.status.SUPERSEDED': 'Superseded',
  'decisions.confidence.high': 'High (80+)',
  'decisions.confidence.medium': 'Medium (60–80)',
  'decisions.confidence.low': 'Low',
  'decisions.rightRail.byTopic': 'Decisions by topic',
  'decisions.rightRail.upcomingRevisits': 'Up for revisit',
  'decisions.card.rationale': 'Rationale',
  'decisions.card.sourcesCited': '{count} sources cited',
  'decisions.card.confidenceAtDecision': '{pct}% confidence at decision time',
  'decisions.card.revisitBy': 'revisit {when}',
  'decisions.card.seeReceipts': 'see receipts →',
  'decisions.empty.eyebrow': 'Decisions · register building',
  'decisions.empty.title': "I'll log each call as it happens.",
  'decisions.empty.body':
    'I derive decisions from approvals you signed, threads where a call was made, and meeting notes that recorded one. As soon as the first qualifying event lands in your graph, it shows up here with the rationale, sources, and a revisit date.',
  'decisions.empty.status': 'Watching approvals + Slack threads + meeting notes…',
  'decisions.error.loadFailed':
    "We couldn't load the decision register. Try again in a minute, or refresh the page.",
  // Add-decision slide-over (server-side CRUD landed in PR #142).
  'decisions.addCta': 'Log a decision',
  'decisions.editor.eyebrow': 'Decisions · new entry',
  'decisions.editor.title': 'Log a decision',
  'decisions.editor.titleLabel': 'Title',
  'decisions.editor.bodyLabel': 'Framing',
  'decisions.editor.topicLabel': 'Topic',
  'decisions.editor.statusLabel': 'Status',
  'decisions.editor.confidenceLabel': 'Confidence at decision time',
  'decisions.editor.revisitLabel': 'Revisit by',
  'decisions.editor.rationaleLabel': 'Rationale (one bullet per line)',
  'decisions.editor.saveCta': 'Save',
  'decisions.editor.savingCta': 'Saving…',
  'decisions.editor.cancelCta': 'Cancel',
  'decisions.editor.closeAria': 'Close',
  'decisions.editor.error.create': "We couldn't save the decision — try again in a moment.",
  // ---------------------------------------------------------------------------
  // Issues board (`/issues`) — the EOS Identify · Discuss · Solve workflow.
  // Server-mediated CRUD landed in PR for issue #143 bucket 1a.
  // ---------------------------------------------------------------------------
  'issues.eyebrow': 'EOS · Identify · Discuss · Solve',
  'issues.title': "Issues for Tuesday's L10.",
  'issues.sub':
    'The pile we discuss at L10. Drag to reorder by vote, drop into Discussing when picked, into Solved when the room agrees on the action.',
  'issues.addCta': '+ Add issue',
  'issues.voteAriaAdd': 'Vote for this issue',
  'issues.voteAriaRemove': 'Remove your vote',
  'issues.status.Identified': 'Identified',
  'issues.status.Discussing': 'Discussing',
  'issues.status.Solved': 'Solved',
  'issues.column.Identified.meta': '{count} issues',
  'issues.column.Discussing.meta': '{count} issues · this hour',
  'issues.column.Solved.meta': '{count} issues',
  'issues.column.label': '{label} · {count}',
  'issues.source.manual': 'manually added',
  'issues.empty.eyebrow': 'Issues · board building',
  'issues.empty.title': "I'll surface the first one as soon as something lands.",
  'issues.empty.body':
    'Issues come from signals, insights, rocks at risk, briefing items, and notes you flag. Add one by hand to get the room going — or wait for the agent to surface the first qualifying event.',
  'issues.empty.status': 'Watching signals + insights + rocks + briefings…',
  'issues.error.loadFailed':
    "We couldn't load the issues board. Try again in a minute, or refresh the page.",
  'issues.editor.eyebrow': 'Issues · new entry',
  'issues.editor.title': 'Add an issue',
  'issues.editor.titleLabel': 'Title',
  'issues.editor.bodyLabel': 'Framing',
  'issues.editor.statusLabel': 'Status',
  'issues.editor.saveCta': 'Add issue',
  'issues.editor.savingCta': 'Saving…',
  'issues.editor.cancelCta': 'Cancel',
  'issues.editor.closeAria': 'Close',
  'issues.editor.error.create': "We couldn't save the issue — try again in a moment.",
  'issues.rail.thisL10': 'This L10 ({date})',
  'issues.rail.issues': 'issues',
  'issues.rail.solvedLast': 'solved last',
  'issues.rail.rolledOver': 'rolled over',
  'issues.rail.agentSuggests': '◆ Agent suggests',
  'issues.rail.agentBody':
    'Sort the Identified pile by votes — top of the list earns the first L10 minute.',
  // ---------------------------------------------------------------------------
  // Memory feed (`/memory`) — what the agent learned this week. Derives
  // from agent actions (synthesized / watched / proposed) + learned facts.
  // Empty state until the synthesis pipeline produces real entries.
  // ---------------------------------------------------------------------------
  'memory.eyebrow': '● Memory · what I learned this week',
  'memory.title': "Here's what I've added to the graph.",
  'memory.sub':
    '23 new facts, 4 new entities, 11 new connections. 3 I unlearned because you corrected them.',
  'memory.tally.section': 'This week',
  'memory.tally.facts': 'new facts',
  'memory.tally.entities': 'new entities',
  'memory.tally.connections': 'new connections',
  'memory.tally.unlearned': 'facts unlearned',
  'memory.tally.confidence-up': 'confidence raised',
  'memory.tally.confidence-down': 'confidence lowered',
  'memory.tag.new-entity': 'NEW ENTITY',
  'memory.tag.unlearned': 'UNLEARNED',
  'memory.tag.new-fact': 'NEW FACT',
  'memory.tag.new-connection': 'NEW CONNECTION',
  'memory.tag.confidence': 'CONFIDENCE',
  'memory.tag.watch': 'WATCH',
  'memory.see-receipts': 'see receipts →',
  'memory.loading': 'Reading the agent log…',
  'memory.error': "Couldn't load the memory feed. Refresh to try again.",
  'memory.empty.eyebrow': '● Memory · still warming up',
  'memory.empty.title': 'The graph fills as the agent reads.',
  'memory.empty.body':
    'Once the synthesis pipeline runs, every new fact, entity, connection, and correction lands here — with a citation back to the source event the agent learned it from.',
  'memory.empty.status': 'Indexing · ETA after the first connector sync',
  'memory.empty.window': 'Indexed {from} → {to}',
  'memory.day.today': 'TODAY · {base}',

  // ---------------------------------------------------------------------------
  // Agent · overnight retro (`/briefing/overnight`). Monday 9 AM standup of
  // what the agent did while you slept — 2-col [highlights / raw log] grid.
  // ---------------------------------------------------------------------------
  'agent.overnight.eyebrow.format': '● Overnight retro · {weekday}, {month} {day} · 09:00',
  'agent.overnight.title': "Here's what I drafted, caught, watched, and corrected.",
  'agent.overnight.sub.format': 'While you slept: {n} things to surface for you.',
  'agent.overnight.sub.none': 'While you slept: nothing surfaced worth your attention.',
  'agent.overnight.highlights.eyebrow': 'Highlights · what to know about',
  'agent.overnight.log.eyebrow': 'Raw activity · since midnight',
  'agent.overnight.log.cta.format': '{n} actions · view full log →',
  'agent.overnight.log.empty': 'No actions logged overnight.',
  'agent.overnight.see-receipts': 'see receipts →',
  'agent.overnight.tag.drafts': 'DRAFTS',
  'agent.overnight.tag.catch': 'CATCH',
  'agent.overnight.tag.watch': 'WATCH',
  'agent.overnight.tag.correction': 'CORRECTION',
  'agent.overnight.bundle.drafts.title.format': 'Drafted {n} {plural} — waiting on your OK',
  'agent.overnight.bundle.catch.title.format': 'Caught {n} {plural}',
  'agent.overnight.bundle.watch.title.format': '{n} {plural} to keep watching',
  'agent.overnight.bundle.correction.title.format': 'Logged {n} {plural}',
  'agent.overnight.bundle.drafts.body.format': '{previews}',
  'agent.overnight.bundle.catch.body.format': '{previews}',
  'agent.overnight.bundle.watch.body.format': '{previews}',
  'agent.overnight.bundle.correction.body.format': '{previews}',
  'agent.overnight.empty.eyebrow': '● Overnight retro · still warming up',
  'agent.overnight.empty.title': 'Nothing to report from overnight yet.',
  'agent.overnight.empty.body':
    'Once the agent runs against your connected tools, every draft, catch, watch, and correction lands here at 9 AM Monday with a citation back to the source event.',
  'agent.overnight.empty.status': 'Idle · ETA after the first connector sync',
  'agent.overnight.error': "Couldn't load overnight retro. Refresh to try again.",
  'agent.overnight.loading': 'Pulling overnight log…',
  'agent.overnight.preview.more': ' · +{count} more',
  'agent.overnight.verb.drafted': 'Drafted',
  'agent.overnight.verb.sent': 'Sent',
  'agent.overnight.verb.scheduled': 'Scheduled',
  'agent.overnight.verb.watched': 'Watching',
  'agent.overnight.verb.proposed': 'Proposed',
  'agent.overnight.verb.synthesized': 'Synthesized',
  'agent.overnight.verb.errored': 'Errored',
  'agent.overnight.bucket.drafts.singular': 'thing',
  'agent.overnight.bucket.drafts.plural': 'things',
  'agent.overnight.bucket.catch.singular': 'near-miss',
  'agent.overnight.bucket.catch.plural': 'near-misses',
  'agent.overnight.bucket.watch.singular': 'entity',
  'agent.overnight.bucket.watch.plural': 'entities',
  'agent.overnight.bucket.correction.singular': 'correction',
  'agent.overnight.bucket.correction.plural': 'corrections',

  // ---------------------------------------------------------------------------
  // Agent · flight plan (`/approvals/flight-plan`). Forward 5-day grid of
  // scheduled agent intent — color-coded by risk.
  // ---------------------------------------------------------------------------
  'agent.flightPlan.eyebrow': '● Flight plan · this week',
  'agent.flightPlan.title': "What I'll do unless you stop me.",
  'agent.flightPlan.sub':
    'All scheduled actions. Pause any individually, or pause all auto-sends from the header.',
  'agent.flightPlan.pause.cta': 'Pause all auto-sends',
  'agent.flightPlan.pause.cta.paused': 'Auto-sends paused',
  'agent.flightPlan.pause.cta.busy': 'Pausing…',
  'agent.flightPlan.pause.toast.paused': 'Auto-sends paused. Drafts will hold for your review.',
  'agent.flightPlan.pause.toast.adminOnly':
    'Only org admins can pause auto-sends. Ask your admin to flip the setting.',
  'agent.flightPlan.pause.toast.error': "Couldn't pause auto-sends. Try again in a moment.",
  'agent.flightPlan.tag.scheduled': 'scheduled',
  'agent.flightPlan.tag.drafting': 'drafting',
  'agent.flightPlan.tag.auto': 'auto',
  'agent.flightPlan.tag.conditional': 'conditional',
  'agent.flightPlan.tag.approval': 'approval',
  'agent.flightPlan.day.empty': 'Nothing scheduled.',
  'agent.flightPlan.empty.eyebrow': '● Flight plan · still warming up',
  'agent.flightPlan.empty.title': 'Nothing on the schedule yet.',
  'agent.flightPlan.empty.body':
    "Once the agent starts queuing scheduled actions and drafts against your connected tools, the week ahead lands here. You'll be able to pause any of them before they ship.",
  'agent.flightPlan.empty.status': 'Idle · ETA after the first connector sync',
  'agent.flightPlan.error': "Couldn't load the flight plan. Refresh to try again.",
  'agent.flightPlan.loading': 'Pulling scheduled actions…',
  'agent.flightPlan.label.via': 'via {channel}',
  'agent.flightPlan.label.drafting': 'Drafting · holds for review',
  'agent.flightPlan.label.scheduled': 'Scheduled · no approval needed',
  'agent.flightPlan.label.proposed': 'Proposal · pending review',
  'agent.flightPlan.label.watching': 'Watching · quiet',
  'agent.flightPlan.label.synthesized': 'Synthesis · auto',
  'agent.flightPlan.label.sent': 'Send · queued',
  'agent.flightPlan.label.errored': 'Errored · needs attention',

  // ---------------------------------------------------------------------------
  // Agent · flight plan page (`/agent/flight-plan`). Issue #173 PR-B Surface 3
  // — 3-tab "what the agent is planning" stub (This week / Next week /
  // Approved). Distinct namespace (`flightPlanPage.*`) from the existing
  // `/approvals/flight-plan` keys above so the two surfaces don't collide.
  // ---------------------------------------------------------------------------
  'agent.flightPlanPage.eyebrow': '● Flight plan',
  'agent.flightPlanPage.title': 'Flight plan',
  'agent.flightPlanPage.subtitle': 'What the agent is planning to do.',
  'agent.flightPlanPage.tabs.thisWeek': 'This week',
  'agent.flightPlanPage.tabs.nextWeek': 'Next week',
  'agent.flightPlanPage.tabs.approved': 'Approved',
  'agent.flightPlanPage.thisWeek.empty': 'No scheduled work this week.',
  'agent.flightPlanPage.nextWeek.empty.title': 'Coming up — grant write-scope to enable',
  'agent.flightPlanPage.nextWeek.empty.body':
    'Agent will surface scheduled work here once you grant write-scope on at least one connector.',
  'agent.flightPlanPage.approved.empty.title': 'Nothing approved yet',
  'agent.flightPlanPage.approved.empty.body': 'Approved scheduled actions will appear here.',
  'agent.flightPlanPage.demoCard.title': 'Demo: draft Acme renewal email',
  'agent.flightPlanPage.demoCard.eyebrow': 'TUE · 9:00 AM · DRAFT',
  'agent.flightPlanPage.demoCard.approveBtn': 'Approve & send',
  'agent.flightPlanPage.demoCard.interp':
    'This is a demonstration card showing the planned action format. Real scheduled actions will appear here once a backend job source is wired.',

  // ---------------------------------------------------------------------------
  // Settings · Agent persona + Meta-personalization (`/settings/agent`).
  // Issue #180 — last B1 surface from #173 PR-B. Closes B1 (8 of 8).
  // ---------------------------------------------------------------------------
  'settingsAgent.eyebrow': '● Agent settings',
  'settingsAgent.title': 'How the agent shows up.',
  'settingsAgent.sub':
    "Tune the voice, the autonomy dial, and the agent's self-suggested improvements. Voice + autonomy changes are org-wide; suggestions are personal to your seat.",
  'settingsAgent.tabs.aria': 'Agent settings tabs',
  'settingsAgent.tabs.persona': 'Persona',
  'settingsAgent.tabs.suggestions': 'Suggestions',
  'settingsAgent.error.load': 'Couldn’t load agent settings. Try again in a moment.',
  'settingsAgent.persona.voice.title': 'Voice · how the agent talks',
  'settingsAgent.persona.voice.sub':
    'Coach is warm and direct. Analyst is neutral and citation-heavy. Operator is terse and action-oriented. Quiet shows up only when something material changed.',
  'settingsAgent.persona.choice.voice': 'Voice',
  'settingsAgent.persona.choice.length': 'Length',
  'settingsAgent.persona.choice.stance': 'Stance',
  'settingsAgent.persona.autonomy.title': 'Autonomy · the trust dial',
  'settingsAgent.persona.autonomy.sub':
    'How much can the agent do without checking with you? This is the most consequential setting in the product. You can move it up as trust builds and back down anytime.',
  'settingsAgent.persona.autonomy.dialInterp':
    'These five toggles are the source-of-truth representation. A friendlier 5-step dial UX (Locked / Draft / Limited / Trusted / Full) is a follow-up.',
  'settingsAgent.persona.toggle.draftDocs': 'Draft documents',
  'settingsAgent.persona.toggle.scheduleMeetings': 'Schedule meetings',
  'settingsAgent.persona.toggle.sendReplies': 'Send replies (without asking)',
  'settingsAgent.persona.toggle.pauseCampaigns': 'Pause campaigns',
  'settingsAgent.persona.toggle.runWarehouseSql': 'Run warehouse SQL (read-only)',
  'settingsAgent.persona.cta.save': 'Save changes',
  'settingsAgent.persona.cta.saving': 'Saving…',
  'settingsAgent.persona.cta.reset': 'Reset',
  'settingsAgent.persona.toast.success': 'Agent persona saved.',
  'settingsAgent.persona.toast.error': 'Couldn’t save. Try again.',
  'settingsAgent.suggestions.eyebrow': '● Meta-personalization · the agent tunes itself',
  'settingsAgent.suggestions.title': 'I noticed something about how you use me.',
  'settingsAgent.suggestions.sub':
    'The agent doesn’t just learn your business — it learns how you read. Then it asks if it can improve. Every suggestion is one click to accept, dismiss, or remix.',
  'settingsAgent.suggestions.interp':
    'Static preview — the 6 cards below are representative. Real suggestions land once the implicit-feedback signal pipeline (dwell time, drill-in rate, decline patterns) ships in a follow-up.',
  'settingsAgent.suggestions.empty':
    'Nothing to suggest right now. The agent is watching how you read and will surface improvements as patterns emerge.',
  'settingsAgent.suggestions.threshold.eyebrow': 'Or: tell me to back off',
  'settingsAgent.suggestions.threshold.body':
    'I propose changes when I’m {pct}%+ confident they’ll help. Tighten if I’m too chatty.',
  'settingsAgent.suggestions.threshold.bodyNever':
    "I won't propose changes. I'll keep watching, but I'll stay quiet until you flip this back on.",
  'settingsAgent.suggestions.threshold.aggressive': 'Aggressive (50+)',
  'settingsAgent.suggestions.threshold.default': 'Default (80+)',
  'settingsAgent.suggestions.threshold.conservative': 'Conservative (95+)',
  'settingsAgent.suggestions.threshold.never': 'Never suggest',

  // Mock suggestion cards — these go away once the B5 implicit-feedback
  // pipeline starts producing real suggestions (server-rendered with
  // localized formatting). Keys are namespaced under `settingsAgent.mock.*`
  // so the swap can be a single-grep replacement.
  'settingsAgent.mock.layoutHiring.when': '20 minutes ago',
  'settingsAgent.mock.layoutHiring.headline':
    'You always read the Hiring section first — want me to put it on top?',
  'settingsAgent.mock.layoutHiring.body':
    '13 of your last 17 sessions opened Hiring before Pipeline. The current order is alphabetical; I can re-rank by your read order.',
  'settingsAgent.mock.layoutHiring.choice1': 'Yes — re-rank',
  'settingsAgent.mock.layoutHiring.choice2': 'Keep alphabetical',
  'settingsAgent.mock.layoutHiring.choice3': 'Show me the data',

  'settingsAgent.mock.voiceShorten.when': '1 hour ago',
  'settingsAgent.mock.voiceShorten.headline': 'Drop my prose. You skim — I’ll shorten.',
  'settingsAgent.mock.voiceShorten.body':
    'Your avg read time on briefing items is 4.2 seconds. I’m writing for 12. Want me to cut item summaries to one sentence?',
  'settingsAgent.mock.voiceShorten.choice1': 'Cut it',
  'settingsAgent.mock.voiceShorten.choice2': 'Keep medium',
  'settingsAgent.mock.voiceShorten.choice3': 'Show example',

  'settingsAgent.mock.timingFriday.when': 'Yesterday 3:14 PM',
  'settingsAgent.mock.timingFriday.headline': 'Stop sending the Friday wrap on Friday.',
  'settingsAgent.mock.timingFriday.body':
    'You read it Saturday morning, every time. Sunday 8am would catch you fresher. Want to shift the auto-send?',
  'settingsAgent.mock.timingFriday.choice1': 'Shift to Sun 8am',
  'settingsAgent.mock.timingFriday.choice2': 'Keep Friday 4pm',
  'settingsAgent.mock.timingFriday.choice3': 'Let me decide weekly',

  'settingsAgent.mock.layoutCinder.when': 'Yesterday 11:02 AM',
  'settingsAgent.mock.layoutCinder.headline': 'Pin Cinder Bio to your home view?',
  'settingsAgent.mock.layoutCinder.body':
    'You’ve opened their dossier 8 times this week. The home shows it on row 4. Pinning would surface it on top.',
  'settingsAgent.mock.layoutCinder.choice1': 'Pin it',
  'settingsAgent.mock.layoutCinder.choice2': 'Don’t pin',
  'settingsAgent.mock.layoutCinder.choice3': 'Pin all my top-3',

  'settingsAgent.mock.filterGovernance.when': '2 days ago',
  'settingsAgent.mock.filterGovernance.headline':
    'Hide governance items unless they’re due in 21 days.',
  'settingsAgent.mock.filterGovernance.body':
    'You only open governance items when there’s a deadline. The default 60-day window adds 11 cards you don’t read. Tighten?',
  'settingsAgent.mock.filterGovernance.choice1': 'Tighten to 21d',
  'settingsAgent.mock.filterGovernance.choice2': 'Keep at 60d',
  'settingsAgent.mock.filterGovernance.choice3': 'Let me set per-type',

  'settingsAgent.mock.vocabPartners.when': '3 days ago',
  'settingsAgent.mock.vocabPartners.headline':
    "Use 'partners' instead of 'customers' in all new dossiers.",
  'settingsAgent.mock.vocabPartners.body':
    'You corrected this word 7 times in the last 14 days. I can switch the default everywhere — including in drafts.',
  'settingsAgent.mock.vocabPartners.choice1': 'Switch everywhere',
  'settingsAgent.mock.vocabPartners.choice2': 'Just new dossiers',
  'settingsAgent.mock.vocabPartners.choice3': 'Keep as-is',

  // ---------------------------------------------------------------------------
  // Agent · Slack preview (`/agent/slack-preview`) — issue #173 PR-B Surface 8
  // (SlackNative). Static showcase of the LeanOpx agent inside a Slack DM:
  // user/bot exchange with citations + confidence + draft email + slash-command
  // composer. No backend wiring — the page is a trust-building visual that
  // CEOs see before granting workspace install scopes.
  // ---------------------------------------------------------------------------
  'agent.slackPreview.eyebrow': '● Slack-native',
  'agent.slackPreview.title': 'Ask, get cited answers, and approve drafts — without leaving Slack.',
  'agent.slackPreview.sub':
    'Most operators live in Slack. The bot DM is a first-class surface: same agent, same memory, same audit trail.',
  'agent.slackPreview.interp':
    'Static preview · the Slack app install + bot wiring lands in a follow-up.',
  'agent.slackPreview.chrome.channel': '# dm-leanopx-agent',
  'agent.slackPreview.chrome.sub': '· Just you and the agent',
  'agent.slackPreview.bot.name': 'LeanOpx',
  'agent.slackPreview.bot.appPill': 'APP',
  'agent.slackPreview.bot.citation.aria': 'Citation {n}',
  'agent.slackPreview.user.name': 'Emily Chen',
  'agent.slackPreview.user.q1': 'why did we lose acme last quarter',
  'agent.slackPreview.user.q2': 'draft a “let’s reconnect” note to acme’s new procurement lead',
  'agent.slackPreview.bot.q1.intro': 'Three things lined up against you:',
  'agent.slackPreview.bot.q1.item1': 'The Northwave proposal was ~$28k cheaper for the same scope',
  'agent.slackPreview.bot.q1.item2':
    'You missed a follow-up after the Mar 12 demo — 18 days of silence',
  'agent.slackPreview.bot.q1.item3':
    'Procurement champion Steve Lin left mid-cycle, and his replacement reset everything',
  'agent.slackPreview.bot.confidence.before': 'I’m',
  'agent.slackPreview.bot.confidence.high': '87% confident',
  'agent.slackPreview.bot.confidence.middle': 'on points 1 and 3, only',
  'agent.slackPreview.bot.confidence.low': '62%',
  'agent.slackPreview.bot.confidence.after': 'on point 2 — your follow-up record is partial.',
  'agent.slackPreview.bot.q2.tone':
    'Drafted. Tone: warm + direct (matches your last 12 outbound emails).',
  'agent.slackPreview.bot.draft.meta':
    'TO: rachel.kim@acmefoundry.com · SUBJ: Quick hello + a thought',
  'agent.slackPreview.bot.draft.line1':
    'Hi Rachel — Steve mentioned you were taking over procurement. Congrats on the role.',
  'agent.slackPreview.bot.draft.line2':
    'We were in a procurement cycle with Acme last quarter and didn’t land it. I think we had the wrong shape of proposal for what you needed. If there’s an opening to reconnect in the next 60 days, I’d love to walk you through what we’ve shipped since then.',
  'agent.slackPreview.bot.draft.line3': 'No pressure — let me know if helpful.',
  'agent.slackPreview.bot.draft.sign': 'Emily',
  'agent.slackPreview.bot.draft.send': 'Send it',
  'agent.slackPreview.bot.draft.edit': 'Edit',
  'agent.slackPreview.bot.draft.tone': 'Try a different tone',
  'agent.slackPreview.bot.draft.discard': 'Discard',
  'agent.slackPreview.composer.prompt': 'Message LeanOpx',
  'agent.slackPreview.footer.eyebrow': '● Preview only',
  'agent.slackPreview.footer.title': 'This is what Slack will look like once the bot ships.',
  'agent.slackPreview.footer.body':
    'The Slack app install + DM wiring is a separate epic. This page exists so you can see the shape of the experience and decide whether to grant the workspace scopes when it’s ready.',

  // ---------------------------------------------------------------------------
  // Agent · near-misses (`/memory/near-misses`). Earned-moment surface — the
  // agent's catches over 30 days, hero card + grid + tally.
  // ---------------------------------------------------------------------------
  'agent.nearMisses.eyebrow': '● Near-misses · earned moments',
  'agent.nearMisses.title': "Here's where I caught a screw-up before it happened.",
  'agent.nearMisses.sub':
    'Every catch surfaced here cites the source event the agent read. Mark as resolved to retire one from the rail.',
  'agent.nearMisses.hero.eyebrow.format': '● Caught at {time}',
  'agent.nearMisses.hero.cta': 'See what I did →',
  'agent.nearMisses.hero.cta.resolve': 'Mark as resolved',
  'agent.nearMisses.impact.eyebrow': 'Estimated impact',
  'agent.nearMisses.past.eyebrow': 'Past 30 days · what I caught',
  'agent.nearMisses.tally.eyebrow': 'Saves at a glance',
  'agent.nearMisses.tally.saves': 'saves in 30 days',
  'agent.nearMisses.tally.hours': 'work hours avoided',
  'agent.nearMisses.tally.risk': 'risk surfaced',
  'agent.nearMisses.tally.corrections': 'corrections logged',
  'agent.nearMisses.empty.eyebrow': '● Near-misses · still warming up',
  'agent.nearMisses.empty.title': "Haven't caught anything for you yet.",
  'agent.nearMisses.empty.body':
    'Once the agent starts watching your connected tools, every near-miss — a slipping renewal, a sentiment drop, a wrong-month booking — surfaces here with the source event it spotted.',
  'agent.nearMisses.empty.status': 'Idle · ETA after the first connector sync',
  'agent.nearMisses.error': "Couldn't load near-misses. Refresh to try again.",
  'agent.nearMisses.loading': 'Pulling catches…',

  // ---------------------------------------------------------------------------
  // Notes timeline (`/notes/timeline`). Holistic chronological feed of every
  // capture — voice, photo, file, text — with a filter rail and year heatmap.
  // ---------------------------------------------------------------------------
  'notes.timeline.eyebrow': '● Notes · holistic timeline',
  'notes.timeline.title': "Every thought you've captured, organized.",
  'notes.timeline.sub.format':
    '{week} notes this week · {voice} voice · {entities} entities touched',
  'notes.timeline.loading': 'Loading your timeline…',
  'notes.timeline.error.load': 'Could not load your notes timeline.',
  'notes.timeline.empty.eyebrow': '● Notes · waiting',
  'notes.timeline.empty.title': 'Your first capture goes here.',
  'notes.timeline.empty.body':
    'Press ⌘N anywhere in the portal to capture a thought — type, dictate, snap a photo, or drop a file. Everything you record lands here on a single chronological rail you can filter, search, and revisit.',

  // Filter rail (left, 240px). Four sections.
  'notes.timeline.filter.eyebrow': 'Filter',
  'notes.timeline.filter.date.heading': 'Date range',
  'notes.timeline.filter.date.today': 'Today',
  'notes.timeline.filter.date.7d': 'Last 7 days',
  'notes.timeline.filter.date.30d': 'Last 30 days',
  'notes.timeline.filter.date.quarter': 'This quarter',
  'notes.timeline.filter.date.year': 'This year',
  'notes.timeline.filter.date.all': 'All time',
  'notes.timeline.filter.type.heading': 'Type',
  'notes.timeline.filter.type.all': 'All',
  'notes.timeline.filter.type.voice': 'Voice',
  'notes.timeline.filter.type.photo': 'Photos',
  'notes.timeline.filter.type.file': 'Files',
  'notes.timeline.filter.type.text': 'Text',
  'notes.timeline.filter.source.heading': 'Source',
  'notes.timeline.filter.source.all': 'Everywhere',
  'notes.timeline.filter.source.you': 'You',
  'notes.timeline.filter.source.team': 'Team',
  'notes.timeline.filter.source.slack': 'Slack capture',
  'notes.timeline.filter.source.email': 'Email capture',
  'notes.timeline.filter.entity.heading': 'Entity type',
  'notes.timeline.filter.entity.all': 'All',
  'notes.timeline.filter.entity.people': 'People',
  'notes.timeline.filter.entity.customer': 'Customers',
  'notes.timeline.filter.entity.topic': 'Topics',
  'notes.timeline.filter.entity.team': 'Teams',
  'notes.timeline.filter.entity.department': 'Departments',

  // Feed (center, 1fr). Day-grouped vertical rail.
  'notes.timeline.feed.group.today': 'Today',
  'notes.timeline.feed.group.yesterday': 'Yesterday',
  'notes.timeline.feed.group.lastWeek': 'Last week',
  'notes.timeline.feed.group.thisMonth': 'This month',
  'notes.timeline.feed.count.one': '1 note',
  'notes.timeline.feed.count.many': '{n} notes',
  'notes.timeline.feed.type.voice': 'VOICE',
  'notes.timeline.feed.type.photo': 'PHOTO',
  'notes.timeline.feed.type.file': 'FILE',
  'notes.timeline.feed.type.text': 'TEXT',
  'notes.timeline.feed.attach.voice.format': 'voice memo · {duration}',
  'notes.timeline.feed.untitled': 'Untitled note',
  'notes.timeline.feed.you': 'You',
  'notes.timeline.feed.noMatches.title': 'No notes match these filters.',
  'notes.timeline.feed.noMatches.body':
    'Clear a filter, broaden the date range, or capture something new.',

  // Heatmap rail (right, 280px). Year density + tag cloud.
  'notes.timeline.heatmap.eyebrow': 'Year heatmap · capture density',
  'notes.timeline.heatmap.legend.less': 'Less',
  'notes.timeline.heatmap.legend.more': 'More',
  'notes.timeline.heatmap.day.format': '{date} · {count} {unit}',
  'notes.timeline.heatmap.tagcloud.eyebrow': 'Most mentioned · this quarter',
  'notes.timeline.heatmap.tagcloud.empty': 'No entity mentions yet this quarter.',

  // Quick capture — global SlideOver primitive bound to ⌘N (see
  // `apps/web/src/components/quick-capture/`). Composed against the
  // design-system `<SlideOver emphasis="accent" scrim="transparent">`
  // so the dossier behind stays visible while the user captures.
  'quickCapture.eyebrow': 'Quick capture',
  'quickCapture.title': 'Capture a thought',
  'quickCapture.contextPrefix': 'thinking about ',
  'quickCapture.closeLabel': 'Close quick capture',
  'quickCapture.kbdHint': '⌘N',
  'quickCapture.placeholder':
    "What's on your mind? Paste a quote, jot a follow-up, drop a link — the agent will link it.",
  'quickCapture.tools.voice': 'Voice',
  'quickCapture.tools.photo': 'Photo',
  'quickCapture.tools.file': 'File',
  'quickCapture.tools.sketch': 'Sketch',
  'quickCapture.tools.mention': 'Mention',
  'quickCapture.tools.tag': 'Tag',
  'quickCapture.toolsUnavailable':
    'Attachments wire in once the editor lands — paste text for now.',
  'quickCapture.attachToHeading': 'Attach to · agent links to related',
  'quickCapture.attachAdd': '+ Add',
  'quickCapture.agentSuggestsEyebrow': 'Agent suggests',
  'quickCapture.agentSuggestsBody':
    'Once the synthesis pass is wired, related dossiers will appear here as one-tap links.',
  'quickCapture.agentSuggestsThinking': 'thinking…',
  'quickCapture.agentSuggestsEmpty':
    'No clear matches in your dossiers yet — keep typing or attach manually.',
  'quickCapture.agentSuggestsAddAll': '+ Add all',
  'quickCapture.agentSuggestsAddOne': '+ Add',
  'quickCapture.agentSuggestsAdded': 'Added',
  'quickCapture.privateLabel': 'Private to you',
  'quickCapture.discard': 'Discard',
  'quickCapture.discardArmed': 'Tap again to discard',
  'quickCapture.save': 'Save & link',
  'quickCapture.saving': 'Saving…',
  'quickCapture.errorSave': "Couldn't save the note. Try again.",
  'quickCapture.errorNoOrg': 'Pick an organisation before capturing a note.',
  'quickCapture.saved': 'Note saved',

  // ── today.brief.* (President's Daily Brief composition) ────────────
  // Owned by the today-hero agent. Strings drive `<DecisionHero>`,
  // `<WatchingGrid>`, `<TheCallCard>`, `<OvernightStrap>` and the page
  // header in `pages/today.tsx`. Closed-set role/severity labels live
  // in `packages/shared` enums — never inlined here.
  'today.brief.header.eyebrow': 'Daily intelligence brief · classified to you',
  'today.brief.header.eyebrowDot': '●',
  'today.brief.header.title': '{date} · Good morning, {firstName}.',
  'today.brief.header.titleNoName': '{date} · Good morning.',
  'today.brief.header.sub':
    'One thing demanding a decision today. Two things to watch. One person to call.',
  'today.brief.header.stat.decision': 'Decision',
  'today.brief.header.stat.watching': 'Watching',
  'today.brief.header.stat.toCall': 'To call',

  // <DecisionHero>
  'today.brief.decision.eyebrow': "The decision · today's classified item",
  'today.brief.decision.dot': '●',
  'today.brief.decision.confidenceLabel': 'Agent confidence',
  'today.brief.decision.cta.approve': 'Approve · agent drafts the email',
  'today.brief.decision.cta.hold': 'Hold · I want to talk first',
  'today.brief.decision.cta.tellMore': 'Tell me more',
  'today.brief.decision.cites.prefix': 'Sources',

  // <WatchingGrid>
  'today.brief.watching.eyebrow': 'Watching · no action yet, but worth your eye',
  'today.brief.watching.dot': '●',
  'today.brief.watching.itemDot': '●',
  'today.brief.watching.itemSuffix': 'Watch',
  'today.brief.watching.linkedGlyph': '◌',
  'today.brief.watching.linkedPrefix': 'linked:',

  // <TheCallCard>
  'today.brief.call.eyebrow': 'The call · who deserves your voice today',
  'today.brief.call.dot': '●',
  'today.brief.call.cta.draftIntro': 'Draft an intro DM',
  'today.brief.call.cta.seeMemo': 'See the memo',

  // <OvernightStrap>
  'today.brief.overnight.eyebrow': 'Agent activity overnight · {n} actions',
  'today.brief.overnight.seeAll': 'see all →',
  'today.brief.overnight.label.drafts': 'drafts waiting',
  'today.brief.overnight.label.nearMiss': 'near-miss caught',
  'today.brief.overnight.label.risks': 'risk flagged',
  'today.brief.overnight.label.memos': 'memos indexed',

  // Empty states — when the agent has nothing classified.
  'today.brief.decision.empty.title': 'No decision needs you right now.',
  'today.brief.decision.empty.body':
    'The agent watched overnight and nothing crossed the line where it needed a call. It will surface the next decision the moment it does.',
  'today.brief.watching.empty':
    'Nothing on the watch list today. The agent is monitoring partners, accounts, and team patterns in the background.',
  'today.brief.call.empty':
    'No one to call today. The agent will flag a person the moment a relationship goes quiet or a memo mentions you.',

  // ── First-briefing-ready celebration (`/first-briefing-ready`). Day
  //    2-3 magic moment after the agent has finished generating the
  //    user's first Monday briefing. Static client-side artifact for
  //    now — the trigger from a real backend signal is a deferred epic.
  //    Mirrors `FirstBriefingReady` (onboarding-extras-3.jsx).
  'onboarding.first-briefing.bg.brand': 'LeanOpx',
  'onboarding.first-briefing.bg.nav.briefing': 'Briefing',
  'onboarding.first-briefing.bg.nav.ask': 'Ask',
  'onboarding.first-briefing.bg.nav.notes': 'Notes',
  'onboarding.first-briefing.bg.nav.dossiers': 'Dossiers',
  'onboarding.first-briefing.bg.nav.sources': 'Sources',
  'onboarding.first-briefing.bg.nav.settings': 'Settings',
  'onboarding.first-briefing.bg.eyebrow': 'MONDAY · MAY 25 · 8:02 AM',
  'onboarding.first-briefing.bg.greeting': 'Good morning, {firstName}.',
  'onboarding.first-briefing.card.eyebrow': 'Your first briefing is live',
  'onboarding.first-briefing.card.title': 'Three things matter this morning.',
  'onboarding.first-briefing.card.body':
    "I read 19,910 items across 6 sources over the weekend. Here's what surfaced. Pinch of salt where there's interpretation — I'll always cite the source.",
  'onboarding.first-briefing.callout.eyebrow': 'One decision · two watches · one call',
  'onboarding.first-briefing.callout.body.before':
    'Acme Foundry is asking for a 30% discount to close by Friday. Three signals say ',
  'onboarding.first-briefing.callout.body.accent': 'hold the line',
  'onboarding.first-briefing.callout.body.after': '.',
  'onboarding.first-briefing.primary-cta': "Read this morning's briefing →",
  'onboarding.first-briefing.secondary-cta': 'Show me what you read this weekend',
  'onboarding.first-briefing.push.app': 'LeanOpx',
  'onboarding.first-briefing.push.when': 'now',
  'onboarding.first-briefing.push.title': 'Your first briefing is ready',
  'onboarding.first-briefing.push.meta': '3 things to know · 4 min read · live now',

  // ── Welcome email artifact (rendered for preview / future MJML
  //    transpilation). Component lives at
  //    `apps/web/src/components/email/welcome-email.tsx`. Mirrors
  //    `WelcomeEmail` (onboarding-extras-3.jsx).
  'onboarding.welcome-email.client.inbox': 'Inbox',
  'onboarding.welcome-email.client.reply': 'Reply',
  'onboarding.welcome-email.client.forward': 'Forward',
  'onboarding.welcome-email.client.archive': 'Archive',
  'onboarding.welcome-email.client.more': '···',
  'onboarding.welcome-email.from.display': 'The LeanOpx agent',
  'onboarding.welcome-email.from.route': '{from} → {to}',
  'onboarding.welcome-email.from.email': 'agent@leanopx.com',
  'onboarding.welcome-email.timestamp': 'Wed · May 20 · 4:08 PM',
  'onboarding.welcome-email.subject': 'Welcome, {firstName}. Three things while I read your stack.',
  'onboarding.welcome-email.lede':
    "Quick note — I'm the LeanOpx agent. Wanted to drop in so this isn't all silent until Monday.",
  'onboarding.welcome-email.reading':
    "You connected {connectors} earlier today. I'm reading the last 90 days now. About 6 minutes left — you don't need to be here for it.",
  'onboarding.welcome-email.connectors.default': 'Gmail, Calendar, Slack, and Salesforce',
  'onboarding.welcome-email.box.eyebrow': '3 things, for the week ahead',
  'onboarding.welcome-email.box.item1.bold': 'Monday 8am',
  'onboarding.welcome-email.box.item1.body':
    ' — your first briefing lands. Three things that matter. Read time ≈ 4 min.',
  'onboarding.welcome-email.box.item2.bold': 'Add 1-2 teammates',
  'onboarding.welcome-email.box.item2.body':
    ' if you want them briefed too. Each gets their own version, tailored to their role.',
  'onboarding.welcome-email.box.item3.bold': 'Reply to this email',
  'onboarding.welcome-email.box.item3.body':
    " with anything I should pay extra attention to (a board prep, a hard week ahead, a deal you're worried about).",
  'onboarding.welcome-email.correct.before': "If something looks off in Monday's briefing, click ",
  'onboarding.welcome-email.correct.link': '"correct"',
  'onboarding.welcome-email.correct.after':
    " on any fact. I'll learn and stop making that mistake.",
  'onboarding.welcome-email.signoff': '— the LeanOpx agent',
  'onboarding.welcome-email.cta': 'Open LeanOpx →',
  'onboarding.welcome-email.footer.body':
    "You're getting this because you signed up for LeanOpx with {email}. Your data lives in your private workspace and never trains models for anyone else.",
  'onboarding.welcome-email.footer.privacy': 'Privacy',
  'onboarding.welcome-email.footer.unsubscribe': 'Unsubscribe from agent emails',
  'onboarding.welcome-email.footer.entity': 'LeanOpx, Inc · San Francisco',

  // ── Setup checklist (`/onboarding/checklist`). Post-go-live "five
  //    things to do this week" surface. Mirrors `SetupChecklist`
  //    (missing-screens.jsx).
  'onboarding.checklist.eyebrow': 'Setup · 5 things to do this week',
  'onboarding.checklist.title': 'A short list. Then the agent takes over.',
  'onboarding.checklist.sub':
    "You finished the basics. Here's what'll multiply the agent's value — most teams check these off by Friday.",
  'onboarding.checklist.progress.label': 'Progress',
  'onboarding.checklist.progress.value': '{done} / {total}',
  'onboarding.checklist.status.done': 'DONE',
  'onboarding.checklist.est': '~{est}',
  // Aria-labels — toggle button reads as a screen-reader action.
  'onboarding.checklist.aria.mark-done': 'Mark step as done',
  'onboarding.checklist.aria.mark-undone': 'Mark step as not done',
  'onboarding.checklist.step.01.title': 'Connect three sources',
  'onboarding.checklist.step.01.body':
    'You connected Slack, Google Workspace, and Salesforce. The agent has been reading since Monday.',
  'onboarding.checklist.step.01.cta': 'Manage sources →',
  'onboarding.checklist.step.01.est': '5 min',
  'onboarding.checklist.step.02.title': 'Confirm what I learned',
  'onboarding.checklist.step.02.body':
    'You approved the reveal of 12 people, 4 rituals, and 3 metrics. Vocabulary set.',
  'onboarding.checklist.step.02.cta': 'Review reveal →',
  'onboarding.checklist.step.02.est': '10 min',
  'onboarding.checklist.step.03.title': 'Invite your team',
  'onboarding.checklist.step.03.body':
    "Add the 6 people you work with most. They'll get a personalized briefing on Monday — no extra setup.",
  'onboarding.checklist.step.03.cta': 'Invite team →',
  'onboarding.checklist.step.03.est': '8 min',
  'onboarding.checklist.step.04.title': 'Set your standing orders',
  'onboarding.checklist.step.04.body':
    'Define 2–3 rules: when to interrupt you, when to hold, when to auto-send. Skip if not sure — defaults are sane.',
  'onboarding.checklist.step.04.cta': 'Set rules →',
  'onboarding.checklist.step.04.est': '5 min',
  'onboarding.checklist.step.05.title': 'Approve the first briefing',
  'onboarding.checklist.step.05.body':
    'Lands Monday 8:30am. Review the draft, edit anything off, send it to yourself or your team.',
  'onboarding.checklist.step.05.cta': 'Waiting for Monday',
  'onboarding.checklist.step.05.est': '2 min',
  'onboarding.checklist.later.eyebrow': 'And later',
  'onboarding.checklist.later.body':
    'Connect a 4th and 5th source (warehouse, finance, HR), then turn on the conversational Slack bot. The agent gets compounding-ly better with each new source.',
  'onboarding.checklist.later.cta': 'See the long checklist',

  // ── First-run walkthrough (`/onboarding/walkthrough`). Static
  //    standalone demo of the in-app product tour — anchored callouts
  //    against a faded chrome placeholder. Mirrors `WalkthroughScreen`
  //    (19311b57-…js).
  'onboarding.walkthrough.eyebrow': 'Command Center · Monday briefing',
  'onboarding.walkthrough.greeting': 'Good evening, {firstName}.',
  'onboarding.walkthrough.active-org': 'Active org · {orgName}',
  'onboarding.walkthrough.headline.before': 'Quiet morning — your ',
  'onboarding.walkthrough.headline.accent': 'net new ARR',
  'onboarding.walkthrough.headline.after': ' is steady.',
  'onboarding.walkthrough.step.aria': 'Step {n} of {total}',
  'onboarding.walkthrough.step.label': 'Step {n} of {total} · Agent tour',
  'onboarding.walkthrough.step.skip': 'Skip',
  'onboarding.walkthrough.step.next': 'Next →',
  'onboarding.walkthrough.step.done': "Got it, let's go",
  'onboarding.walkthrough.footer.label': 'Walkthrough · {n} / {total}',
  'onboarding.walkthrough.footer.crumbs': 'Headline → Insights → Calendar → Drafts → Ask',
  'onboarding.walkthrough.step.01.title': 'This is your briefing.',
  'onboarding.walkthrough.step.01.body':
    'Read-only by default. The agent surfaces what changed overnight — you decide what to act on.',
  'onboarding.walkthrough.step.02.title': 'This is your headline.',
  'onboarding.walkthrough.step.02.body':
    "The agent writes one each morning. It's grounded in events from the last 24h — every claim cites the source.",
  'onboarding.walkthrough.step.03.title': 'Ask anything.',
  'onboarding.walkthrough.step.03.body':
    'Plain English in, cited synthesis out. The agent stitches an answer from Slack, email, Salesforce, and the rest of your stack.',
  'onboarding.walkthrough.step.04.title': 'Capture as you go.',
  'onboarding.walkthrough.step.04.body':
    "Drop voice notes, text, or screenshots. The agent files them into the right dossier and pulls them into next Monday's briefing.",
  'onboarding.walkthrough.step.05.title': 'Approve what goes out.',
  'onboarding.walkthrough.step.05.body':
    'Drafts from the agent — emails, replies, briefings — wait here for your eyes before they ship. Standing orders relax this over time.',

  // ── Empty workspace component (`<EmptyWorkspaceCard>`, lives at
  //    `apps/web/src/components/onboarding/empty-workspace.tsx`).
  //    First-run zero-data card surfaced inside `/today` or other
  //    surfaces while the agent is still indexing. Distinct from the
  //    legacy `EmptyWorkspacePage` at `/empty` (which is the full-page
  //    onboarding-redirect). Mirrors `EmptyWorkspaceScreen`
  //    (944411bc-…js) — the in-shell zero-state card.
  'onboarding.empty-workspace.eyebrow': 'Your briefing will be here Monday',
  'onboarding.empty-workspace.title': 'Getting your briefing ready…',
  'onboarding.empty-workspace.body':
    'The agent is reading the last 90 days across your connected sources. About 6 minutes left on the indexing pass — check back soon.',
  'onboarding.empty-workspace.progress.label': 'Indexing',
  'onboarding.empty-workspace.progress.percent': '{pct}%',
  'onboarding.empty-workspace.tile.briefing.title': "Monday's briefing",
  'onboarding.empty-workspace.tile.briefing.body': 'Three things that matter, with sources.',
  'onboarding.empty-workspace.tile.dossiers.title': 'Dossiers',
  'onboarding.empty-workspace.tile.dossiers.body':
    'Living folders for every customer, person, and topic.',
  'onboarding.empty-workspace.tile.ask.title': 'Ask',
  'onboarding.empty-workspace.tile.ask.body':
    'Plain English in, cited synthesis out — once indexing finishes.',
  'onboarding.empty-workspace.cta': 'Check connector status →',

  // ── Agent trust loop (#166 PR A) ─────────────────────────────────
  // Feedback primitives (FB-01 line-vote, FB-02 microbar, FB-03 selection,
  // FB-04 self-doubt, FB-05 outcome-followback, FB-06 correction-ack).
  'feedback.line.up.aria': 'This line is useful',
  'feedback.line.down.aria': 'This line is wrong',
  'feedback.microbar.eyebrow': 'How was this brief?',
  'feedback.microbar.up': 'Useful',
  'feedback.microbar.down': 'Not useful',
  'feedback.microbar.thanks': 'Thanks — logged.',
  'feedback.selection.cta': 'Tell the agent why',
  'feedback.selection.placeholder': 'What’s wrong with this passage?',
  'feedback.selection.submit': 'Send',

  // FB-04 self-doubt prompt — verbatim from comment B
  'fb04.selfdoubt.prompt': "Wasn't sure on this one — was I right?",
  'fb04.selfdoubt.yes': 'Yes',
  'fb04.selfdoubt.no': 'No',
  'fb04.selfdoubt.unsure': "I'm not sure",

  // FB-05 outcome followback — verbatim eyebrow template
  'fb05.outcome.eyebrow': '{relativeWeeks} ago · prediction',
  'fb05.outcome.confirmed': 'I called this right',
  'fb05.outcome.wrong': 'I got this wrong',
  'fb05.outcome.partial': 'Partly right',

  // FB-06 correction acknowledgment
  'fb06.ack.eyebrow': 'AGENT',
  'fb06.ack.title': "Got it. I'm re-synthesizing the dossier now.",
  'fb06.ack.body': "I'll downweight the sources you flagged. Back in about 2 minutes.",

  // ── Agent questions (/agent/questions) ───────────────────────────
  'agent.questions.title': 'Nine things I want to ask you when you have five minutes.',
  'agent.questions.sub':
    'Each of these is a place I’m guessing — and would rather know. Skip any; nothing is required.',
  'agent.questions.rail.total': 'Total',
  'agent.questions.rail.highPriority': 'High priority',
  'agent.questions.rail.estTime': 'Est. time',
  'agent.questions.rail.blocksBriefing': 'Blocks Monday’s brief',
  'agent.questions.empty':
    'Nothing to ask. I’m caught up — next batch arrives after the agent re-synthesizes.',
  'agent.questions.error': 'Couldn’t load your questions.',
  'agent.questions.skip': 'Skip',
  'agent.questions.stopAsking': 'Stop asking',
  'agent.questions.answer': 'Answer',
  'agent.questions.priority.high': 'High',
  'agent.questions.priority.med': 'Med',
  'agent.questions.priority.low': 'Low',

  // ── Agent calibration (/agent/calibration) ───────────────────────
  'agent.calibration.title':
    'The track record. {accuracyPct}% over {n} decisions — and getting better.',
  'agent.calibration.sub':
    'Every prediction the agent makes is scored against what actually happened. The number isn’t a vibe — it’s the math.',
  'agent.calibration.stat.right': 'Right',
  'agent.calibration.stat.wrong': 'Wrong',
  'agent.calibration.stat.unsure': 'Unsure',
  'agent.calibration.stat.learned': 'Things I learned',
  'agent.calibration.chart.title': 'Accuracy, last 12 weeks',
  'agent.calibration.misses.title': 'Things I got wrong',
  'agent.calibration.miss.said': 'Said:',
  'agent.calibration.miss.actually': 'Actually:',
  'agent.calibration.misses.learned': '◆ Learned',
  'agent.calibration.misses.empty':
    'No misses logged. Either I’m perfect, or I haven’t learned enough yet — probably the latter.',
  'agent.calibration.error': 'Couldn’t load the calibration data.',

  // ── Agent changelog (/agent/changelog) ───────────────────────────
  'agent.changelog.title': 'What I learned this month (and how I got better at it).',
  'agent.changelog.sub':
    'Every learning, source-weight tweak, and behavior change — with the conversation or correction that triggered it.',
  'agent.changelog.empty':
    'Nothing logged yet — corrections + outcome followbacks feed this stream.',
  'agent.changelog.error': 'Couldn’t load the changelog.',
  'agent.changelog.tag.speed': 'Speed',
  'agent.changelog.tag.accuracy': 'Accuracy',
  'agent.changelog.tag.new': 'New',
  'agent.changelog.tag.transparency': 'Transparency',
  'agent.changelog.tag.respect': 'Respect',
  'agent.changelog.tag.identity': 'Identity',
  'agent.changelog.tag.honesty': 'Honesty',
  'agent.changelog.source.correction': 'From your correction',
  'agent.changelog.source.platform': 'Platform update',

  // ── Agent corrections (/agent/corrections) ───────────────────────
  'agent.corrections.openCta': '+ Correct a fact',
  'agent.corrections.heroEyebrow': 'You got this wrong.',
  'agent.corrections.heroTitle': 'Help me understand what I missed.',
  'agent.corrections.heroSub':
    "Help me understand. I'll re-synthesize this dossier within 2 minutes.",
  'agent.corrections.section.whatISaid': 'What I said',
  'agent.corrections.section.whatsWrong': "What's actually wrong?",
  'agent.corrections.section.truth': 'The truth, in your words',
  'agent.corrections.section.oldSources': 'What should I do with the old sources?',
  'agent.corrections.kind.fact': 'The fact itself is wrong',
  'agent.corrections.kind.fact.body': 'You read the source correctly, but the source is wrong.',
  'agent.corrections.kind.scope': 'The scope is wrong',
  'agent.corrections.kind.scope.body': 'It’s right about one thing, wrong about another.',
  'agent.corrections.kind.source': 'You used the wrong source',
  'agent.corrections.kind.source.body': 'Trust this one instead.',
  'agent.corrections.kind.stale': 'It’s stale',
  'agent.corrections.kind.stale.body': 'Was true once; not anymore.',
  'agent.corrections.truth.placeholder': 'Tell me what’s actually true — in a sentence or two.',
  'agent.corrections.oldSources.downweight': 'Downweight this source on this dossier',
  'agent.corrections.oldSources.markStale': 'Mark the conflicting facts stale',
  'agent.corrections.oldSources.keep': 'Keep the old sources, just trust the new one more',
  'agent.corrections.submit': 'Submit correction',
  'agent.corrections.cancel': 'Cancel',
  'agent.corrections.footer': 'Correction logged · agent re-synthesizes in 2m',
  // FB-06 inline acknowledgment surface (renders above the drawer body
  // after the recordFeedback POST resolves; offers a propagation choice
  // when the correction targets a specific dossier).
  'agent.corrections.ack.message': "Got it. I'll re-synthesize this dossier in the next 2 minutes.",
  'agent.corrections.ack.propagate': 'Update related dossiers too',
  'agent.corrections.ack.local': 'Just this one',

  // ── Dossier conflict resolver ────────────────────────────────────
  'dossier.conflict.title': 'Sources disagree on this fact.',
  'dossier.conflict.sub':
    'Pick which source the agent should trust, or set a manual override. The policy applies to future conflicts of the same kind.',
  'dossier.conflict.override.label': 'Or set a manual value',
  'dossier.conflict.override.use': 'Use this value',
  'dossier.conflict.policy.eyebrow': 'Set a default for next time',
  'dossier.conflict.policy.voice-notes-first': 'Prefer your voice notes',
  'dossier.conflict.policy.salesforce-first': 'Prefer Salesforce',
  'dossier.conflict.policy.always-ask': 'Ask me each time',
  'dossier.conflict.empty': 'No active conflicts on this dossier.',
  'dossier.conflict.error': 'Couldn’t load the conflict candidates.',
  'dossier.conflict.applied': 'Resolution applied. Re-synthesizing.',

  // ── Stale fact heatmap ────────────────────────────────────────────
  'dossier.staleHeatmap.title': 'Freshness by fact',
  'dossier.staleHeatmap.sub': 'How recently each fact has been touched.',
  'dossier.staleHeatmap.bucket.lt2mo': '< 2 months',
  'dossier.staleHeatmap.bucket.2to6mo': '2–6 months',
  'dossier.staleHeatmap.bucket.6to12mo': '6–12 months',
  'dossier.staleHeatmap.bucket.gt12mo': '> 12 months',
  'dossier.staleHeatmap.topStale.eyebrow': 'Most stale on this dossier',
  'dossier.staleHeatmap.action.refetch': 'Re-fetch from {source}',
  'dossier.staleHeatmap.action.ask': 'Ask {owner}',
  'dossier.staleHeatmap.action.markCurrent': 'Mark still current',
  'dossier.staleHeatmap.col.fact': 'Fact',
  'dossier.staleHeatmap.col.value': 'Value',
  'dossier.staleHeatmap.col.age': 'Age',
  'dossier.staleHeatmap.empty': 'Every fact on this dossier is fresh.',

  // ── Dossier comparison ────────────────────────────────────────────
  'dossier.comparison.title': 'Compared side-by-side',
  'dossier.comparison.sub': 'Differences the agent thinks matter.',
  'dossier.comparison.winner': 'Stronger',
  'dossier.comparison.agentSummary.eyebrow': 'Agent summary',

  // ── Fact timeline slide-over ──────────────────────────────────────
  'dossier.factTimeline.title': 'How this fact evolved',
  'dossier.factTimeline.sub': 'Every event the agent observed for this fact.',
  'dossier.factTimeline.chart.title': 'Trust over time',
  'dossier.factTimeline.events.title': 'Events',
  'dossier.factTimeline.empty':
    'No event history yet — this fact has only one observation on record.',
  'dossier.factTimeline.weight.high': 'High weight',
  'dossier.factTimeline.weight.med': 'Med weight',
  'dossier.factTimeline.weight.low': 'Low weight',
  'dossier.factTimeline.event.trust': 'trust {pct}%',

  // ── Sources weight tuning (/settings/sources) ─────────────────────
  'sources.weights.title': 'How much I trust each source',
  'sources.weights.sub':
    'Slide a source up if I should trust it more, down if I keep getting facts from it wrong. Changes apply to every dossier within seconds.',
  'sources.weights.reset': 'Reset to defaults',
  'sources.weights.save': 'Save weights',
  'sources.weights.locked': 'locked',
  'sources.weights.tradeoffs.title': 'What you’re tuning',
  'sources.weights.tradeoffs.body':
    'Sources with higher weights win in conflicts and get cited more often. Drop them too far and the agent will fall back to less-trusted signals.',
  'sources.weights.saved': 'Weights saved.',
  'sources.weights.error': 'Couldn’t save weights. Try again.',
  'sources.weights.loading': 'Loading source weights…',
  'sources.weights.source.told': 'You',
  'sources.weights.source.web': 'Web',
  'sources.weights.source.gmail': 'Gmail',
  'sources.weights.source.gcal': 'Calendar',
  'sources.weights.source.slack': 'Slack',
  'sources.weights.source.salesforce': 'Salesforce',
  'sources.weights.source.github': 'GitHub',
  'sources.weights.source.stripe': 'Stripe',
  'sources.weights.source.linkedin': 'LinkedIn',
  'sources.weights.source.crunchbase': 'Crunchbase',
  'sources.weights.source.apollo': 'Apollo',
  'sources.weights.source.voice-notes': 'Voice notes',
  'sources.weights.source.written-notes': 'Written notes',
  'sources.weights.source.notion': 'Notion',
  'sources.weights.source.drive': 'Drive',
  'sources.weights.source.agent-inference': 'Agent inference',
  'sources.weights.source.external-web': 'External web',

  // ── Ask deep research mode (/ask) ─────────────────────────────────
  'ask.deepResearch.toggle': 'Deep research',
  'ask.deepResearch.toggle.hint': 'Slower · traceable · cites every step',
  'ask.deepResearch.trace.title': 'Research trace',
  'ask.deepResearch.step.done': 'Done',
  'ask.deepResearch.step.running': 'Running…',
  'ask.deepResearch.step.pending': 'Pending',
  'ask.deepResearch.run': 'Run',

  // ── Ask promote-to-watchlist modal ────────────────────────────────
  'ask.promoteWatchlist.title': 'Promote to watchlist',
  'ask.promoteWatchlist.sub':
    'Turn this saved query into a smart watchlist. The agent re-runs it on your cadence and surfaces changes.',
  'ask.promoteWatchlist.label.name': 'Watchlist name',
  'ask.promoteWatchlist.label.cadence': 'Cadence',
  'ask.promoteWatchlist.cadence.daily': 'Daily',
  'ask.promoteWatchlist.cadence.weekly': 'Weekly',
  'ask.promoteWatchlist.cadence.monthly': 'Monthly',
  'ask.promoteWatchlist.cadence.onChange': 'On change',
  'ask.promoteWatchlist.cadence.weeklyRecommended': '(recommended)',
  'ask.promoteWatchlist.submit': 'Create watchlist',
  'ask.promoteWatchlist.cancel': 'Cancel',

  // ── Ask ground-truth filter ───────────────────────────────────────
  'ask.groundTruth.title': 'Ground-truth filter',
  'ask.groundTruth.sub': 'Hide facts the agent isn’t sure about.',
  'ask.groundTruth.count.total': 'Total',
  'ask.groundTruth.count.visible': 'Visible',
  'ask.groundTruth.count.filteredOut': 'Filtered out',
  'ask.groundTruth.option.authored': 'You authored',
  'ask.groundTruth.option.confirmed': 'You confirmed',
  'ask.groundTruth.option.inferred': 'Inferred by agent',
  'ask.groundTruth.option.notStale': 'Hide stale (> 6 months)',

  // ── Ask bad-citation popover ──────────────────────────────────────
  'ask.badCitation.cta': 'Flag this citation',
  'ask.badCitation.reason.wrong-source': '📄 Should cite something else',
  'ask.badCitation.reason.outdated': '⌛ This was the 2023 template',
  'ask.badCitation.reason.misread': '✗ Doesn’t say that',
  'ask.badCitation.reason.confidential': '⚠ Shouldn’t be cited here',
  'ask.badCitation.suggestion.placeholder': 'What should we cite instead? (optional)',
  'ask.badCitation.submit': 'Flag citation',
  'ask.badCitation.cancel': 'Cancel',
  'ask.badCitation.thanks': 'Flagged — the agent will revisit this.',

  // ── Ask empty state (refusal-to-hallucinate copy) ─────────────────
  // Verbatim from comment B — load-bearing copy preserved.
  'ask.empty.noCitations': 'I have nothing to cite here',
  'ask.empty.refuseBody':
    "I looked through everything I have access to from {entity} — {emails} emails, {meetings} meeting transcripts, {sf} Salesforce updates, {slack} Slack threads — and there's no mention of {topic}.",
  'ask.empty.refuse': "That doesn't mean it isn't true… I won't synthesize a guess.",
  'ask.empty.refuseCta': 'Ask the team',
  'ask.empty.showUngrounded': 'Show ungrounded draft',

  // ── briefs.detail.* (Brief-as-Dossier detail page, #166 PR B) ─────
  // The 7-tab detail surface at `/briefs/$briefId/*`. Identity header,
  // tab strip, per-item card, action ledger, notes, citations, outcomes,
  // source snapshot, share. Keys cluster by surface — identity header
  // first, then tab labels, then per-tab bodies, then shared bits.
  'briefs.detail.loading': 'Loading brief…',
  'briefs.detail.error': 'Couldn’t load this brief. Try again.',
  // Identity header
  'briefs.detail.eyebrow.diamond': '◆',
  'briefs.detail.eyebrow.format': 'Brief · {kind}',
  'briefs.detail.title.format': 'Brief #{n} · {date}',
  'briefs.detail.sub.format':
    'Sent {time} · {n} items · {decisionCount} decision · {watchCount} watches · {callCount} call',
  'briefs.detail.nav.prev.aria': 'Previous brief',
  'briefs.detail.nav.next.aria': 'Next brief',
  'briefs.detail.kind.daily': 'daily',
  'briefs.detail.kind.weekly': 'weekly',
  'briefs.detail.kind.ad-hoc': 'ad-hoc',
  'briefs.detail.kind.meeting-prep': 'meeting-prep',
  'briefs.detail.kind.catch-up': 'catch-up',
  'briefs.detail.kind.no-brief-today': 'no brief today',
  // Tab strip
  'briefs.detail.tab.items': 'Items',
  'briefs.detail.tab.action-ledger': 'Action ledger',
  'briefs.detail.tab.notes': 'Notes',
  'briefs.detail.tab.citations': 'Citations',
  'briefs.detail.tab.outcomes': 'Outcomes',
  'briefs.detail.tab.source-snapshot': 'Source snapshot',
  'briefs.detail.tab.share': 'Share',
  // Items tab
  'briefs.detail.items.empty':
    'This brief has no items. That’s the agent saying nothing material happened — not a bug.',
  'briefs.detail.items.footnote.format':
    'generated_at · {generatedAt} · source_snapshot · {snapshotId} · brief_id · {briefId}',
  'briefs.detail.item.conf.format': '{pct}% conf',
  'briefs.detail.item.confLabel': 'Agent confidence',
  'briefs.detail.item.cites.prefix': 'Sources',
  'briefs.detail.item.menu.aria': 'Item actions',
  'briefs.detail.item.menu.approve': 'Approve',
  'briefs.detail.item.menu.decline': 'Decline',
  'briefs.detail.item.menu.defer': 'Defer to tomorrow',
  'briefs.detail.item.menu.drill-in': 'Drill in',
  'briefs.detail.item.menu.pin-note': 'Pin a note',
  'briefs.detail.item.menu.correct': 'Correct the agent',
  'briefs.detail.item.menu.share': 'Share',
  'briefs.detail.item.disposition.pending': 'Pending',
  'briefs.detail.item.disposition.approved': 'Approved',
  'briefs.detail.item.disposition.declined': 'Declined',
  'briefs.detail.item.disposition.deferred': 'Deferred',
  'briefs.detail.item.disposition.ignored': 'Ignored',
  // Items tab — right rail
  'briefs.detail.rail.pin.eyebrow': 'Pin a thought',
  'briefs.detail.rail.pin.body':
    'Capture something you noticed in this brief. The agent reads pinned notes into the next pass.',
  'briefs.detail.rail.pin.note': '+ note',
  'briefs.detail.rail.pin.voice': '⏵ voice',
  'briefs.detail.rail.pin.paste': '📎 paste',
  'briefs.detail.rail.snapshot.eyebrow': 'Source snapshot',
  'briefs.detail.rail.snapshot.counts.format': '{items} items · {citations} citations',
  'briefs.detail.rail.nav.eyebrow': 'Navigate',
  'briefs.detail.rail.nav.allBriefs': 'All briefs →',
  // Action ledger tab
  'briefs.detail.ledger.filter.eyebrow': 'Filter',
  'briefs.detail.ledger.filter.user': 'User actions',
  'briefs.detail.ledger.filter.agent': 'Agent actions',
  'briefs.detail.ledger.filter.system': 'System events',
  'briefs.detail.ledger.heading.format': '{n} events',
  'briefs.detail.ledger.empty': 'No events yet.',
  'briefs.detail.ledger.error': 'Couldn’t load the action ledger. Try again.',
  // Notes tab
  'briefs.detail.notes.empty': 'No notes pinned to this brief yet.',
  'briefs.detail.notes.brief.heading.format': 'Brief-level · {n}',
  'briefs.detail.notes.item.heading.format': 'Item-level · {n}',
  'briefs.detail.notes.pinned.format': 'Pinned to {itemId}',
  // Citations tab
  'briefs.detail.citations.empty':
    'No citations in this brief — the agent had no grounded sources to cite this time.',
  'briefs.detail.citations.heading.format': '{n} citations across {sources} sources',
  'briefs.detail.citations.source.format': '{source} · {n}',
  'briefs.detail.citations.itemCount.format': '{n} items',
  // Outcomes tab
  'briefs.detail.outcomes.loading': 'Loading outcomes…',
  'briefs.detail.outcomes.error': 'Couldn’t load outcomes. Try again.',
  'briefs.detail.outcomes.empty':
    'No outcomes observed yet. The agent watches 1–4 weeks after each item disposition.',
  'briefs.detail.outcomes.score.eyebrow': 'Score · this brief',
  'briefs.detail.outcomes.trend.eyebrow': 'Trend · last 30 briefs',
  'briefs.detail.outcomes.trend.body.format':
    'Cross-brief outcome trend lands when the agent has {n} briefs of history.',
  'briefs.detail.outcome.status.confirmed': 'Confirmed',
  'briefs.detail.outcome.status.partial': 'Partial',
  'briefs.detail.outcome.status.refuted': 'Refuted',
  'briefs.detail.outcome.status.pending': 'Pending',
  'briefs.detail.outcome.status.lapsed': 'Lapsed',
  'briefs.detail.outcome.evidence.prefix': 'evidence',
  'briefs.detail.outcome.rel.today': 'today',
  'briefs.detail.outcome.rel.yesterday': 'yesterday',
  'briefs.detail.outcome.rel.tomorrow': 'tomorrow',
  'briefs.detail.outcome.rel.daysAgo': '{n}d ago',
  'briefs.detail.outcome.rel.daysAhead': 'in {n}d',
  // Source snapshot tab
  'briefs.detail.snap.eyebrow.diamond': '◆',
  'briefs.detail.snap.banner.format': 'Reproducible · {id}',
  'briefs.detail.snap.caption.format':
    'The exact source state the agent had at {snapshotAt}. Regenerating this brief on this snapshot would produce the same output — that’s the contract.',
  'briefs.detail.snap.sources.heading': 'Sources read',
  'briefs.detail.snap.reasoning.heading': 'Agent reasoning trace',
  'briefs.detail.snap.reasoning.empty':
    'No reasoning trace recorded for this snapshot. The synthesis layer wrote the brief without numbered steps.',
  'briefs.detail.snap.footer.format': '{id} · {size} · expires in 90d',
  'briefs.detail.snap.replay.cta': 'Replay from this snapshot',
  'briefs.detail.snap.loading': 'Loading source snapshot…',
  'briefs.detail.snap.error': 'Couldn’t load the source snapshot. Try again.',
  'briefs.detail.snap.counts.emails': 'Emails',
  'briefs.detail.snap.counts.calendar': 'Calendar',
  'briefs.detail.snap.counts.docs': 'Docs',
  'briefs.detail.snap.counts.slack': 'Slack',
  'briefs.detail.snap.counts.salesforce': 'Salesforce',
  'briefs.detail.snap.counts.stripe': 'Stripe',
  'briefs.detail.snap.counts.github': 'GitHub',
  'briefs.detail.snap.counts.notion': 'Notion',
  'briefs.detail.snap.units.emails': 'emails',
  'briefs.detail.snap.units.events': 'events',
  'briefs.detail.snap.units.edits': 'edits',
  'briefs.detail.snap.units.messages': 'messages',
  'briefs.detail.snap.units.updates': 'updates',
  // Share tab
  'briefs.detail.share.link.eyebrow': 'Public read-only link',
  'briefs.detail.share.link.heading': 'Share this brief',
  'briefs.detail.share.link.aria': 'Public share link',
  'briefs.detail.share.link.placeholder':
    'No share link yet. Generate one to send this brief to teammates or investors. The link is read-only and excludes citations + source snapshot.',
  'briefs.detail.share.link.generate': 'Generate share link',
  'briefs.detail.share.link.copy': 'Copy',
  'briefs.detail.share.link.copied': 'Copied',
  'briefs.detail.share.link.revoke': 'Revoke',
  'briefs.detail.share.link.meta.format':
    'Created {createdAt} · expires {expiresAt} · CAN view items + outcomes · CANNOT see citations or source snapshot',
  'briefs.detail.share.redact.eyebrow': 'Redactions',
  'briefs.detail.share.redact.heading': 'Hide sensitive sections',
  'briefs.detail.share.redact.note':
    'Redactions are saved with the share link once Phase 6 persistence ships. For now, toggles are session-local.',
  'briefs.detail.share.redact.salaries': 'Hide salaries + comp',
  'briefs.detail.share.redact.customer-names': 'Hide customer names',
  'briefs.detail.share.redact.financial-figures': 'Hide financial figures',
  'briefs.detail.share.redact.legal-docs': 'Hide legal-doc citations',
  'briefs.detail.share.redact.personal-notes': 'Hide personal notes',
  'briefs.detail.share.redact.email-bodies': 'Hide email bodies',
  'briefs.detail.share.send.eyebrow': 'Or send directly',
  'briefs.detail.share.send.heading': 'Send to a teammate',
  'briefs.detail.share.send.email': '📧 Email',
  'briefs.detail.share.send.slack': '💬 Slack DM',
  'briefs.detail.share.send.pdf': '📄 Export PDF',
  'briefs.detail.share.send.notion': '🔗 Notion',
  // Briefing overnight (now redirects to action-ledger filtered view)
  'briefs.detail.overnight.redirecting': 'Loading the overnight standup…',
  'briefs.detail.overnight.error':
    'Couldn’t resolve today’s brief. The overnight standup folds into the brief’s Action Ledger tab — open today’s brief and filter to agent actions since midnight.',

  // /briefs/anniversaries — "same time last year you decided X" feed (#166 PR B Phase 6)
  'briefs.anniversaries.eyebrow': 'Same time last year',
  'briefs.anniversaries.title': 'Same time last year, you decided —',
  'briefs.anniversaries.sub':
    'Briefs from this week last year. Worth a glance — patterns are how you get better.',
  'briefs.anniversaries.empty.eyebrow': 'No anniversaries yet',
  'briefs.anniversaries.empty.title': 'Come back in a year.',
  'briefs.anniversaries.empty.body':
    'This feed lights up once you have briefs from ±7 days a year prior. Until then it stays quiet — no point fabricating an anniversary.',
  'briefs.anniversaries.row.daysAgo': '{days}d ago',
  'briefs.anniversaries.row.acted': '{acted} of {total} items acted on',
  'briefs.anniversaries.row.untitled': 'Untitled brief',
  'briefs.anniversaries.error.title': 'Couldn’t load the anniversaries feed.',
  'briefs.anniversaries.error.body':
    'Try again in a moment, or check the agent log for what failed. An empty feed and an error look the same — we’d rather tell you which one this is.',

  // /settings/cadence — vacation mode + quiet hours (#166 PR B Phase 6)
  'settings.cadence.eyebrow': 'Cadence · pause + quiet hours',
  'settings.cadence.title': 'Tell me when you want silence.',
  'settings.cadence.sub':
    "I'll still listen and reason in the background. I just won't push briefings, interrupts, or notifications until you're back.",
  'settings.cadence.error':
    "Couldn't load your cadence preferences. Check your connection and try again.",
  'settings.cadence.error.retry': 'Retry',
  'settings.cadence.vacation.eyebrow': '◆ Vacation mode',
  'settings.cadence.vacation.on': 'Vacation mode is on.',
  'settings.cadence.vacation.off': 'Currently off.',
  'settings.cadence.vacation.on.body':
    'No briefings, no interrupts, no notifications. The agent is reading + reasoning in the background — your catch-up brief lands when you return.',
  'settings.cadence.vacation.off.body':
    'Toggle on, set a range. The agent will resume on the end date automatically.',
  'settings.cadence.vacation.enable': 'Enable',
  'settings.cadence.vacation.disable': 'Disable',
  'settings.cadence.quiet-hours.eyebrow': 'Quiet hours · daily',
  'settings.cadence.quiet-hours.start': 'Start',
  'settings.cadence.quiet-hours.end': 'End',
  'settings.cadence.quiet-hours.weekends': 'Weekends',
  'settings.cadence.quiet-hours.weekends-quiet': 'Quiet',
  'settings.cadence.quiet-hours.weekends-on': 'Always on',
  'settings.cadence.quiet-hours.edit-hint':
    'Per-day editing lands in a follow-up. For now the values apply uniformly Mon–Fri.',
  'settings.cadence.emergency.eyebrow': '◆ Override · what counts as an emergency?',
  'settings.cadence.emergency.body':
    'These categories bypass vacation mode + quiet hours. Edit the policy in a follow-up.',

  // Dossier — mentioned-in-briefs sub-tab
  'dossier.mentioned-in-briefs.loading': 'Scanning recent briefs for mentions of this entity…',
  'dossier.mentioned-in-briefs.error': 'Couldn’t scan briefs for mentions. Try again.',
  'dossier.mentioned-in-briefs.empty.format':
    'No mentions in the last {scanned} briefs. The reverse-index endpoint will surface deeper history when epic #9 ships.',
  'dossier.mentioned-in-briefs.heading.format': 'Mentioned in {n} of {scanned} recent briefs',
  'dossier.mentioned-in-briefs.itemRefs.format': 'Items · {items}',

  // Ask — synthesis-card restructure (design-pass PR-A).
  'ask.eyebrowCited': 'Ask · cited synthesis',
  'ask.titlePlaceholder': 'Ask the agent',
  'ask.sub.format': 'Threaded with {events} events across your tools · streamed {ago}',
  'ask.sub.placeholder':
    'Plain English in, cited synthesis out. Every claim anchors to a source the agent can show you.',
  'ask.synthesis': 'Synthesis',
  'ask.suggestedActions': 'Suggested actions',
  'ask.rowsFromGraph': 'Rows from the graph',
  'ask.followUpPlaceholder': 'Ask follow-up — “what should I do next?”',
  'ask.askArrow': 'Ask →',

  // Operating loop — Section 07 design-pass overrides. All keys prefixed
  // with `loop.*` per the design-pass agent contract so concurrent agents
  // don't collide. These provide the designer's declarative voice for
  // the page-head treatment (eyebrow + H1 + sub) on the 5 loop surfaces:
  // /issues, /rocks, /scorecard, /l10, /playbook. The original keys
  // (issues.title, rocks.title, etc.) are preserved for backward
  // compatibility but the pages reference these `loop.*` variants where
  // the design intent diverged from the legacy copy.
  'loop.rocks.title.format': '{quarter} Rocks',
  'loop.rocks.weekCaption.format': 'WEEK {weekN} / 13 · {pct}% TIME ELAPSED',
  'loop.scorecard.title': 'Weekly scorecard',
  'loop.playbook.title': 'How we operate.',
  'loop.l10.eyebrow': 'Weekly · L10 prep',

  // PR-A sources/connectors/integrations restructure (issue #173 PR-A · 12-artboard pass).
  // /sources tile affordance + status copy aligned with the design's
  // `SourcesScreen` artboard (Platform.html §14, artboard `sources-main`).
  'sources.tile.manage': 'Manage',
  'sources.tile.connect': 'Connect',
  'sources.tile.status.active': 'live',
  'sources.tile.status.installing': 'installing',
  'sources.tile.status.backfilling': 'backfilling',
  'sources.tile.status.paused': 'paused',
  'sources.tile.status.error': 'errored',
  'sources.tile.status.disconnected': 'not connected',

  // /admin/connectors page-head eyebrow — restructure pass replaces the
  // hardcoded "ADMIN" literal so the page-head pattern reads consistently
  // with /sources and /admin/integrations.
  'connector.admin.eyebrow': 'Admin · connectors',

  // Section 09 — Agent transparency restructure pass.
  // Designer wants the canonical agent-log page to lead with
  // "Provenance · agent" + "Everything the agent did." matching the
  // mock at assets/6c3b1c1c…js §AgentLogScreen. New keys keep the
  // existing 'agent.*' keys untouched (other surfaces still rely on
  // them) and the /agent surface adopts the polished copy.
  'agentLog.eyebrow': 'Provenance · agent',
  'agentLog.title': 'Everything the agent did.',
  'agentLog.sub':
    'Timestamped, cited, rollback-able. Click any row to see input, output, and prompt.',

  // Sibling transparency surfaces — eyebrows the designer mock uses
  // for the page-head treatment. Each is its own slot so future copy
  // tweaks don't ripple through the rest of the agent.* table.
  'agentLog.changelog.eyebrow': "Agent · what's new",
  'agentLog.calibration.eyebrow': 'Agent · calibration',
  'agentLog.questions.eyebrow': 'Agent · open questions',
  'agentLog.corrections.eyebrow': 'Agent · corrections',

  // Memory surface — designer's intent is the same canonical "what
  // the agent learned" page-head but with the eyebrow stripped of the
  // dot prefix that 'memory.eyebrow' carries (the dot was a holdover
  // from when it shared the empty-state status chip styling). We
  // keep both for backwards compatibility; the page can opt in.

  // /admin/audit/$id — diff drill-through. Designer's AuditDiffScreen
  // (assets/bab9c16b-d4ac-43df-856b-5dd6eda0732c.js §AuditDiffScreen).
  // Eyebrow + page-head + diff header strings live here so the page is
  // 100% literal-free and stays in sync with the audit feed copy.
  'adminAudit.diff.eyebrow.prefix': 'Audit',
  'adminAudit.diff.trigger.eyebrow': 'Trigger · what caused this edit',
  'adminAudit.diff.diffHeader.section': 'section 2 · "Cohort evidence"',
  'adminAudit.diff.diffHeader.counts':
    '{changed} lines changed · {added} added · {removed} removed',
  'adminAudit.diff.rail.event': 'Event',
  'adminAudit.diff.rail.id': 'id',
  'adminAudit.diff.rail.at': 'at',
  'adminAudit.diff.rail.by': 'by',
  'adminAudit.diff.rail.kind': 'kind',

  // ── People + Profile design-pass (Section 11/12 of LeanOpx Platform.html).
  // `peopleTeam.*` are new strings for the /admin/people directory's page-head
  // actions; existing `admin.people.*` keys are untouched. `companyProfile.*`
  // and `accountability.*` additions are append-only (no existing keys are
  // mutated).
  'peopleTeam.invite.cta': '+ Invite',

  // /profile/company — eyebrow for the company-profile page-head. The page
  // previously rendered without an eyebrow; the design (PeopleScreen sibling
  // `companyProfile`) leads with a mono eyebrow on every surface in this
  // section, so we add one here without changing existing companyProfile.*
  // copy.
  'companyProfile.eyebrow': 'Company profile',

  // /accountability — landing surface for the org accountability set. The
  // detailed chart visualization lives at `/accountability/chart` (separate
  // page). This landing page sets the eyebrow + H1 + sub for the surface and
  // surfaces two follow-on cards (org chart + people directory).
  'accountability.eyebrow': 'Accountability · who owns what',
  'accountability.title': 'Seats, accountabilities, and who reports to whom.',
  'accountability.sub':
    'Seats not titles — one person can hold multiple seats; every seat reports to exactly one parent. Open the org chart to see the live tree.',
  'accountability.openChart.cta': 'Open org chart',
  'accountability.card.chart.eyebrow': 'Org chart',
  'accountability.card.chart.title': 'How the company is wired',
  'accountability.card.chart.body':
    'Visualize the reporting tree, flag empty seats, and spot span-of-control risk.',
  'accountability.card.chart.cta': 'Open chart',
  'accountability.card.people.eyebrow': 'People directory',
  'accountability.card.people.title': 'Who has access to what',
  'accountability.card.people.body':
    'Manage members, roles, invites, and per-person ownership of rocks and topics.',
  'accountability.card.people.cta': 'Open people',

  // ── Notes / capture (Section 05) — quick-filter row + per-row metadata.
  //    Prefix `notesCapture.*` to avoid collision with the pre-existing
  //    `capture.*` keys, which belong to the investigation "Save to
  //    investigation" capture-from-source flow.
  'notesCapture.filter.aria': 'Filter notes by type',
  'notesCapture.filter.all': 'All',
  'notesCapture.filter.voice': 'Voice',
  'notesCapture.filter.photo': 'Photos',
  'notesCapture.filter.file': 'Files',
  'notesCapture.filter.text': 'Text',
  'notesCapture.row.type.voice': 'VOICE',
  'notesCapture.row.type.photo': 'PHOTO',
  'notesCapture.row.type.file': 'FILE',
  'notesCapture.row.type.text': 'TEXT',

  // ── Overlays (Section 18) — shared eyebrow + label keys for the
  //    command palette, modals, slide-overs, and toast surfaces. All
  //    new keys live behind the `overlay.*` prefix so adding a row to
  //    the design language doesn't risk colliding with the per-surface
  //    namespaces (e.g. `playbook.create.*`) that already exist.
  'overlay.commandPalette.a11y.title': 'Command palette',
  'overlay.commandPalette.a11y.description':
    'Type to search across LeanOpx surfaces — playbooks, dossiers, scorecard, signals, and quick actions. Press Enter to navigate or Cmd-Enter to open in a new tab.',
  'overlay.commandPalette.placeholder': 'Search · ask · run action…',
  'overlay.commandPalette.escKey': 'esc',
  'overlay.commandPalette.empty': 'No matches.',
  'overlay.commandPalette.group.jumpTo': 'Jump to',
  'overlay.commandPalette.group.ask': 'Ask the agent',
  'overlay.commandPalette.group.do': 'Do',
  'overlay.commandPalette.hint.admin': 'Admin',
  'overlay.commandPalette.hint.surface': 'Surface',
  'overlay.commandPalette.hint.synthesis': 'Synthesis · cited',
  'overlay.commandPalette.hint.theme': 'Theme',
  'overlay.commandPalette.hint.density': 'Density',
  'overlay.commandPalette.hint.sidebar': 'Sidebar',
  'overlay.commandPalette.hint.orgSwitch': 'Org switch',
  'overlay.commandPalette.altLabel.newTab': 'new tab',
  'overlay.commandPalette.ask.prefix': 'Ask:',
  'overlay.commandPalette.ask.open': 'Open Ask',
  'overlay.commandPalette.do.toggleTheme': 'Toggle theme',
  'overlay.commandPalette.do.cycleDensity': 'Cycle density (compact / regular / comfy)',
  'overlay.commandPalette.do.toggleSidebar': 'Toggle sidebar pin (⌘\\)',
  'overlay.commandPalette.do.switchOrg': 'Switch org → {name}',
  'overlay.commandPalette.foot.nav': 'nav',
  'overlay.commandPalette.foot.open': 'open',
  'overlay.commandPalette.foot.newPane': 'open in new pane',
  'overlay.commandPalette.foot.results': '{n} results',

  // Shared modal eyebrows — applied above the playbook create/edit/AI-
  //    refine titles for visual consistency with the rest of the
  //    designer surfaces (every surface gets a small mono eyebrow).
  'overlay.modal.playbookCreate.eyebrow': 'Playbook · new',
  'overlay.modal.playbookEdit.eyebrow': 'Playbook · edit',
  'overlay.modal.playbookAi.eyebrow': 'Playbook · refine with AI',

  // /rules right-rail summary cards (Section 10 design pass). Numbers
  // derive from the same hydrated rules list — no extra API call. The
  // `{live}/{total}` shape echoes the designer's rail-row visual
  // ("11 / 12" live), and the "how it works" card explains the
  // When → then grammar so admins reading the page for the first time
  // know what a rule actually is before they scan the categories.
  'rules.rail.summary.eyebrow': 'Summary',
  'rules.rail.summary.live': 'live rules',
  'rules.rail.summary.live.value': '{live} / {total}',
  'rules.rail.summary.paused': 'paused',
  'rules.rail.howItWorks.eyebrow': 'How rules work',
  'rules.rail.howItWorks.body':
    'Each rule reads as "When X → then Y". The agent watches the connectors for X, then runs Y — either silently, queued for approval, or as an interrupt — depending on the category.',

  // Section 16 · Help & in-product moments — currently DEFERRED. The
  // portal has no `/help` route today, and the designer's HelpCenter +
  // WhatsNewModal + NotificationsScreen + StatesShowcase artboards
  // describe a help-center / release-notes / notifications-prefs page
  // set that hasn't been scaffolded yet. Tracked as an explicit
  // deviation in the PR-A design-pass report. The reserved key here
  // documents the gap so the next surface-scoping pass knows the
  // `helpMoment.*` namespace is allocated.
  'helpMoment.deferred.note':
    'Help center, what’s-new modal, notification preferences, and a states showcase have no implementation surface in the portal yet.',

  // ── Section 32 v2-brief-tabs + Section 25 briefing-extras — design-pass wave ──
  // Appended (not interleaved) so the diff against the prior STRINGS
  // map is a contiguous block. `briefTab.*` prefix per the wave's
  // ownership rules; existing `briefs.detail.tab.*` labels stay put.
  'briefTab.nav.aria': 'Brief detail tabs',

  /* ──────────────────────────────────────────────────────────────
   * Section 35 · Self-serve customer (residency + spend caps).
   *
   * Mounted as additive sections inside `/admin/billing`. UI-only on
   * first ship — the residency picker + spend-cap form render the
   * design intent but mutations are not wired to backend yet. Every
   * row carries a "not yet wired" caption so the operator is not
   * misled. Backend wiring tracked as follow-up (`orgPreferences`
   * extension for residency; new `orgSpendCaps/{orgId}` mirror +
   * monthly usage rollup for caps). All copy keys live under
   * `selfServe.*`.
   * ────────────────────────────────────────────────────────────── */
  'selfServe.residency.eyebrow': 'Data residency · where your tenant lives',
  'selfServe.residency.title': 'Where should your data live?',
  'selfServe.residency.sub':
    'Pick the region your tenant data is hosted in. Region cannot be changed without an explicit migration — once tenant data lands in a region it stays there.',
  'selfServe.residency.notWired':
    'Read-only preview · region changes are not yet wired. Email billing@leanopx.com to move regions today.',
  'selfServe.residency.region.us.name': 'United States',
  'selfServe.residency.region.us.host': 'Virginia · us-east-1',
  'selfServe.residency.region.us.certs': 'SOC 2 Type II · HIPAA-ready',
  'selfServe.residency.region.us.note': 'Default · lowest latency from North America.',
  'selfServe.residency.region.eu.name': 'European Union',
  'selfServe.residency.region.eu.host': 'Frankfurt · eu-central-1',
  'selfServe.residency.region.eu.certs': 'SOC 2 · GDPR · Schrems II–compliant',
  'selfServe.residency.region.eu.note': 'Recommended if you process EU citizen data.',
  'selfServe.residency.region.uk.name': 'United Kingdom',
  'selfServe.residency.region.uk.host': 'London · eu-west-2',
  'selfServe.residency.region.uk.certs': 'SOC 2 · UK GDPR · ICO-registered',
  'selfServe.residency.region.uk.note': 'Required for UK government and many financial firms.',
  'selfServe.residency.region.recommended': 'recommended',
  'selfServe.residency.region.current': 'current',
  'selfServe.residency.dpa.note':
    'Sub-processors + DPA at /security · Enterprise customers can request a custom DPA.',

  'selfServe.spend.eyebrow': 'Spend caps · no surprises',
  'selfServe.spend.title': 'Set the ceiling on agent spend.',
  'selfServe.spend.sub':
    'The agent uses inference tokens to read, synthesize, and act. You decide how much it can use per month, and what happens when it hits the cap.',
  'selfServe.spend.notWired':
    'Read-only preview · spend cap enforcement and per-user limits are not yet wired. Today every plan ships with the limits in your contract.',
  'selfServe.spend.orgCap.title': 'Org-wide cap',
  'selfServe.spend.orgCap.cadence': '/ month · resets on the 1st',
  'selfServe.spend.orgCap.amountAria': 'Org-wide monthly cap in US dollars',
  'selfServe.spend.mode.soft.label': 'Soft cap',
  'selfServe.spend.mode.soft.body': 'Alert at 80%, 100%, 120%. The agent keeps working.',
  'selfServe.spend.mode.hard.label': 'Hard cap',
  'selfServe.spend.mode.hard.body':
    'Agent pauses at 100%. Briefings stop until reset or you raise the cap.',
  'selfServe.spend.perUser.title': 'Per-user caps',
  'selfServe.spend.perUser.empty':
    'Per-user caps appear here once your team has more than one seat. Today the org-wide cap covers everyone.',
  'selfServe.spend.alert.title': 'Spend alert',
  'selfServe.spend.alert.body':
    'When a member is on pace to breach their cap the agent will email you with the option to raise the cap or hold the line.',

  /* ──────────────────────────────────────────────────────────────
   * Section 30 · Critical-gap surfaces that did not ship in this
   * design pass and were not extended into an existing page.
   *
   * - `gap.apiKeys.*` — the API keys + MCP + webhooks artboard
   *   describes a `/admin/api-keys` developer surface. Backend
   *   (`apiKeys/{keyId}` collection, key minting + rotation, MCP
   *   endpoint provisioning, webhook delivery rollup) is entirely
   *   unwired and overlaps admin-settings / admin-integrations
   *   real-estate which this PR cannot touch. Deferred as a
   *   follow-up — keys reserved so the next surface-scoping pass
   *   knows the namespace.
   *
   * - `gap.persona.*` — agent-persona-settings expansion (voice /
   *   autonomy dial / cadence). Already covered today by the
   *   `/profile` "How LeanOpx shows up for you" card +
   *   `/settings/agent`. Reserved for the next round.
   *
   * - `gap.profile.day3.*` — day-3 complete-profile artboard.
   *   Already covered by `/profile` v7 (epic #123). Reserved.
   *
   * - `trustMoment.*` — preview/trigger/continuity patterns. The
   *   "what I would write" preview is already implemented in
   *   `/agent/rehearsal`; the governance trigger + cross-surface
   *   continuity toast overlap briefing surfaces this PR cannot
   *   touch. Reserved.
   * ────────────────────────────────────────────────────────────── */
  'gap.apiKeys.deferred.note':
    'API keys, MCP server, and webhook delivery surfaces have no implementation route yet (Section 30 critical-gap artboard).',
  'gap.persona.deferred.note':
    'Agent persona expansion (voice · autonomy dial · cadence) is already covered today by /profile and /settings/agent.',
  'gap.profile.day3.deferred.note':
    'Day-3 complete-profile expansion is already covered today by the /profile v7 (epic #123) told/learned cards.',
  'trustMoment.preview.deferred.note':
    'The "what I would write" preview pattern is already implemented in /agent/rehearsal.',
  'trustMoment.trigger.deferred.note':
    'The governance trigger pattern (e.g. franchise-tax 23 days out) overlaps briefing surfaces that are owned by other agents in this PR series.',
  'trustMoment.continuity.deferred.note':
    'The cross-surface continuity toast overlaps briefing surfaces that are owned by other agents in this PR series.',
} as const satisfies Record<string, string>;

export type StringKey = keyof typeof STRINGS;

export function t(key: StringKey): string {
  return STRINGS[key];
}

/**
 * Substitute `{name}` placeholders in a STRINGS entry. Centralizes the
 * `t(...).replace('{n}', String(v))` idiom that the codebase had begun
 * to scatter — new placeholder-bearing strings should reach for this instead.
 */
export function tFormat(key: StringKey, vars: Record<string, string | number>): string {
  // Annotate `out: string` — STRINGS is `as const`, so the lookup
  // returns a literal type that narrows the var and rejects subsequent
  // `replaceAll` returns. Widening once at the entry keeps the loop sane.
  let out: string = STRINGS[key];
  for (const [k, v] of Object.entries(vars)) {
    out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}
